'use client';
/**
 * Copyright 2024 Aiden Bai, Million Software, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software
 * and associated documentation files (the “Software”), to deal in the Software without restriction,
 * including without limitation the rights to use, copy, modify, merge, publish, distribute,
 * sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or
 * substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED “AS IS”, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING
 * BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
 * NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
 * DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
"use client";

// src/core/monitor/params/next.ts
import { useParams, usePathname, useSearchParams } from "next/navigation.js";
import { createElement as createElement2, Suspense } from "react";

// src/core/monitor/index.ts
import {
  getDisplayName as getDisplayName8,
  getTimings as getTimings3,
  isCompositeFiber as isCompositeFiber3
} from "bippy";
import { useEffect as useEffect16 } from "react";

// src/core/index.ts
import { signal as signal5 } from "@preact/signals";
import {
  detectReactBuildType,
  getRDTHook,
  getType as getType3,
  isInstrumentationActive as isInstrumentationActive2
} from "bippy";

// src/new-outlines/index.ts
import {
  didFiberCommit as didFiberCommit2,
  getDisplayName as getDisplayName5,
  getFiberId,
  getNearestHostFibers,
  isCompositeFiber as isCompositeFiber2
} from "bippy";

// src/core/instrumentation.ts
import { signal as signal3 } from "@preact/signals";
import {
  ClassComponentTag as ClassComponentTag2,
  ForwardRefTag as ForwardRefTag3,
  FunctionComponentTag as FunctionComponentTag3,
  MemoComponentTag as MemoComponentTag3,
  SimpleMemoComponentTag as SimpleMemoComponentTag3,
  didFiberCommit,
  getDisplayName as getDisplayName4,
  getMutatedHostFibers,
  getTimings as getTimings2,
  getType as getType2,
  hasMemoCache as hasMemoCache2,
  instrument,
  traverseContexts,
  traverseProps,
  traverseRenderedFibers
} from "bippy";
import { isValidElement } from "preact";

// src/core/utils.ts
import { getType } from "bippy";
var updateFiberRenderData = (fiber, renders) => {
  ReactScanInternals.options.value.onRender?.(fiber, renders);
  const type = getType(fiber.type) || fiber.type;
  if (type && typeof type === "function" && typeof type === "object") {
    const renderData = type.renderData || {
      count: 0,
      time: 0,
      renders: []
    };
    const firstRender = renders[0];
    renderData.count += firstRender.count;
    renderData.time += firstRender.time ?? 0;
    renderData.renders.push(firstRender);
    type.renderData = renderData;
  }
};
function isEqual(a, b) {
  return a === b || a !== a && b !== b;
}

// src/web/components/inspector/timeline/utils.ts
import {
  ClassComponentTag,
  ForwardRefTag as ForwardRefTag2,
  FunctionComponentTag as FunctionComponentTag2,
  MemoComponentTag as MemoComponentTag2,
  SimpleMemoComponentTag as SimpleMemoComponentTag2
} from "bippy";

// src/web/components/inspector/utils.ts
import {
  FunctionComponentTag,
  getDisplayName as getDisplayName3,
  getTimings,
  isCompositeFiber,
  isHostFiber,
  traverseFiber
} from "bippy";

// src/web/utils/helpers.ts
import {
  ForwardRefTag,
  MemoComponentTag,
  SimpleMemoComponentTag,
  SuspenseComponentTag,
  getDisplayName,
  hasMemoCache
} from "bippy";

// ../../node_modules/.pnpm/clsx@2.1.1/node_modules/clsx/dist/clsx.mjs
function r(e) {
  var t, f, n = "";
  if ("string" == typeof e || "number" == typeof e) n += e;
  else if ("object" == typeof e) if (Array.isArray(e)) {
    var o = e.length;
    for (t = 0; t < o; t++) e[t] && (f = r(e[t])) && (n && (n += " "), n += f);
  } else for (f in e) e[f] && (n && (n += " "), n += f);
  return n;
}
function clsx() {
  for (var e, t, f = 0, n = "", o = arguments.length; f < o; f++) (e = arguments[f]) && (t = r(e)) && (n && (n += " "), n += t);
  return n;
}

// ../../node_modules/.pnpm/tailwind-merge@2.5.5/node_modules/tailwind-merge/dist/bundle-mjs.mjs
var CLASS_PART_SEPARATOR = "-";
var createClassGroupUtils = (config) => {
  const classMap = createClassMap(config);
  const {
    conflictingClassGroups,
    conflictingClassGroupModifiers
  } = config;
  const getClassGroupId = (className) => {
    const classParts = className.split(CLASS_PART_SEPARATOR);
    if (classParts[0] === "" && classParts.length !== 1) {
      classParts.shift();
    }
    return getGroupRecursive(classParts, classMap) || getGroupIdForArbitraryProperty(className);
  };
  const getConflictingClassGroupIds = (classGroupId, hasPostfixModifier) => {
    const conflicts = conflictingClassGroups[classGroupId] || [];
    if (hasPostfixModifier && conflictingClassGroupModifiers[classGroupId]) {
      return [...conflicts, ...conflictingClassGroupModifiers[classGroupId]];
    }
    return conflicts;
  };
  return {
    getClassGroupId,
    getConflictingClassGroupIds
  };
};
var getGroupRecursive = (classParts, classPartObject) => {
  if (classParts.length === 0) {
    return classPartObject.classGroupId;
  }
  const currentClassPart = classParts[0];
  const nextClassPartObject = classPartObject.nextPart.get(currentClassPart);
  const classGroupFromNextClassPart = nextClassPartObject ? getGroupRecursive(classParts.slice(1), nextClassPartObject) : void 0;
  if (classGroupFromNextClassPart) {
    return classGroupFromNextClassPart;
  }
  if (classPartObject.validators.length === 0) {
    return void 0;
  }
  const classRest = classParts.join(CLASS_PART_SEPARATOR);
  return classPartObject.validators.find(({
    validator
  }) => validator(classRest))?.classGroupId;
};
var arbitraryPropertyRegex = /^\[(.+)\]$/;
var getGroupIdForArbitraryProperty = (className) => {
  if (arbitraryPropertyRegex.test(className)) {
    const arbitraryPropertyClassName = arbitraryPropertyRegex.exec(className)[1];
    const property = arbitraryPropertyClassName?.substring(0, arbitraryPropertyClassName.indexOf(":"));
    if (property) {
      return "arbitrary.." + property;
    }
  }
};
var createClassMap = (config) => {
  const {
    theme,
    prefix
  } = config;
  const classMap = {
    nextPart: /* @__PURE__ */ new Map(),
    validators: []
  };
  const prefixedClassGroupEntries = getPrefixedClassGroupEntries(Object.entries(config.classGroups), prefix);
  prefixedClassGroupEntries.forEach(([classGroupId, classGroup]) => {
    processClassesRecursively(classGroup, classMap, classGroupId, theme);
  });
  return classMap;
};
var processClassesRecursively = (classGroup, classPartObject, classGroupId, theme) => {
  classGroup.forEach((classDefinition) => {
    if (typeof classDefinition === "string") {
      const classPartObjectToEdit = classDefinition === "" ? classPartObject : getPart(classPartObject, classDefinition);
      classPartObjectToEdit.classGroupId = classGroupId;
      return;
    }
    if (typeof classDefinition === "function") {
      if (isThemeGetter(classDefinition)) {
        processClassesRecursively(classDefinition(theme), classPartObject, classGroupId, theme);
        return;
      }
      classPartObject.validators.push({
        validator: classDefinition,
        classGroupId
      });
      return;
    }
    Object.entries(classDefinition).forEach(([key, classGroup2]) => {
      processClassesRecursively(classGroup2, getPart(classPartObject, key), classGroupId, theme);
    });
  });
};
var getPart = (classPartObject, path) => {
  let currentClassPartObject = classPartObject;
  path.split(CLASS_PART_SEPARATOR).forEach((pathPart) => {
    if (!currentClassPartObject.nextPart.has(pathPart)) {
      currentClassPartObject.nextPart.set(pathPart, {
        nextPart: /* @__PURE__ */ new Map(),
        validators: []
      });
    }
    currentClassPartObject = currentClassPartObject.nextPart.get(pathPart);
  });
  return currentClassPartObject;
};
var isThemeGetter = (func) => func.isThemeGetter;
var getPrefixedClassGroupEntries = (classGroupEntries, prefix) => {
  if (!prefix) {
    return classGroupEntries;
  }
  return classGroupEntries.map(([classGroupId, classGroup]) => {
    const prefixedClassGroup = classGroup.map((classDefinition) => {
      if (typeof classDefinition === "string") {
        return prefix + classDefinition;
      }
      if (typeof classDefinition === "object") {
        return Object.fromEntries(Object.entries(classDefinition).map(([key, value]) => [prefix + key, value]));
      }
      return classDefinition;
    });
    return [classGroupId, prefixedClassGroup];
  });
};
var createLruCache = (maxCacheSize) => {
  if (maxCacheSize < 1) {
    return {
      get: () => void 0,
      set: () => {
      }
    };
  }
  let cacheSize = 0;
  let cache2 = /* @__PURE__ */ new Map();
  let previousCache = /* @__PURE__ */ new Map();
  const update = (key, value) => {
    cache2.set(key, value);
    cacheSize++;
    if (cacheSize > maxCacheSize) {
      cacheSize = 0;
      previousCache = cache2;
      cache2 = /* @__PURE__ */ new Map();
    }
  };
  return {
    get(key) {
      let value = cache2.get(key);
      if (value !== void 0) {
        return value;
      }
      if ((value = previousCache.get(key)) !== void 0) {
        update(key, value);
        return value;
      }
    },
    set(key, value) {
      if (cache2.has(key)) {
        cache2.set(key, value);
      } else {
        update(key, value);
      }
    }
  };
};
var IMPORTANT_MODIFIER = "!";
var createParseClassName = (config) => {
  const {
    separator,
    experimentalParseClassName
  } = config;
  const isSeparatorSingleCharacter = separator.length === 1;
  const firstSeparatorCharacter = separator[0];
  const separatorLength = separator.length;
  const parseClassName = (className) => {
    const modifiers = [];
    let bracketDepth = 0;
    let modifierStart = 0;
    let postfixModifierPosition;
    for (let index = 0; index < className.length; index++) {
      let currentCharacter = className[index];
      if (bracketDepth === 0) {
        if (currentCharacter === firstSeparatorCharacter && (isSeparatorSingleCharacter || className.slice(index, index + separatorLength) === separator)) {
          modifiers.push(className.slice(modifierStart, index));
          modifierStart = index + separatorLength;
          continue;
        }
        if (currentCharacter === "/") {
          postfixModifierPosition = index;
          continue;
        }
      }
      if (currentCharacter === "[") {
        bracketDepth++;
      } else if (currentCharacter === "]") {
        bracketDepth--;
      }
    }
    const baseClassNameWithImportantModifier = modifiers.length === 0 ? className : className.substring(modifierStart);
    const hasImportantModifier = baseClassNameWithImportantModifier.startsWith(IMPORTANT_MODIFIER);
    const baseClassName = hasImportantModifier ? baseClassNameWithImportantModifier.substring(1) : baseClassNameWithImportantModifier;
    const maybePostfixModifierPosition = postfixModifierPosition && postfixModifierPosition > modifierStart ? postfixModifierPosition - modifierStart : void 0;
    return {
      modifiers,
      hasImportantModifier,
      baseClassName,
      maybePostfixModifierPosition
    };
  };
  if (experimentalParseClassName) {
    return (className) => experimentalParseClassName({
      className,
      parseClassName
    });
  }
  return parseClassName;
};
var sortModifiers = (modifiers) => {
  if (modifiers.length <= 1) {
    return modifiers;
  }
  const sortedModifiers = [];
  let unsortedModifiers = [];
  modifiers.forEach((modifier) => {
    const isArbitraryVariant = modifier[0] === "[";
    if (isArbitraryVariant) {
      sortedModifiers.push(...unsortedModifiers.sort(), modifier);
      unsortedModifiers = [];
    } else {
      unsortedModifiers.push(modifier);
    }
  });
  sortedModifiers.push(...unsortedModifiers.sort());
  return sortedModifiers;
};
var createConfigUtils = (config) => ({
  cache: createLruCache(config.cacheSize),
  parseClassName: createParseClassName(config),
  ...createClassGroupUtils(config)
});
var SPLIT_CLASSES_REGEX = /\s+/;
var mergeClassList = (classList, configUtils) => {
  const {
    parseClassName,
    getClassGroupId,
    getConflictingClassGroupIds
  } = configUtils;
  const classGroupsInConflict = [];
  const classNames = classList.trim().split(SPLIT_CLASSES_REGEX);
  let result = "";
  for (let index = classNames.length - 1; index >= 0; index -= 1) {
    const originalClassName = classNames[index];
    const {
      modifiers,
      hasImportantModifier,
      baseClassName,
      maybePostfixModifierPosition
    } = parseClassName(originalClassName);
    let hasPostfixModifier = Boolean(maybePostfixModifierPosition);
    let classGroupId = getClassGroupId(hasPostfixModifier ? baseClassName.substring(0, maybePostfixModifierPosition) : baseClassName);
    if (!classGroupId) {
      if (!hasPostfixModifier) {
        result = originalClassName + (result.length > 0 ? " " + result : result);
        continue;
      }
      classGroupId = getClassGroupId(baseClassName);
      if (!classGroupId) {
        result = originalClassName + (result.length > 0 ? " " + result : result);
        continue;
      }
      hasPostfixModifier = false;
    }
    const variantModifier = sortModifiers(modifiers).join(":");
    const modifierId = hasImportantModifier ? variantModifier + IMPORTANT_MODIFIER : variantModifier;
    const classId = modifierId + classGroupId;
    if (classGroupsInConflict.includes(classId)) {
      continue;
    }
    classGroupsInConflict.push(classId);
    const conflictGroups = getConflictingClassGroupIds(classGroupId, hasPostfixModifier);
    for (let i = 0; i < conflictGroups.length; ++i) {
      const group = conflictGroups[i];
      classGroupsInConflict.push(modifierId + group);
    }
    result = originalClassName + (result.length > 0 ? " " + result : result);
  }
  return result;
};
function twJoin() {
  let index = 0;
  let argument;
  let resolvedValue;
  let string = "";
  while (index < arguments.length) {
    if (argument = arguments[index++]) {
      if (resolvedValue = toValue(argument)) {
        string && (string += " ");
        string += resolvedValue;
      }
    }
  }
  return string;
}
var toValue = (mix) => {
  if (typeof mix === "string") {
    return mix;
  }
  let resolvedValue;
  let string = "";
  for (let k = 0; k < mix.length; k++) {
    if (mix[k]) {
      if (resolvedValue = toValue(mix[k])) {
        string && (string += " ");
        string += resolvedValue;
      }
    }
  }
  return string;
};
function createTailwindMerge(createConfigFirst, ...createConfigRest) {
  let configUtils;
  let cacheGet;
  let cacheSet;
  let functionToCall = initTailwindMerge;
  function initTailwindMerge(classList) {
    const config = createConfigRest.reduce((previousConfig, createConfigCurrent) => createConfigCurrent(previousConfig), createConfigFirst());
    configUtils = createConfigUtils(config);
    cacheGet = configUtils.cache.get;
    cacheSet = configUtils.cache.set;
    functionToCall = tailwindMerge;
    return tailwindMerge(classList);
  }
  function tailwindMerge(classList) {
    const cachedResult = cacheGet(classList);
    if (cachedResult) {
      return cachedResult;
    }
    const result = mergeClassList(classList, configUtils);
    cacheSet(classList, result);
    return result;
  }
  return function callTailwindMerge() {
    return functionToCall(twJoin.apply(null, arguments));
  };
}
var fromTheme = (key) => {
  const themeGetter = (theme) => theme[key] || [];
  themeGetter.isThemeGetter = true;
  return themeGetter;
};
var arbitraryValueRegex = /^\[(?:([a-z-]+):)?(.+)\]$/i;
var fractionRegex = /^\d+\/\d+$/;
var stringLengths = /* @__PURE__ */ new Set(["px", "full", "screen"]);
var tshirtUnitRegex = /^(\d+(\.\d+)?)?(xs|sm|md|lg|xl)$/;
var lengthUnitRegex = /\d+(%|px|r?em|[sdl]?v([hwib]|min|max)|pt|pc|in|cm|mm|cap|ch|ex|r?lh|cq(w|h|i|b|min|max))|\b(calc|min|max|clamp)\(.+\)|^0$/;
var colorFunctionRegex = /^(rgba?|hsla?|hwb|(ok)?(lab|lch))\(.+\)$/;
var shadowRegex = /^(inset_)?-?((\d+)?\.?(\d+)[a-z]+|0)_-?((\d+)?\.?(\d+)[a-z]+|0)/;
var imageRegex = /^(url|image|image-set|cross-fade|element|(repeating-)?(linear|radial|conic)-gradient)\(.+\)$/;
var isLength = (value) => isNumber(value) || stringLengths.has(value) || fractionRegex.test(value);
var isArbitraryLength = (value) => getIsArbitraryValue(value, "length", isLengthOnly);
var isNumber = (value) => Boolean(value) && !Number.isNaN(Number(value));
var isArbitraryNumber = (value) => getIsArbitraryValue(value, "number", isNumber);
var isInteger = (value) => Boolean(value) && Number.isInteger(Number(value));
var isPercent = (value) => value.endsWith("%") && isNumber(value.slice(0, -1));
var isArbitraryValue = (value) => arbitraryValueRegex.test(value);
var isTshirtSize = (value) => tshirtUnitRegex.test(value);
var sizeLabels = /* @__PURE__ */ new Set(["length", "size", "percentage"]);
var isArbitrarySize = (value) => getIsArbitraryValue(value, sizeLabels, isNever);
var isArbitraryPosition = (value) => getIsArbitraryValue(value, "position", isNever);
var imageLabels = /* @__PURE__ */ new Set(["image", "url"]);
var isArbitraryImage = (value) => getIsArbitraryValue(value, imageLabels, isImage);
var isArbitraryShadow = (value) => getIsArbitraryValue(value, "", isShadow);
var isAny = () => true;
var getIsArbitraryValue = (value, label, testValue) => {
  const result = arbitraryValueRegex.exec(value);
  if (result) {
    if (result[1]) {
      return typeof label === "string" ? result[1] === label : label.has(result[1]);
    }
    return testValue(result[2]);
  }
  return false;
};
var isLengthOnly = (value) => (
  // `colorFunctionRegex` check is necessary because color functions can have percentages in them which which would be incorrectly classified as lengths.
  // For example, `hsl(0 0% 0%)` would be classified as a length without this check.
  // I could also use lookbehind assertion in `lengthUnitRegex` but that isn't supported widely enough.
  lengthUnitRegex.test(value) && !colorFunctionRegex.test(value)
);
var isNever = () => false;
var isShadow = (value) => shadowRegex.test(value);
var isImage = (value) => imageRegex.test(value);
var getDefaultConfig = () => {
  const colors = fromTheme("colors");
  const spacing = fromTheme("spacing");
  const blur = fromTheme("blur");
  const brightness = fromTheme("brightness");
  const borderColor = fromTheme("borderColor");
  const borderRadius = fromTheme("borderRadius");
  const borderSpacing = fromTheme("borderSpacing");
  const borderWidth = fromTheme("borderWidth");
  const contrast = fromTheme("contrast");
  const grayscale = fromTheme("grayscale");
  const hueRotate = fromTheme("hueRotate");
  const invert = fromTheme("invert");
  const gap = fromTheme("gap");
  const gradientColorStops = fromTheme("gradientColorStops");
  const gradientColorStopPositions = fromTheme("gradientColorStopPositions");
  const inset = fromTheme("inset");
  const margin = fromTheme("margin");
  const opacity = fromTheme("opacity");
  const padding = fromTheme("padding");
  const saturate = fromTheme("saturate");
  const scale = fromTheme("scale");
  const sepia = fromTheme("sepia");
  const skew = fromTheme("skew");
  const space = fromTheme("space");
  const translate = fromTheme("translate");
  const getOverscroll = () => ["auto", "contain", "none"];
  const getOverflow = () => ["auto", "hidden", "clip", "visible", "scroll"];
  const getSpacingWithAutoAndArbitrary = () => ["auto", isArbitraryValue, spacing];
  const getSpacingWithArbitrary = () => [isArbitraryValue, spacing];
  const getLengthWithEmptyAndArbitrary = () => ["", isLength, isArbitraryLength];
  const getNumberWithAutoAndArbitrary = () => ["auto", isNumber, isArbitraryValue];
  const getPositions = () => ["bottom", "center", "left", "left-bottom", "left-top", "right", "right-bottom", "right-top", "top"];
  const getLineStyles = () => ["solid", "dashed", "dotted", "double", "none"];
  const getBlendModes = () => ["normal", "multiply", "screen", "overlay", "darken", "lighten", "color-dodge", "color-burn", "hard-light", "soft-light", "difference", "exclusion", "hue", "saturation", "color", "luminosity"];
  const getAlign = () => ["start", "end", "center", "between", "around", "evenly", "stretch"];
  const getZeroAndEmpty = () => ["", "0", isArbitraryValue];
  const getBreaks = () => ["auto", "avoid", "all", "avoid-page", "page", "left", "right", "column"];
  const getNumberAndArbitrary = () => [isNumber, isArbitraryValue];
  return {
    cacheSize: 500,
    separator: ":",
    theme: {
      colors: [isAny],
      spacing: [isLength, isArbitraryLength],
      blur: ["none", "", isTshirtSize, isArbitraryValue],
      brightness: getNumberAndArbitrary(),
      borderColor: [colors],
      borderRadius: ["none", "", "full", isTshirtSize, isArbitraryValue],
      borderSpacing: getSpacingWithArbitrary(),
      borderWidth: getLengthWithEmptyAndArbitrary(),
      contrast: getNumberAndArbitrary(),
      grayscale: getZeroAndEmpty(),
      hueRotate: getNumberAndArbitrary(),
      invert: getZeroAndEmpty(),
      gap: getSpacingWithArbitrary(),
      gradientColorStops: [colors],
      gradientColorStopPositions: [isPercent, isArbitraryLength],
      inset: getSpacingWithAutoAndArbitrary(),
      margin: getSpacingWithAutoAndArbitrary(),
      opacity: getNumberAndArbitrary(),
      padding: getSpacingWithArbitrary(),
      saturate: getNumberAndArbitrary(),
      scale: getNumberAndArbitrary(),
      sepia: getZeroAndEmpty(),
      skew: getNumberAndArbitrary(),
      space: getSpacingWithArbitrary(),
      translate: getSpacingWithArbitrary()
    },
    classGroups: {
      // Layout
      /**
       * Aspect Ratio
       * @see https://tailwindcss.com/docs/aspect-ratio
       */
      aspect: [{
        aspect: ["auto", "square", "video", isArbitraryValue]
      }],
      /**
       * Container
       * @see https://tailwindcss.com/docs/container
       */
      container: ["container"],
      /**
       * Columns
       * @see https://tailwindcss.com/docs/columns
       */
      columns: [{
        columns: [isTshirtSize]
      }],
      /**
       * Break After
       * @see https://tailwindcss.com/docs/break-after
       */
      "break-after": [{
        "break-after": getBreaks()
      }],
      /**
       * Break Before
       * @see https://tailwindcss.com/docs/break-before
       */
      "break-before": [{
        "break-before": getBreaks()
      }],
      /**
       * Break Inside
       * @see https://tailwindcss.com/docs/break-inside
       */
      "break-inside": [{
        "break-inside": ["auto", "avoid", "avoid-page", "avoid-column"]
      }],
      /**
       * Box Decoration Break
       * @see https://tailwindcss.com/docs/box-decoration-break
       */
      "box-decoration": [{
        "box-decoration": ["slice", "clone"]
      }],
      /**
       * Box Sizing
       * @see https://tailwindcss.com/docs/box-sizing
       */
      box: [{
        box: ["border", "content"]
      }],
      /**
       * Display
       * @see https://tailwindcss.com/docs/display
       */
      display: ["block", "inline-block", "inline", "flex", "inline-flex", "table", "inline-table", "table-caption", "table-cell", "table-column", "table-column-group", "table-footer-group", "table-header-group", "table-row-group", "table-row", "flow-root", "grid", "inline-grid", "contents", "list-item", "hidden"],
      /**
       * Floats
       * @see https://tailwindcss.com/docs/float
       */
      float: [{
        float: ["right", "left", "none", "start", "end"]
      }],
      /**
       * Clear
       * @see https://tailwindcss.com/docs/clear
       */
      clear: [{
        clear: ["left", "right", "both", "none", "start", "end"]
      }],
      /**
       * Isolation
       * @see https://tailwindcss.com/docs/isolation
       */
      isolation: ["isolate", "isolation-auto"],
      /**
       * Object Fit
       * @see https://tailwindcss.com/docs/object-fit
       */
      "object-fit": [{
        object: ["contain", "cover", "fill", "none", "scale-down"]
      }],
      /**
       * Object Position
       * @see https://tailwindcss.com/docs/object-position
       */
      "object-position": [{
        object: [...getPositions(), isArbitraryValue]
      }],
      /**
       * Overflow
       * @see https://tailwindcss.com/docs/overflow
       */
      overflow: [{
        overflow: getOverflow()
      }],
      /**
       * Overflow X
       * @see https://tailwindcss.com/docs/overflow
       */
      "overflow-x": [{
        "overflow-x": getOverflow()
      }],
      /**
       * Overflow Y
       * @see https://tailwindcss.com/docs/overflow
       */
      "overflow-y": [{
        "overflow-y": getOverflow()
      }],
      /**
       * Overscroll Behavior
       * @see https://tailwindcss.com/docs/overscroll-behavior
       */
      overscroll: [{
        overscroll: getOverscroll()
      }],
      /**
       * Overscroll Behavior X
       * @see https://tailwindcss.com/docs/overscroll-behavior
       */
      "overscroll-x": [{
        "overscroll-x": getOverscroll()
      }],
      /**
       * Overscroll Behavior Y
       * @see https://tailwindcss.com/docs/overscroll-behavior
       */
      "overscroll-y": [{
        "overscroll-y": getOverscroll()
      }],
      /**
       * Position
       * @see https://tailwindcss.com/docs/position
       */
      position: ["static", "fixed", "absolute", "relative", "sticky"],
      /**
       * Top / Right / Bottom / Left
       * @see https://tailwindcss.com/docs/top-right-bottom-left
       */
      inset: [{
        inset: [inset]
      }],
      /**
       * Right / Left
       * @see https://tailwindcss.com/docs/top-right-bottom-left
       */
      "inset-x": [{
        "inset-x": [inset]
      }],
      /**
       * Top / Bottom
       * @see https://tailwindcss.com/docs/top-right-bottom-left
       */
      "inset-y": [{
        "inset-y": [inset]
      }],
      /**
       * Start
       * @see https://tailwindcss.com/docs/top-right-bottom-left
       */
      start: [{
        start: [inset]
      }],
      /**
       * End
       * @see https://tailwindcss.com/docs/top-right-bottom-left
       */
      end: [{
        end: [inset]
      }],
      /**
       * Top
       * @see https://tailwindcss.com/docs/top-right-bottom-left
       */
      top: [{
        top: [inset]
      }],
      /**
       * Right
       * @see https://tailwindcss.com/docs/top-right-bottom-left
       */
      right: [{
        right: [inset]
      }],
      /**
       * Bottom
       * @see https://tailwindcss.com/docs/top-right-bottom-left
       */
      bottom: [{
        bottom: [inset]
      }],
      /**
       * Left
       * @see https://tailwindcss.com/docs/top-right-bottom-left
       */
      left: [{
        left: [inset]
      }],
      /**
       * Visibility
       * @see https://tailwindcss.com/docs/visibility
       */
      visibility: ["visible", "invisible", "collapse"],
      /**
       * Z-Index
       * @see https://tailwindcss.com/docs/z-index
       */
      z: [{
        z: ["auto", isInteger, isArbitraryValue]
      }],
      // Flexbox and Grid
      /**
       * Flex Basis
       * @see https://tailwindcss.com/docs/flex-basis
       */
      basis: [{
        basis: getSpacingWithAutoAndArbitrary()
      }],
      /**
       * Flex Direction
       * @see https://tailwindcss.com/docs/flex-direction
       */
      "flex-direction": [{
        flex: ["row", "row-reverse", "col", "col-reverse"]
      }],
      /**
       * Flex Wrap
       * @see https://tailwindcss.com/docs/flex-wrap
       */
      "flex-wrap": [{
        flex: ["wrap", "wrap-reverse", "nowrap"]
      }],
      /**
       * Flex
       * @see https://tailwindcss.com/docs/flex
       */
      flex: [{
        flex: ["1", "auto", "initial", "none", isArbitraryValue]
      }],
      /**
       * Flex Grow
       * @see https://tailwindcss.com/docs/flex-grow
       */
      grow: [{
        grow: getZeroAndEmpty()
      }],
      /**
       * Flex Shrink
       * @see https://tailwindcss.com/docs/flex-shrink
       */
      shrink: [{
        shrink: getZeroAndEmpty()
      }],
      /**
       * Order
       * @see https://tailwindcss.com/docs/order
       */
      order: [{
        order: ["first", "last", "none", isInteger, isArbitraryValue]
      }],
      /**
       * Grid Template Columns
       * @see https://tailwindcss.com/docs/grid-template-columns
       */
      "grid-cols": [{
        "grid-cols": [isAny]
      }],
      /**
       * Grid Column Start / End
       * @see https://tailwindcss.com/docs/grid-column
       */
      "col-start-end": [{
        col: ["auto", {
          span: ["full", isInteger, isArbitraryValue]
        }, isArbitraryValue]
      }],
      /**
       * Grid Column Start
       * @see https://tailwindcss.com/docs/grid-column
       */
      "col-start": [{
        "col-start": getNumberWithAutoAndArbitrary()
      }],
      /**
       * Grid Column End
       * @see https://tailwindcss.com/docs/grid-column
       */
      "col-end": [{
        "col-end": getNumberWithAutoAndArbitrary()
      }],
      /**
       * Grid Template Rows
       * @see https://tailwindcss.com/docs/grid-template-rows
       */
      "grid-rows": [{
        "grid-rows": [isAny]
      }],
      /**
       * Grid Row Start / End
       * @see https://tailwindcss.com/docs/grid-row
       */
      "row-start-end": [{
        row: ["auto", {
          span: [isInteger, isArbitraryValue]
        }, isArbitraryValue]
      }],
      /**
       * Grid Row Start
       * @see https://tailwindcss.com/docs/grid-row
       */
      "row-start": [{
        "row-start": getNumberWithAutoAndArbitrary()
      }],
      /**
       * Grid Row End
       * @see https://tailwindcss.com/docs/grid-row
       */
      "row-end": [{
        "row-end": getNumberWithAutoAndArbitrary()
      }],
      /**
       * Grid Auto Flow
       * @see https://tailwindcss.com/docs/grid-auto-flow
       */
      "grid-flow": [{
        "grid-flow": ["row", "col", "dense", "row-dense", "col-dense"]
      }],
      /**
       * Grid Auto Columns
       * @see https://tailwindcss.com/docs/grid-auto-columns
       */
      "auto-cols": [{
        "auto-cols": ["auto", "min", "max", "fr", isArbitraryValue]
      }],
      /**
       * Grid Auto Rows
       * @see https://tailwindcss.com/docs/grid-auto-rows
       */
      "auto-rows": [{
        "auto-rows": ["auto", "min", "max", "fr", isArbitraryValue]
      }],
      /**
       * Gap
       * @see https://tailwindcss.com/docs/gap
       */
      gap: [{
        gap: [gap]
      }],
      /**
       * Gap X
       * @see https://tailwindcss.com/docs/gap
       */
      "gap-x": [{
        "gap-x": [gap]
      }],
      /**
       * Gap Y
       * @see https://tailwindcss.com/docs/gap
       */
      "gap-y": [{
        "gap-y": [gap]
      }],
      /**
       * Justify Content
       * @see https://tailwindcss.com/docs/justify-content
       */
      "justify-content": [{
        justify: ["normal", ...getAlign()]
      }],
      /**
       * Justify Items
       * @see https://tailwindcss.com/docs/justify-items
       */
      "justify-items": [{
        "justify-items": ["start", "end", "center", "stretch"]
      }],
      /**
       * Justify Self
       * @see https://tailwindcss.com/docs/justify-self
       */
      "justify-self": [{
        "justify-self": ["auto", "start", "end", "center", "stretch"]
      }],
      /**
       * Align Content
       * @see https://tailwindcss.com/docs/align-content
       */
      "align-content": [{
        content: ["normal", ...getAlign(), "baseline"]
      }],
      /**
       * Align Items
       * @see https://tailwindcss.com/docs/align-items
       */
      "align-items": [{
        items: ["start", "end", "center", "baseline", "stretch"]
      }],
      /**
       * Align Self
       * @see https://tailwindcss.com/docs/align-self
       */
      "align-self": [{
        self: ["auto", "start", "end", "center", "stretch", "baseline"]
      }],
      /**
       * Place Content
       * @see https://tailwindcss.com/docs/place-content
       */
      "place-content": [{
        "place-content": [...getAlign(), "baseline"]
      }],
      /**
       * Place Items
       * @see https://tailwindcss.com/docs/place-items
       */
      "place-items": [{
        "place-items": ["start", "end", "center", "baseline", "stretch"]
      }],
      /**
       * Place Self
       * @see https://tailwindcss.com/docs/place-self
       */
      "place-self": [{
        "place-self": ["auto", "start", "end", "center", "stretch"]
      }],
      // Spacing
      /**
       * Padding
       * @see https://tailwindcss.com/docs/padding
       */
      p: [{
        p: [padding]
      }],
      /**
       * Padding X
       * @see https://tailwindcss.com/docs/padding
       */
      px: [{
        px: [padding]
      }],
      /**
       * Padding Y
       * @see https://tailwindcss.com/docs/padding
       */
      py: [{
        py: [padding]
      }],
      /**
       * Padding Start
       * @see https://tailwindcss.com/docs/padding
       */
      ps: [{
        ps: [padding]
      }],
      /**
       * Padding End
       * @see https://tailwindcss.com/docs/padding
       */
      pe: [{
        pe: [padding]
      }],
      /**
       * Padding Top
       * @see https://tailwindcss.com/docs/padding
       */
      pt: [{
        pt: [padding]
      }],
      /**
       * Padding Right
       * @see https://tailwindcss.com/docs/padding
       */
      pr: [{
        pr: [padding]
      }],
      /**
       * Padding Bottom
       * @see https://tailwindcss.com/docs/padding
       */
      pb: [{
        pb: [padding]
      }],
      /**
       * Padding Left
       * @see https://tailwindcss.com/docs/padding
       */
      pl: [{
        pl: [padding]
      }],
      /**
       * Margin
       * @see https://tailwindcss.com/docs/margin
       */
      m: [{
        m: [margin]
      }],
      /**
       * Margin X
       * @see https://tailwindcss.com/docs/margin
       */
      mx: [{
        mx: [margin]
      }],
      /**
       * Margin Y
       * @see https://tailwindcss.com/docs/margin
       */
      my: [{
        my: [margin]
      }],
      /**
       * Margin Start
       * @see https://tailwindcss.com/docs/margin
       */
      ms: [{
        ms: [margin]
      }],
      /**
       * Margin End
       * @see https://tailwindcss.com/docs/margin
       */
      me: [{
        me: [margin]
      }],
      /**
       * Margin Top
       * @see https://tailwindcss.com/docs/margin
       */
      mt: [{
        mt: [margin]
      }],
      /**
       * Margin Right
       * @see https://tailwindcss.com/docs/margin
       */
      mr: [{
        mr: [margin]
      }],
      /**
       * Margin Bottom
       * @see https://tailwindcss.com/docs/margin
       */
      mb: [{
        mb: [margin]
      }],
      /**
       * Margin Left
       * @see https://tailwindcss.com/docs/margin
       */
      ml: [{
        ml: [margin]
      }],
      /**
       * Space Between X
       * @see https://tailwindcss.com/docs/space
       */
      "space-x": [{
        "space-x": [space]
      }],
      /**
       * Space Between X Reverse
       * @see https://tailwindcss.com/docs/space
       */
      "space-x-reverse": ["space-x-reverse"],
      /**
       * Space Between Y
       * @see https://tailwindcss.com/docs/space
       */
      "space-y": [{
        "space-y": [space]
      }],
      /**
       * Space Between Y Reverse
       * @see https://tailwindcss.com/docs/space
       */
      "space-y-reverse": ["space-y-reverse"],
      // Sizing
      /**
       * Width
       * @see https://tailwindcss.com/docs/width
       */
      w: [{
        w: ["auto", "min", "max", "fit", "svw", "lvw", "dvw", isArbitraryValue, spacing]
      }],
      /**
       * Min-Width
       * @see https://tailwindcss.com/docs/min-width
       */
      "min-w": [{
        "min-w": [isArbitraryValue, spacing, "min", "max", "fit"]
      }],
      /**
       * Max-Width
       * @see https://tailwindcss.com/docs/max-width
       */
      "max-w": [{
        "max-w": [isArbitraryValue, spacing, "none", "full", "min", "max", "fit", "prose", {
          screen: [isTshirtSize]
        }, isTshirtSize]
      }],
      /**
       * Height
       * @see https://tailwindcss.com/docs/height
       */
      h: [{
        h: [isArbitraryValue, spacing, "auto", "min", "max", "fit", "svh", "lvh", "dvh"]
      }],
      /**
       * Min-Height
       * @see https://tailwindcss.com/docs/min-height
       */
      "min-h": [{
        "min-h": [isArbitraryValue, spacing, "min", "max", "fit", "svh", "lvh", "dvh"]
      }],
      /**
       * Max-Height
       * @see https://tailwindcss.com/docs/max-height
       */
      "max-h": [{
        "max-h": [isArbitraryValue, spacing, "min", "max", "fit", "svh", "lvh", "dvh"]
      }],
      /**
       * Size
       * @see https://tailwindcss.com/docs/size
       */
      size: [{
        size: [isArbitraryValue, spacing, "auto", "min", "max", "fit"]
      }],
      // Typography
      /**
       * Font Size
       * @see https://tailwindcss.com/docs/font-size
       */
      "font-size": [{
        text: ["base", isTshirtSize, isArbitraryLength]
      }],
      /**
       * Font Smoothing
       * @see https://tailwindcss.com/docs/font-smoothing
       */
      "font-smoothing": ["antialiased", "subpixel-antialiased"],
      /**
       * Font Style
       * @see https://tailwindcss.com/docs/font-style
       */
      "font-style": ["italic", "not-italic"],
      /**
       * Font Weight
       * @see https://tailwindcss.com/docs/font-weight
       */
      "font-weight": [{
        font: ["thin", "extralight", "light", "normal", "medium", "semibold", "bold", "extrabold", "black", isArbitraryNumber]
      }],
      /**
       * Font Family
       * @see https://tailwindcss.com/docs/font-family
       */
      "font-family": [{
        font: [isAny]
      }],
      /**
       * Font Variant Numeric
       * @see https://tailwindcss.com/docs/font-variant-numeric
       */
      "fvn-normal": ["normal-nums"],
      /**
       * Font Variant Numeric
       * @see https://tailwindcss.com/docs/font-variant-numeric
       */
      "fvn-ordinal": ["ordinal"],
      /**
       * Font Variant Numeric
       * @see https://tailwindcss.com/docs/font-variant-numeric
       */
      "fvn-slashed-zero": ["slashed-zero"],
      /**
       * Font Variant Numeric
       * @see https://tailwindcss.com/docs/font-variant-numeric
       */
      "fvn-figure": ["lining-nums", "oldstyle-nums"],
      /**
       * Font Variant Numeric
       * @see https://tailwindcss.com/docs/font-variant-numeric
       */
      "fvn-spacing": ["proportional-nums", "tabular-nums"],
      /**
       * Font Variant Numeric
       * @see https://tailwindcss.com/docs/font-variant-numeric
       */
      "fvn-fraction": ["diagonal-fractions", "stacked-fractions"],
      /**
       * Letter Spacing
       * @see https://tailwindcss.com/docs/letter-spacing
       */
      tracking: [{
        tracking: ["tighter", "tight", "normal", "wide", "wider", "widest", isArbitraryValue]
      }],
      /**
       * Line Clamp
       * @see https://tailwindcss.com/docs/line-clamp
       */
      "line-clamp": [{
        "line-clamp": ["none", isNumber, isArbitraryNumber]
      }],
      /**
       * Line Height
       * @see https://tailwindcss.com/docs/line-height
       */
      leading: [{
        leading: ["none", "tight", "snug", "normal", "relaxed", "loose", isLength, isArbitraryValue]
      }],
      /**
       * List Style Image
       * @see https://tailwindcss.com/docs/list-style-image
       */
      "list-image": [{
        "list-image": ["none", isArbitraryValue]
      }],
      /**
       * List Style Type
       * @see https://tailwindcss.com/docs/list-style-type
       */
      "list-style-type": [{
        list: ["none", "disc", "decimal", isArbitraryValue]
      }],
      /**
       * List Style Position
       * @see https://tailwindcss.com/docs/list-style-position
       */
      "list-style-position": [{
        list: ["inside", "outside"]
      }],
      /**
       * Placeholder Color
       * @deprecated since Tailwind CSS v3.0.0
       * @see https://tailwindcss.com/docs/placeholder-color
       */
      "placeholder-color": [{
        placeholder: [colors]
      }],
      /**
       * Placeholder Opacity
       * @see https://tailwindcss.com/docs/placeholder-opacity
       */
      "placeholder-opacity": [{
        "placeholder-opacity": [opacity]
      }],
      /**
       * Text Alignment
       * @see https://tailwindcss.com/docs/text-align
       */
      "text-alignment": [{
        text: ["left", "center", "right", "justify", "start", "end"]
      }],
      /**
       * Text Color
       * @see https://tailwindcss.com/docs/text-color
       */
      "text-color": [{
        text: [colors]
      }],
      /**
       * Text Opacity
       * @see https://tailwindcss.com/docs/text-opacity
       */
      "text-opacity": [{
        "text-opacity": [opacity]
      }],
      /**
       * Text Decoration
       * @see https://tailwindcss.com/docs/text-decoration
       */
      "text-decoration": ["underline", "overline", "line-through", "no-underline"],
      /**
       * Text Decoration Style
       * @see https://tailwindcss.com/docs/text-decoration-style
       */
      "text-decoration-style": [{
        decoration: [...getLineStyles(), "wavy"]
      }],
      /**
       * Text Decoration Thickness
       * @see https://tailwindcss.com/docs/text-decoration-thickness
       */
      "text-decoration-thickness": [{
        decoration: ["auto", "from-font", isLength, isArbitraryLength]
      }],
      /**
       * Text Underline Offset
       * @see https://tailwindcss.com/docs/text-underline-offset
       */
      "underline-offset": [{
        "underline-offset": ["auto", isLength, isArbitraryValue]
      }],
      /**
       * Text Decoration Color
       * @see https://tailwindcss.com/docs/text-decoration-color
       */
      "text-decoration-color": [{
        decoration: [colors]
      }],
      /**
       * Text Transform
       * @see https://tailwindcss.com/docs/text-transform
       */
      "text-transform": ["uppercase", "lowercase", "capitalize", "normal-case"],
      /**
       * Text Overflow
       * @see https://tailwindcss.com/docs/text-overflow
       */
      "text-overflow": ["truncate", "text-ellipsis", "text-clip"],
      /**
       * Text Wrap
       * @see https://tailwindcss.com/docs/text-wrap
       */
      "text-wrap": [{
        text: ["wrap", "nowrap", "balance", "pretty"]
      }],
      /**
       * Text Indent
       * @see https://tailwindcss.com/docs/text-indent
       */
      indent: [{
        indent: getSpacingWithArbitrary()
      }],
      /**
       * Vertical Alignment
       * @see https://tailwindcss.com/docs/vertical-align
       */
      "vertical-align": [{
        align: ["baseline", "top", "middle", "bottom", "text-top", "text-bottom", "sub", "super", isArbitraryValue]
      }],
      /**
       * Whitespace
       * @see https://tailwindcss.com/docs/whitespace
       */
      whitespace: [{
        whitespace: ["normal", "nowrap", "pre", "pre-line", "pre-wrap", "break-spaces"]
      }],
      /**
       * Word Break
       * @see https://tailwindcss.com/docs/word-break
       */
      break: [{
        break: ["normal", "words", "all", "keep"]
      }],
      /**
       * Hyphens
       * @see https://tailwindcss.com/docs/hyphens
       */
      hyphens: [{
        hyphens: ["none", "manual", "auto"]
      }],
      /**
       * Content
       * @see https://tailwindcss.com/docs/content
       */
      content: [{
        content: ["none", isArbitraryValue]
      }],
      // Backgrounds
      /**
       * Background Attachment
       * @see https://tailwindcss.com/docs/background-attachment
       */
      "bg-attachment": [{
        bg: ["fixed", "local", "scroll"]
      }],
      /**
       * Background Clip
       * @see https://tailwindcss.com/docs/background-clip
       */
      "bg-clip": [{
        "bg-clip": ["border", "padding", "content", "text"]
      }],
      /**
       * Background Opacity
       * @deprecated since Tailwind CSS v3.0.0
       * @see https://tailwindcss.com/docs/background-opacity
       */
      "bg-opacity": [{
        "bg-opacity": [opacity]
      }],
      /**
       * Background Origin
       * @see https://tailwindcss.com/docs/background-origin
       */
      "bg-origin": [{
        "bg-origin": ["border", "padding", "content"]
      }],
      /**
       * Background Position
       * @see https://tailwindcss.com/docs/background-position
       */
      "bg-position": [{
        bg: [...getPositions(), isArbitraryPosition]
      }],
      /**
       * Background Repeat
       * @see https://tailwindcss.com/docs/background-repeat
       */
      "bg-repeat": [{
        bg: ["no-repeat", {
          repeat: ["", "x", "y", "round", "space"]
        }]
      }],
      /**
       * Background Size
       * @see https://tailwindcss.com/docs/background-size
       */
      "bg-size": [{
        bg: ["auto", "cover", "contain", isArbitrarySize]
      }],
      /**
       * Background Image
       * @see https://tailwindcss.com/docs/background-image
       */
      "bg-image": [{
        bg: ["none", {
          "gradient-to": ["t", "tr", "r", "br", "b", "bl", "l", "tl"]
        }, isArbitraryImage]
      }],
      /**
       * Background Color
       * @see https://tailwindcss.com/docs/background-color
       */
      "bg-color": [{
        bg: [colors]
      }],
      /**
       * Gradient Color Stops From Position
       * @see https://tailwindcss.com/docs/gradient-color-stops
       */
      "gradient-from-pos": [{
        from: [gradientColorStopPositions]
      }],
      /**
       * Gradient Color Stops Via Position
       * @see https://tailwindcss.com/docs/gradient-color-stops
       */
      "gradient-via-pos": [{
        via: [gradientColorStopPositions]
      }],
      /**
       * Gradient Color Stops To Position
       * @see https://tailwindcss.com/docs/gradient-color-stops
       */
      "gradient-to-pos": [{
        to: [gradientColorStopPositions]
      }],
      /**
       * Gradient Color Stops From
       * @see https://tailwindcss.com/docs/gradient-color-stops
       */
      "gradient-from": [{
        from: [gradientColorStops]
      }],
      /**
       * Gradient Color Stops Via
       * @see https://tailwindcss.com/docs/gradient-color-stops
       */
      "gradient-via": [{
        via: [gradientColorStops]
      }],
      /**
       * Gradient Color Stops To
       * @see https://tailwindcss.com/docs/gradient-color-stops
       */
      "gradient-to": [{
        to: [gradientColorStops]
      }],
      // Borders
      /**
       * Border Radius
       * @see https://tailwindcss.com/docs/border-radius
       */
      rounded: [{
        rounded: [borderRadius]
      }],
      /**
       * Border Radius Start
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-s": [{
        "rounded-s": [borderRadius]
      }],
      /**
       * Border Radius End
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-e": [{
        "rounded-e": [borderRadius]
      }],
      /**
       * Border Radius Top
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-t": [{
        "rounded-t": [borderRadius]
      }],
      /**
       * Border Radius Right
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-r": [{
        "rounded-r": [borderRadius]
      }],
      /**
       * Border Radius Bottom
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-b": [{
        "rounded-b": [borderRadius]
      }],
      /**
       * Border Radius Left
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-l": [{
        "rounded-l": [borderRadius]
      }],
      /**
       * Border Radius Start Start
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-ss": [{
        "rounded-ss": [borderRadius]
      }],
      /**
       * Border Radius Start End
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-se": [{
        "rounded-se": [borderRadius]
      }],
      /**
       * Border Radius End End
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-ee": [{
        "rounded-ee": [borderRadius]
      }],
      /**
       * Border Radius End Start
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-es": [{
        "rounded-es": [borderRadius]
      }],
      /**
       * Border Radius Top Left
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-tl": [{
        "rounded-tl": [borderRadius]
      }],
      /**
       * Border Radius Top Right
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-tr": [{
        "rounded-tr": [borderRadius]
      }],
      /**
       * Border Radius Bottom Right
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-br": [{
        "rounded-br": [borderRadius]
      }],
      /**
       * Border Radius Bottom Left
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-bl": [{
        "rounded-bl": [borderRadius]
      }],
      /**
       * Border Width
       * @see https://tailwindcss.com/docs/border-width
       */
      "border-w": [{
        border: [borderWidth]
      }],
      /**
       * Border Width X
       * @see https://tailwindcss.com/docs/border-width
       */
      "border-w-x": [{
        "border-x": [borderWidth]
      }],
      /**
       * Border Width Y
       * @see https://tailwindcss.com/docs/border-width
       */
      "border-w-y": [{
        "border-y": [borderWidth]
      }],
      /**
       * Border Width Start
       * @see https://tailwindcss.com/docs/border-width
       */
      "border-w-s": [{
        "border-s": [borderWidth]
      }],
      /**
       * Border Width End
       * @see https://tailwindcss.com/docs/border-width
       */
      "border-w-e": [{
        "border-e": [borderWidth]
      }],
      /**
       * Border Width Top
       * @see https://tailwindcss.com/docs/border-width
       */
      "border-w-t": [{
        "border-t": [borderWidth]
      }],
      /**
       * Border Width Right
       * @see https://tailwindcss.com/docs/border-width
       */
      "border-w-r": [{
        "border-r": [borderWidth]
      }],
      /**
       * Border Width Bottom
       * @see https://tailwindcss.com/docs/border-width
       */
      "border-w-b": [{
        "border-b": [borderWidth]
      }],
      /**
       * Border Width Left
       * @see https://tailwindcss.com/docs/border-width
       */
      "border-w-l": [{
        "border-l": [borderWidth]
      }],
      /**
       * Border Opacity
       * @see https://tailwindcss.com/docs/border-opacity
       */
      "border-opacity": [{
        "border-opacity": [opacity]
      }],
      /**
       * Border Style
       * @see https://tailwindcss.com/docs/border-style
       */
      "border-style": [{
        border: [...getLineStyles(), "hidden"]
      }],
      /**
       * Divide Width X
       * @see https://tailwindcss.com/docs/divide-width
       */
      "divide-x": [{
        "divide-x": [borderWidth]
      }],
      /**
       * Divide Width X Reverse
       * @see https://tailwindcss.com/docs/divide-width
       */
      "divide-x-reverse": ["divide-x-reverse"],
      /**
       * Divide Width Y
       * @see https://tailwindcss.com/docs/divide-width
       */
      "divide-y": [{
        "divide-y": [borderWidth]
      }],
      /**
       * Divide Width Y Reverse
       * @see https://tailwindcss.com/docs/divide-width
       */
      "divide-y-reverse": ["divide-y-reverse"],
      /**
       * Divide Opacity
       * @see https://tailwindcss.com/docs/divide-opacity
       */
      "divide-opacity": [{
        "divide-opacity": [opacity]
      }],
      /**
       * Divide Style
       * @see https://tailwindcss.com/docs/divide-style
       */
      "divide-style": [{
        divide: getLineStyles()
      }],
      /**
       * Border Color
       * @see https://tailwindcss.com/docs/border-color
       */
      "border-color": [{
        border: [borderColor]
      }],
      /**
       * Border Color X
       * @see https://tailwindcss.com/docs/border-color
       */
      "border-color-x": [{
        "border-x": [borderColor]
      }],
      /**
       * Border Color Y
       * @see https://tailwindcss.com/docs/border-color
       */
      "border-color-y": [{
        "border-y": [borderColor]
      }],
      /**
       * Border Color S
       * @see https://tailwindcss.com/docs/border-color
       */
      "border-color-s": [{
        "border-s": [borderColor]
      }],
      /**
       * Border Color E
       * @see https://tailwindcss.com/docs/border-color
       */
      "border-color-e": [{
        "border-e": [borderColor]
      }],
      /**
       * Border Color Top
       * @see https://tailwindcss.com/docs/border-color
       */
      "border-color-t": [{
        "border-t": [borderColor]
      }],
      /**
       * Border Color Right
       * @see https://tailwindcss.com/docs/border-color
       */
      "border-color-r": [{
        "border-r": [borderColor]
      }],
      /**
       * Border Color Bottom
       * @see https://tailwindcss.com/docs/border-color
       */
      "border-color-b": [{
        "border-b": [borderColor]
      }],
      /**
       * Border Color Left
       * @see https://tailwindcss.com/docs/border-color
       */
      "border-color-l": [{
        "border-l": [borderColor]
      }],
      /**
       * Divide Color
       * @see https://tailwindcss.com/docs/divide-color
       */
      "divide-color": [{
        divide: [borderColor]
      }],
      /**
       * Outline Style
       * @see https://tailwindcss.com/docs/outline-style
       */
      "outline-style": [{
        outline: ["", ...getLineStyles()]
      }],
      /**
       * Outline Offset
       * @see https://tailwindcss.com/docs/outline-offset
       */
      "outline-offset": [{
        "outline-offset": [isLength, isArbitraryValue]
      }],
      /**
       * Outline Width
       * @see https://tailwindcss.com/docs/outline-width
       */
      "outline-w": [{
        outline: [isLength, isArbitraryLength]
      }],
      /**
       * Outline Color
       * @see https://tailwindcss.com/docs/outline-color
       */
      "outline-color": [{
        outline: [colors]
      }],
      /**
       * Ring Width
       * @see https://tailwindcss.com/docs/ring-width
       */
      "ring-w": [{
        ring: getLengthWithEmptyAndArbitrary()
      }],
      /**
       * Ring Width Inset
       * @see https://tailwindcss.com/docs/ring-width
       */
      "ring-w-inset": ["ring-inset"],
      /**
       * Ring Color
       * @see https://tailwindcss.com/docs/ring-color
       */
      "ring-color": [{
        ring: [colors]
      }],
      /**
       * Ring Opacity
       * @see https://tailwindcss.com/docs/ring-opacity
       */
      "ring-opacity": [{
        "ring-opacity": [opacity]
      }],
      /**
       * Ring Offset Width
       * @see https://tailwindcss.com/docs/ring-offset-width
       */
      "ring-offset-w": [{
        "ring-offset": [isLength, isArbitraryLength]
      }],
      /**
       * Ring Offset Color
       * @see https://tailwindcss.com/docs/ring-offset-color
       */
      "ring-offset-color": [{
        "ring-offset": [colors]
      }],
      // Effects
      /**
       * Box Shadow
       * @see https://tailwindcss.com/docs/box-shadow
       */
      shadow: [{
        shadow: ["", "inner", "none", isTshirtSize, isArbitraryShadow]
      }],
      /**
       * Box Shadow Color
       * @see https://tailwindcss.com/docs/box-shadow-color
       */
      "shadow-color": [{
        shadow: [isAny]
      }],
      /**
       * Opacity
       * @see https://tailwindcss.com/docs/opacity
       */
      opacity: [{
        opacity: [opacity]
      }],
      /**
       * Mix Blend Mode
       * @see https://tailwindcss.com/docs/mix-blend-mode
       */
      "mix-blend": [{
        "mix-blend": [...getBlendModes(), "plus-lighter", "plus-darker"]
      }],
      /**
       * Background Blend Mode
       * @see https://tailwindcss.com/docs/background-blend-mode
       */
      "bg-blend": [{
        "bg-blend": getBlendModes()
      }],
      // Filters
      /**
       * Filter
       * @deprecated since Tailwind CSS v3.0.0
       * @see https://tailwindcss.com/docs/filter
       */
      filter: [{
        filter: ["", "none"]
      }],
      /**
       * Blur
       * @see https://tailwindcss.com/docs/blur
       */
      blur: [{
        blur: [blur]
      }],
      /**
       * Brightness
       * @see https://tailwindcss.com/docs/brightness
       */
      brightness: [{
        brightness: [brightness]
      }],
      /**
       * Contrast
       * @see https://tailwindcss.com/docs/contrast
       */
      contrast: [{
        contrast: [contrast]
      }],
      /**
       * Drop Shadow
       * @see https://tailwindcss.com/docs/drop-shadow
       */
      "drop-shadow": [{
        "drop-shadow": ["", "none", isTshirtSize, isArbitraryValue]
      }],
      /**
       * Grayscale
       * @see https://tailwindcss.com/docs/grayscale
       */
      grayscale: [{
        grayscale: [grayscale]
      }],
      /**
       * Hue Rotate
       * @see https://tailwindcss.com/docs/hue-rotate
       */
      "hue-rotate": [{
        "hue-rotate": [hueRotate]
      }],
      /**
       * Invert
       * @see https://tailwindcss.com/docs/invert
       */
      invert: [{
        invert: [invert]
      }],
      /**
       * Saturate
       * @see https://tailwindcss.com/docs/saturate
       */
      saturate: [{
        saturate: [saturate]
      }],
      /**
       * Sepia
       * @see https://tailwindcss.com/docs/sepia
       */
      sepia: [{
        sepia: [sepia]
      }],
      /**
       * Backdrop Filter
       * @deprecated since Tailwind CSS v3.0.0
       * @see https://tailwindcss.com/docs/backdrop-filter
       */
      "backdrop-filter": [{
        "backdrop-filter": ["", "none"]
      }],
      /**
       * Backdrop Blur
       * @see https://tailwindcss.com/docs/backdrop-blur
       */
      "backdrop-blur": [{
        "backdrop-blur": [blur]
      }],
      /**
       * Backdrop Brightness
       * @see https://tailwindcss.com/docs/backdrop-brightness
       */
      "backdrop-brightness": [{
        "backdrop-brightness": [brightness]
      }],
      /**
       * Backdrop Contrast
       * @see https://tailwindcss.com/docs/backdrop-contrast
       */
      "backdrop-contrast": [{
        "backdrop-contrast": [contrast]
      }],
      /**
       * Backdrop Grayscale
       * @see https://tailwindcss.com/docs/backdrop-grayscale
       */
      "backdrop-grayscale": [{
        "backdrop-grayscale": [grayscale]
      }],
      /**
       * Backdrop Hue Rotate
       * @see https://tailwindcss.com/docs/backdrop-hue-rotate
       */
      "backdrop-hue-rotate": [{
        "backdrop-hue-rotate": [hueRotate]
      }],
      /**
       * Backdrop Invert
       * @see https://tailwindcss.com/docs/backdrop-invert
       */
      "backdrop-invert": [{
        "backdrop-invert": [invert]
      }],
      /**
       * Backdrop Opacity
       * @see https://tailwindcss.com/docs/backdrop-opacity
       */
      "backdrop-opacity": [{
        "backdrop-opacity": [opacity]
      }],
      /**
       * Backdrop Saturate
       * @see https://tailwindcss.com/docs/backdrop-saturate
       */
      "backdrop-saturate": [{
        "backdrop-saturate": [saturate]
      }],
      /**
       * Backdrop Sepia
       * @see https://tailwindcss.com/docs/backdrop-sepia
       */
      "backdrop-sepia": [{
        "backdrop-sepia": [sepia]
      }],
      // Tables
      /**
       * Border Collapse
       * @see https://tailwindcss.com/docs/border-collapse
       */
      "border-collapse": [{
        border: ["collapse", "separate"]
      }],
      /**
       * Border Spacing
       * @see https://tailwindcss.com/docs/border-spacing
       */
      "border-spacing": [{
        "border-spacing": [borderSpacing]
      }],
      /**
       * Border Spacing X
       * @see https://tailwindcss.com/docs/border-spacing
       */
      "border-spacing-x": [{
        "border-spacing-x": [borderSpacing]
      }],
      /**
       * Border Spacing Y
       * @see https://tailwindcss.com/docs/border-spacing
       */
      "border-spacing-y": [{
        "border-spacing-y": [borderSpacing]
      }],
      /**
       * Table Layout
       * @see https://tailwindcss.com/docs/table-layout
       */
      "table-layout": [{
        table: ["auto", "fixed"]
      }],
      /**
       * Caption Side
       * @see https://tailwindcss.com/docs/caption-side
       */
      caption: [{
        caption: ["top", "bottom"]
      }],
      // Transitions and Animation
      /**
       * Tranisition Property
       * @see https://tailwindcss.com/docs/transition-property
       */
      transition: [{
        transition: ["none", "all", "", "colors", "opacity", "shadow", "transform", isArbitraryValue]
      }],
      /**
       * Transition Duration
       * @see https://tailwindcss.com/docs/transition-duration
       */
      duration: [{
        duration: getNumberAndArbitrary()
      }],
      /**
       * Transition Timing Function
       * @see https://tailwindcss.com/docs/transition-timing-function
       */
      ease: [{
        ease: ["linear", "in", "out", "in-out", isArbitraryValue]
      }],
      /**
       * Transition Delay
       * @see https://tailwindcss.com/docs/transition-delay
       */
      delay: [{
        delay: getNumberAndArbitrary()
      }],
      /**
       * Animation
       * @see https://tailwindcss.com/docs/animation
       */
      animate: [{
        animate: ["none", "spin", "ping", "pulse", "bounce", isArbitraryValue]
      }],
      // Transforms
      /**
       * Transform
       * @see https://tailwindcss.com/docs/transform
       */
      transform: [{
        transform: ["", "gpu", "none"]
      }],
      /**
       * Scale
       * @see https://tailwindcss.com/docs/scale
       */
      scale: [{
        scale: [scale]
      }],
      /**
       * Scale X
       * @see https://tailwindcss.com/docs/scale
       */
      "scale-x": [{
        "scale-x": [scale]
      }],
      /**
       * Scale Y
       * @see https://tailwindcss.com/docs/scale
       */
      "scale-y": [{
        "scale-y": [scale]
      }],
      /**
       * Rotate
       * @see https://tailwindcss.com/docs/rotate
       */
      rotate: [{
        rotate: [isInteger, isArbitraryValue]
      }],
      /**
       * Translate X
       * @see https://tailwindcss.com/docs/translate
       */
      "translate-x": [{
        "translate-x": [translate]
      }],
      /**
       * Translate Y
       * @see https://tailwindcss.com/docs/translate
       */
      "translate-y": [{
        "translate-y": [translate]
      }],
      /**
       * Skew X
       * @see https://tailwindcss.com/docs/skew
       */
      "skew-x": [{
        "skew-x": [skew]
      }],
      /**
       * Skew Y
       * @see https://tailwindcss.com/docs/skew
       */
      "skew-y": [{
        "skew-y": [skew]
      }],
      /**
       * Transform Origin
       * @see https://tailwindcss.com/docs/transform-origin
       */
      "transform-origin": [{
        origin: ["center", "top", "top-right", "right", "bottom-right", "bottom", "bottom-left", "left", "top-left", isArbitraryValue]
      }],
      // Interactivity
      /**
       * Accent Color
       * @see https://tailwindcss.com/docs/accent-color
       */
      accent: [{
        accent: ["auto", colors]
      }],
      /**
       * Appearance
       * @see https://tailwindcss.com/docs/appearance
       */
      appearance: [{
        appearance: ["none", "auto"]
      }],
      /**
       * Cursor
       * @see https://tailwindcss.com/docs/cursor
       */
      cursor: [{
        cursor: ["auto", "default", "pointer", "wait", "text", "move", "help", "not-allowed", "none", "context-menu", "progress", "cell", "crosshair", "vertical-text", "alias", "copy", "no-drop", "grab", "grabbing", "all-scroll", "col-resize", "row-resize", "n-resize", "e-resize", "s-resize", "w-resize", "ne-resize", "nw-resize", "se-resize", "sw-resize", "ew-resize", "ns-resize", "nesw-resize", "nwse-resize", "zoom-in", "zoom-out", isArbitraryValue]
      }],
      /**
       * Caret Color
       * @see https://tailwindcss.com/docs/just-in-time-mode#caret-color-utilities
       */
      "caret-color": [{
        caret: [colors]
      }],
      /**
       * Pointer Events
       * @see https://tailwindcss.com/docs/pointer-events
       */
      "pointer-events": [{
        "pointer-events": ["none", "auto"]
      }],
      /**
       * Resize
       * @see https://tailwindcss.com/docs/resize
       */
      resize: [{
        resize: ["none", "y", "x", ""]
      }],
      /**
       * Scroll Behavior
       * @see https://tailwindcss.com/docs/scroll-behavior
       */
      "scroll-behavior": [{
        scroll: ["auto", "smooth"]
      }],
      /**
       * Scroll Margin
       * @see https://tailwindcss.com/docs/scroll-margin
       */
      "scroll-m": [{
        "scroll-m": getSpacingWithArbitrary()
      }],
      /**
       * Scroll Margin X
       * @see https://tailwindcss.com/docs/scroll-margin
       */
      "scroll-mx": [{
        "scroll-mx": getSpacingWithArbitrary()
      }],
      /**
       * Scroll Margin Y
       * @see https://tailwindcss.com/docs/scroll-margin
       */
      "scroll-my": [{
        "scroll-my": getSpacingWithArbitrary()
      }],
      /**
       * Scroll Margin Start
       * @see https://tailwindcss.com/docs/scroll-margin
       */
      "scroll-ms": [{
        "scroll-ms": getSpacingWithArbitrary()
      }],
      /**
       * Scroll Margin End
       * @see https://tailwindcss.com/docs/scroll-margin
       */
      "scroll-me": [{
        "scroll-me": getSpacingWithArbitrary()
      }],
      /**
       * Scroll Margin Top
       * @see https://tailwindcss.com/docs/scroll-margin
       */
      "scroll-mt": [{
        "scroll-mt": getSpacingWithArbitrary()
      }],
      /**
       * Scroll Margin Right
       * @see https://tailwindcss.com/docs/scroll-margin
       */
      "scroll-mr": [{
        "scroll-mr": getSpacingWithArbitrary()
      }],
      /**
       * Scroll Margin Bottom
       * @see https://tailwindcss.com/docs/scroll-margin
       */
      "scroll-mb": [{
        "scroll-mb": getSpacingWithArbitrary()
      }],
      /**
       * Scroll Margin Left
       * @see https://tailwindcss.com/docs/scroll-margin
       */
      "scroll-ml": [{
        "scroll-ml": getSpacingWithArbitrary()
      }],
      /**
       * Scroll Padding
       * @see https://tailwindcss.com/docs/scroll-padding
       */
      "scroll-p": [{
        "scroll-p": getSpacingWithArbitrary()
      }],
      /**
       * Scroll Padding X
       * @see https://tailwindcss.com/docs/scroll-padding
       */
      "scroll-px": [{
        "scroll-px": getSpacingWithArbitrary()
      }],
      /**
       * Scroll Padding Y
       * @see https://tailwindcss.com/docs/scroll-padding
       */
      "scroll-py": [{
        "scroll-py": getSpacingWithArbitrary()
      }],
      /**
       * Scroll Padding Start
       * @see https://tailwindcss.com/docs/scroll-padding
       */
      "scroll-ps": [{
        "scroll-ps": getSpacingWithArbitrary()
      }],
      /**
       * Scroll Padding End
       * @see https://tailwindcss.com/docs/scroll-padding
       */
      "scroll-pe": [{
        "scroll-pe": getSpacingWithArbitrary()
      }],
      /**
       * Scroll Padding Top
       * @see https://tailwindcss.com/docs/scroll-padding
       */
      "scroll-pt": [{
        "scroll-pt": getSpacingWithArbitrary()
      }],
      /**
       * Scroll Padding Right
       * @see https://tailwindcss.com/docs/scroll-padding
       */
      "scroll-pr": [{
        "scroll-pr": getSpacingWithArbitrary()
      }],
      /**
       * Scroll Padding Bottom
       * @see https://tailwindcss.com/docs/scroll-padding
       */
      "scroll-pb": [{
        "scroll-pb": getSpacingWithArbitrary()
      }],
      /**
       * Scroll Padding Left
       * @see https://tailwindcss.com/docs/scroll-padding
       */
      "scroll-pl": [{
        "scroll-pl": getSpacingWithArbitrary()
      }],
      /**
       * Scroll Snap Align
       * @see https://tailwindcss.com/docs/scroll-snap-align
       */
      "snap-align": [{
        snap: ["start", "end", "center", "align-none"]
      }],
      /**
       * Scroll Snap Stop
       * @see https://tailwindcss.com/docs/scroll-snap-stop
       */
      "snap-stop": [{
        snap: ["normal", "always"]
      }],
      /**
       * Scroll Snap Type
       * @see https://tailwindcss.com/docs/scroll-snap-type
       */
      "snap-type": [{
        snap: ["none", "x", "y", "both"]
      }],
      /**
       * Scroll Snap Type Strictness
       * @see https://tailwindcss.com/docs/scroll-snap-type
       */
      "snap-strictness": [{
        snap: ["mandatory", "proximity"]
      }],
      /**
       * Touch Action
       * @see https://tailwindcss.com/docs/touch-action
       */
      touch: [{
        touch: ["auto", "none", "manipulation"]
      }],
      /**
       * Touch Action X
       * @see https://tailwindcss.com/docs/touch-action
       */
      "touch-x": [{
        "touch-pan": ["x", "left", "right"]
      }],
      /**
       * Touch Action Y
       * @see https://tailwindcss.com/docs/touch-action
       */
      "touch-y": [{
        "touch-pan": ["y", "up", "down"]
      }],
      /**
       * Touch Action Pinch Zoom
       * @see https://tailwindcss.com/docs/touch-action
       */
      "touch-pz": ["touch-pinch-zoom"],
      /**
       * User Select
       * @see https://tailwindcss.com/docs/user-select
       */
      select: [{
        select: ["none", "text", "all", "auto"]
      }],
      /**
       * Will Change
       * @see https://tailwindcss.com/docs/will-change
       */
      "will-change": [{
        "will-change": ["auto", "scroll", "contents", "transform", isArbitraryValue]
      }],
      // SVG
      /**
       * Fill
       * @see https://tailwindcss.com/docs/fill
       */
      fill: [{
        fill: [colors, "none"]
      }],
      /**
       * Stroke Width
       * @see https://tailwindcss.com/docs/stroke-width
       */
      "stroke-w": [{
        stroke: [isLength, isArbitraryLength, isArbitraryNumber]
      }],
      /**
       * Stroke
       * @see https://tailwindcss.com/docs/stroke
       */
      stroke: [{
        stroke: [colors, "none"]
      }],
      // Accessibility
      /**
       * Screen Readers
       * @see https://tailwindcss.com/docs/screen-readers
       */
      sr: ["sr-only", "not-sr-only"],
      /**
       * Forced Color Adjust
       * @see https://tailwindcss.com/docs/forced-color-adjust
       */
      "forced-color-adjust": [{
        "forced-color-adjust": ["auto", "none"]
      }]
    },
    conflictingClassGroups: {
      overflow: ["overflow-x", "overflow-y"],
      overscroll: ["overscroll-x", "overscroll-y"],
      inset: ["inset-x", "inset-y", "start", "end", "top", "right", "bottom", "left"],
      "inset-x": ["right", "left"],
      "inset-y": ["top", "bottom"],
      flex: ["basis", "grow", "shrink"],
      gap: ["gap-x", "gap-y"],
      p: ["px", "py", "ps", "pe", "pt", "pr", "pb", "pl"],
      px: ["pr", "pl"],
      py: ["pt", "pb"],
      m: ["mx", "my", "ms", "me", "mt", "mr", "mb", "ml"],
      mx: ["mr", "ml"],
      my: ["mt", "mb"],
      size: ["w", "h"],
      "font-size": ["leading"],
      "fvn-normal": ["fvn-ordinal", "fvn-slashed-zero", "fvn-figure", "fvn-spacing", "fvn-fraction"],
      "fvn-ordinal": ["fvn-normal"],
      "fvn-slashed-zero": ["fvn-normal"],
      "fvn-figure": ["fvn-normal"],
      "fvn-spacing": ["fvn-normal"],
      "fvn-fraction": ["fvn-normal"],
      "line-clamp": ["display", "overflow"],
      rounded: ["rounded-s", "rounded-e", "rounded-t", "rounded-r", "rounded-b", "rounded-l", "rounded-ss", "rounded-se", "rounded-ee", "rounded-es", "rounded-tl", "rounded-tr", "rounded-br", "rounded-bl"],
      "rounded-s": ["rounded-ss", "rounded-es"],
      "rounded-e": ["rounded-se", "rounded-ee"],
      "rounded-t": ["rounded-tl", "rounded-tr"],
      "rounded-r": ["rounded-tr", "rounded-br"],
      "rounded-b": ["rounded-br", "rounded-bl"],
      "rounded-l": ["rounded-tl", "rounded-bl"],
      "border-spacing": ["border-spacing-x", "border-spacing-y"],
      "border-w": ["border-w-s", "border-w-e", "border-w-t", "border-w-r", "border-w-b", "border-w-l"],
      "border-w-x": ["border-w-r", "border-w-l"],
      "border-w-y": ["border-w-t", "border-w-b"],
      "border-color": ["border-color-s", "border-color-e", "border-color-t", "border-color-r", "border-color-b", "border-color-l"],
      "border-color-x": ["border-color-r", "border-color-l"],
      "border-color-y": ["border-color-t", "border-color-b"],
      "scroll-m": ["scroll-mx", "scroll-my", "scroll-ms", "scroll-me", "scroll-mt", "scroll-mr", "scroll-mb", "scroll-ml"],
      "scroll-mx": ["scroll-mr", "scroll-ml"],
      "scroll-my": ["scroll-mt", "scroll-mb"],
      "scroll-p": ["scroll-px", "scroll-py", "scroll-ps", "scroll-pe", "scroll-pt", "scroll-pr", "scroll-pb", "scroll-pl"],
      "scroll-px": ["scroll-pr", "scroll-pl"],
      "scroll-py": ["scroll-pt", "scroll-pb"],
      touch: ["touch-x", "touch-y", "touch-pz"],
      "touch-x": ["touch"],
      "touch-y": ["touch"],
      "touch-pz": ["touch"]
    },
    conflictingClassGroupModifiers: {
      "font-size": ["leading"]
    }
  };
};
var twMerge = /* @__PURE__ */ createTailwindMerge(getDefaultConfig);

// src/web/utils/helpers.ts
var cn = (...inputs) => {
  return twMerge(clsx(inputs));
};
var isFirefox = typeof navigator !== "undefined" && navigator.userAgent.includes("Firefox");
var onIdle = (callback) => {
  if ("scheduler" in globalThis) {
    return globalThis.scheduler.postTask(callback, {
      priority: "background"
    });
  }
  if ("requestIdleCallback" in window) {
    return requestIdleCallback(callback);
  }
  return setTimeout(callback, 0);
};
var throttle = (callback, delay) => {
  let lastCall = 0;
  return (e) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      return callback(e);
    }
    return void 0;
  };
};
var tryOrElse = (fn, defaultValue) => {
  try {
    return fn();
  } catch {
    return defaultValue;
  }
};
var readLocalStorage = (storageKey) => {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};
var saveLocalStorage = (storageKey, state) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(state));
  } catch {
  }
};
var removeLocalStorage = (storageKey) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(storageKey);
  } catch {
  }
};

// src/web/utils/lru.ts
var LRUNode = class {
  constructor(key, value) {
    this.key = key;
    this.value = value;
  }
};
var LRUMap = class {
  constructor(limit) {
    this.limit = limit;
    this.nodes = /* @__PURE__ */ new Map();
  }
  has(key) {
    return this.nodes.has(key);
  }
  get(key) {
    const result = this.nodes.get(key);
    if (result) {
      this.bubble(result);
      return result.value;
    }
    return void 0;
  }
  set(key, value) {
    if (this.nodes.has(key)) {
      const result = this.nodes.get(key);
      if (result) {
        this.bubble(result);
      }
      return;
    }
    const node = new LRUNode(key, value);
    this.insertHead(node);
    if (this.nodes.size === this.limit && this.tail) {
      this.delete(this.tail.key);
    }
    this.nodes.set(key, node);
  }
  delete(key) {
    const result = this.nodes.get(key);
    if (result) {
      this.removeNode(result);
      this.nodes.delete(key);
    }
  }
  insertHead(node) {
    if (this.head) {
      node.next = this.head;
      this.head.prev = node;
    } else {
      this.tail = node;
      node.next = void 0;
    }
    node.prev = void 0;
    this.head = node;
  }
  removeNode(node) {
    if (node.prev) {
      node.prev.next = node.next;
    }
    if (node.next) {
      node.next.prev = node.prev;
    }
    if (node === this.tail) {
      this.tail = node.prev;
      if (this.tail) {
        this.tail.next = void 0;
      }
    }
  }
  insertBefore(node, newNode) {
    newNode.next = node;
    if (node.prev) {
      newNode.prev = node.prev;
      node.prev.next = newNode;
    } else {
      newNode.prev = void 0;
      this.head = newNode;
    }
    node.prev = newNode;
  }
  bubble(node) {
    if (node.prev) {
      this.removeNode(node);
      this.insertBefore(node.prev, node);
    }
  }
};

// src/core/worker/deferred.ts
function createDeferred() {
  let resolve;
  let reject;
  return {
    promise: new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    }),
    resolve(value) {
      resolve(value);
    },
    reject(value) {
      reject(value);
    }
  };
}

// src/core/worker/smol.ts
function setupWorker(setup) {
  const callback = setup();
  function success(id, data) {
    self.postMessage([id, true, data]);
  }
  function failure(id, data) {
    self.postMessage([id, false, data]);
  }
  self.addEventListener("message", (event) => {
    const [id, data] = event.data;
    try {
      Promise.resolve(callback(data)).then(
        (res) => success(id, res),
        (res) => failure(id, res)
      );
    } catch (error) {
      failure(id, error);
    }
  });
}
function createWorker(callback) {
  const template = `(${setupWorker.toString()})(${callback.toString()})`;
  const url = URL.createObjectURL(new Blob([template]));
  const worker = new Worker(url);
  return worker;
}
var SmolWorker = class {
  constructor(callback) {
    this.callback = callback;
    this.deferredMap = /* @__PURE__ */ new Map();
    this.count = 0;
    this.sync = false;
  }
  setupWorker(worker) {
    worker.addEventListener(
      "message",
      (event) => {
        const [id, flag, data] = event.data;
        const deferred = this.deferredMap.get(id);
        if (deferred) {
          if (flag) {
            deferred.resolve(data);
          } else {
            deferred.reject(data);
          }
          this.deferredMap.delete(id);
        }
      }
    );
  }
  async call(data, options) {
    if (this.sync) {
      if (!this.setup) {
        this.setup = this.callback();
      }
      return this.setup(data);
    }
    if (!this.worker) {
      this.worker = createWorker(this.callback);
      this.setupWorker(this.worker);
    }
    const deferred = createDeferred();
    const id = this.count++;
    this.deferredMap.set(id, deferred);
    this.worker.postMessage([id, data], {
      transfer: options?.transfer
    });
    return deferred.promise;
  }
  destroy() {
    this.deferredMap.clear();
    this.worker?.terminate();
  }
};

// src/core/worker/smol-extension.ts
var SmolWorkerExtension = class {
  constructor(callback) {
    this.callback = callback;
    this.sync = true;
  }
  async call(data, _options) {
    if (!this.setup) {
      this.setup = this.callback();
    }
    return this.setup(data);
  }
  destroy() {
  }
};

// src/web/utils/outline-worker.ts
function setupOutlineWorker() {
  const MONO_FONT = "Menlo,Consolas,Monaco,Liberation Mono,Lucida Console,monospace";
  let ctx;
  let Reason;
  ((Reason2) => {
    Reason2[Reason2["Commit"] = 1] = "Commit";
    Reason2[Reason2["Unstable"] = 2] = "Unstable";
    Reason2[Reason2["Unnecessary"] = 4] = "Unnecessary";
  })(Reason || (Reason = {}));
  return async (action) => {
    switch (action.type) {
      case "set-canvas":
        {
          const current = action.payload.getContext("2d");
          if (current) {
            ctx = current;
          }
        }
        break;
      case "resize":
        if (ctx) {
          const { dpi, width, height } = action.payload;
          ctx.canvas.width = width;
          ctx.canvas.height = height;
          ctx.resetTransform();
          ctx.scale(dpi, dpi);
        }
        break;
      case "fade-out-outline":
        if (ctx) {
          const { dpi, drawingQueue, mergedLabels } = action.payload;
          ctx.clearRect(0, 0, ctx.canvas.width / dpi, ctx.canvas.height / dpi);
          ctx.save();
          for (let i = 0, len = drawingQueue.length; i < len; i++) {
            const { rect, color, alpha, fillAlpha } = drawingQueue[i];
            const rgb = `${color.r},${color.g},${color.b}`;
            ctx.strokeStyle = `rgba(${rgb},${alpha})`;
            ctx.lineWidth = 1;
            ctx.fillStyle = `rgba(${rgb},${fillAlpha})`;
            ctx.beginPath();
            ctx.rect(rect.x, rect.y, rect.width, rect.height);
            ctx.stroke();
            ctx.fill();
          }
          ctx.restore();
          for (let i = 0, len = mergedLabels.length; i < len; i++) {
            const { alpha, rect, color, reasons, labelText } = mergedLabels[i];
            const conditionalText = reasons & 4 /* Unnecessary */ ? `${labelText}\u26A0\uFE0F` : labelText;
            ctx.save();
            ctx.font = `11px ${MONO_FONT}`;
            const textMetrics = ctx.measureText(conditionalText);
            const textWidth = textMetrics.width;
            const textHeight = 11;
            const labelX = rect.x;
            const labelY = rect.y - textHeight - 4;
            ctx.fillStyle = `rgba(${color.r},${color.g},${color.b},${alpha})`;
            ctx.fillRect(labelX, labelY, textWidth + 4, textHeight + 4);
            ctx.fillStyle = `rgba(255,255,255,${alpha})`;
            ctx.fillText(conditionalText, labelX + 2, labelY + textHeight);
          }
        }
        break;
    }
  };
}
var createWorker2 = () => {
  const useExtensionWorker = readLocalStorage("use-extension-worker");
  removeLocalStorage("use-extension-worker");
  if (useExtensionWorker) {
    return new SmolWorkerExtension(setupOutlineWorker);
  }
  return new SmolWorker(setupOutlineWorker);
};
var outlineWorker = createWorker2();

// src/web/utils/outline.ts
var DEFAULT_THROTTLE_TIME = 32;
function incrementFrameId() {
  requestAnimationFrame(incrementFrameId);
}
if (typeof window !== "undefined") {
  incrementFrameId();
}
var recalcOutlines = throttle(async () => {
  const { activeOutlines } = ReactScanInternals;
  const domNodes = [];
  for (const activeOutline of activeOutlines.values()) {
    domNodes.push(activeOutline.domNode);
  }
  const rectMap = await batchGetBoundingRects(domNodes);
  for (const activeOutline of activeOutlines.values()) {
    const rect = rectMap.get(activeOutline.domNode);
    if (!rect) {
      continue;
    }
    activeOutline.target = rect;
  }
}, DEFAULT_THROTTLE_TIME);
var batchGetBoundingRects = (elements) => {
  return new Promise((resolve) => {
    const results = /* @__PURE__ */ new Map();
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const element = entry.target;
        const bounds = entry.boundingClientRect;
        results.set(element, bounds);
      }
      observer.disconnect();
      resolve(results);
    });
    for (const element of elements) {
      observer.observe(element);
    }
  });
};
var RENDER_PHASE_STRING_TO_ENUM = {
  mount: 1 /* Mount */,
  update: 2 /* Update */,
  unmount: 4 /* Unmount */
};
var textMeasurementCache = new LRUMap(100);

// src/web/components/inspector/index.tsx
import { Component } from "preact";
import { useEffect as useEffect6, useRef as useRef7 } from "preact/hooks";

// src/web/state.ts
import { signal } from "@preact/signals";

// src/web/constants.ts
var SAFE_AREA = 24;
var MIN_SIZE = {
  width: 480,
  height: 36,
  initialHeight: 36 * 10
};
var MIN_CONTAINER_WIDTH = 240;
var LOCALSTORAGE_KEY = "react-scan-widget-settings";

// src/web/state.ts
var signalIsSettingsOpen = signal(false);
var signalRefWidget = signal(null);
var defaultWidgetConfig = {
  corner: "top-left",
  dimensions: {
    isFullWidth: false,
    isFullHeight: false,
    width: MIN_SIZE.width,
    height: MIN_SIZE.height,
    position: { x: SAFE_AREA, y: SAFE_AREA }
  },
  lastDimensions: {
    isFullWidth: false,
    isFullHeight: false,
    width: MIN_SIZE.width,
    height: MIN_SIZE.height,
    position: { x: SAFE_AREA, y: SAFE_AREA }
  },
  componentsTree: {
    width: MIN_CONTAINER_WIDTH
  }
};
var getInitialWidgetConfig = () => {
  const stored = readLocalStorage(LOCALSTORAGE_KEY);
  if (!stored) {
    saveLocalStorage(LOCALSTORAGE_KEY, {
      corner: defaultWidgetConfig.corner,
      dimensions: defaultWidgetConfig.dimensions,
      lastDimensions: defaultWidgetConfig.lastDimensions,
      componentsTree: defaultWidgetConfig.componentsTree
    });
    return defaultWidgetConfig;
  }
  return {
    corner: stored.corner ?? defaultWidgetConfig.corner,
    dimensions: {
      isFullWidth: false,
      isFullHeight: false,
      width: MIN_SIZE.width,
      height: MIN_SIZE.height,
      position: stored.dimensions.position ?? defaultWidgetConfig.dimensions.position
    },
    lastDimensions: stored.dimensions ?? defaultWidgetConfig.dimensions,
    componentsTree: stored.componentsTree ?? defaultWidgetConfig.componentsTree
  };
};
var signalWidget = signal(getInitialWidgetConfig());

// src/web/utils/preact/constant.ts
import {
  createElement
} from "preact";
function CONSTANT_UPDATE() {
  return false;
}
function constant(Component3) {
  function Memoed(props) {
    this.shouldComponentUpdate = CONSTANT_UPDATE;
    return createElement(Component3, props);
  }
  Memoed.displayName = `Memo(${Component3.displayName || Component3.name})`;
  Memoed.prototype.isReactComponent = true;
  Memoed._forwarded = true;
  return Memoed;
}

// src/web/components/icon/index.tsx
import { forwardRef } from "preact/compat";
import { jsx, jsxs } from "preact/jsx-runtime";
var Icon = forwardRef(({
  size = 15,
  name,
  fill = "currentColor",
  stroke = "currentColor",
  className,
  externalURL = "",
  style
}, ref) => {
  const width = Array.isArray(size) ? size[0] : size;
  const height = Array.isArray(size) ? size[1] || size[0] : size;
  const path = `${externalURL}#${name}`;
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      ref,
      width: `${width}px`,
      height: `${height}px`,
      fill,
      stroke,
      className,
      style: {
        ...style,
        minWidth: `${width}px`,
        maxWidth: `${width}px`,
        minHeight: `${height}px`,
        maxHeight: `${height}px`
      },
      children: [
        /* @__PURE__ */ jsx("title", { children: name }),
        /* @__PURE__ */ jsx("use", { href: path })
      ]
    }
  );
});

// src/web/components/sticky-section/index.tsx
import { memo } from "preact/compat";
import { useCallback, useRef, useState } from "preact/hooks";
var StickySection = memo(({ children }) => {
  const refScrollableElement = useRef(null);
  const refElement = useRef(null);
  const refScrollAtTop = useRef(false);
  const [isSticky, setIsSticky] = useState(false);
  const refRafId = useRef(0);
  const calculateStickyTop = useCallback((removeSticky = false) => {
    const stickyElements = Array.from(
      refScrollableElement.current?.children || []
    );
    if (!stickyElements.length) return;
    let cumulativeHeight = 0;
    for (const element of stickyElements) {
      const sticky = element;
      if (sticky.dataset.sticky) {
        if (removeSticky) {
          sticky.style.removeProperty("top");
        } else {
          sticky.style.setProperty("top", `${cumulativeHeight}px`);
        }
        cumulativeHeight += sticky.offsetHeight;
      }
    }
  }, []);
  const refSticky = useCallback(
    (node) => {
      if (!node) {
        requestAnimationFrame(() => {
          calculateStickyTop();
        });
        return;
      }
      refElement.current = node;
      refScrollableElement.current = node.parentElement;
      node.dataset.sticky = "true";
      const handleClick = () => {
        if (!node.dataset.disableScroll) {
          refScrollableElement.current?.scrollTo({
            top: Number(node.style.top) ?? 0,
            behavior: "smooth"
          });
        }
      };
      node.onclick = handleClick;
      calculateStickyTop();
      const handleScroll = () => {
        cancelAnimationFrame(refRafId.current);
        refRafId.current = requestAnimationFrame(() => {
          if (!node || !refScrollableElement.current) return;
          const refRect = node.getBoundingClientRect();
          const containerRect = refScrollableElement.current.getBoundingClientRect();
          const stickyOffset = Number.parseInt(getComputedStyle(node).top);
          refScrollAtTop.current = refScrollableElement.current.scrollTop > 0;
          const stickyActive = refScrollAtTop.current && refRect.top <= containerRect.top + stickyOffset;
          if (stickyActive !== isSticky) {
            setIsSticky(stickyActive);
          }
          calculateStickyTop();
        });
      };
      refScrollableElement.current?.addEventListener("scroll", handleScroll, {
        passive: true
      });
    },
    [isSticky, calculateStickyTop]
  );
  return children({
    refSticky,
    isSticky,
    calculateStickyTop
  });
});

// src/web/components/inspector/flash-overlay.ts
var fadeOutTimers = /* @__PURE__ */ new WeakMap();
var trackElementPosition = (element, callback) => {
  const handleScroll = callback.bind(null, element);
  document.addEventListener("scroll", handleScroll, {
    passive: true,
    capture: true
  });
  return () => {
    document.removeEventListener("scroll", handleScroll, { capture: true });
  };
};
var flashManager = {
  activeFlashes: /* @__PURE__ */ new Map(),
  create(container) {
    const existingOverlay = container.querySelector(
      ".react-scan-flash-overlay"
    );
    const overlay = existingOverlay instanceof HTMLElement ? existingOverlay : (() => {
      const newOverlay = document.createElement("div");
      newOverlay.className = "react-scan-flash-overlay";
      container.appendChild(newOverlay);
      const scrollCleanup = trackElementPosition(container, () => {
        if (container.querySelector(".react-scan-flash-overlay")) {
          this.create(container);
        }
      });
      this.activeFlashes.set(container, {
        element: container,
        overlay: newOverlay,
        scrollCleanup
      });
      return newOverlay;
    })();
    const existingTimer = fadeOutTimers.get(overlay);
    if (existingTimer) {
      clearTimeout(existingTimer);
      fadeOutTimers.delete(overlay);
    }
    requestAnimationFrame(() => {
      overlay.style.transition = "none";
      overlay.style.opacity = "0.9";
      const timerId = setTimeout(() => {
        overlay.style.transition = "opacity 150ms ease-out";
        overlay.style.opacity = "0";
        const cleanupTimer = setTimeout(() => {
          if (overlay.parentNode) {
            overlay.parentNode.removeChild(overlay);
          }
          const entry = this.activeFlashes.get(container);
          if (entry?.scrollCleanup) {
            entry.scrollCleanup();
          }
          this.activeFlashes.delete(container);
          fadeOutTimers.delete(overlay);
        }, 150);
        fadeOutTimers.set(overlay, cleanupTimer);
      }, 300);
      fadeOutTimers.set(overlay, timerId);
    });
  },
  cleanup(container) {
    const entry = this.activeFlashes.get(container);
    if (entry) {
      const existingTimer = fadeOutTimers.get(entry.overlay);
      if (existingTimer) {
        clearTimeout(existingTimer);
        fadeOutTimers.delete(entry.overlay);
      }
      if (entry.overlay.parentNode) {
        entry.overlay.parentNode.removeChild(entry.overlay);
      }
      if (entry.scrollCleanup) {
        entry.scrollCleanup();
      }
      this.activeFlashes.delete(container);
    }
  },
  cleanupAll() {
    for (const [, entry] of this.activeFlashes) {
      this.cleanup(entry.element);
    }
  }
};

// src/web/components/inspector/properties.tsx
import { getDisplayName as getDisplayName2 } from "bippy";
import {
  useCallback as useCallback4,
  useEffect as useEffect2,
  useMemo,
  useRef as useRef3,
  useState as useState3
} from "preact/hooks";

// src/web/components/copy-to-clipboard/index.tsx
import { memo as memo2 } from "preact/compat";
import { useCallback as useCallback2, useEffect, useRef as useRef2, useState as useState2 } from "preact/hooks";
import { jsx as jsx2 } from "preact/jsx-runtime";
var CopyToClipboard = memo2(
  ({
    text,
    children,
    onCopy,
    className,
    iconSize = 14
  }) => {
    const refTimeout = useRef2();
    const [isCopied, setIsCopied] = useState2(false);
    useEffect(() => {
      if (isCopied) {
        refTimeout.current = setTimeout(() => setIsCopied(false), 600);
        return () => {
          clearTimeout(refTimeout.current);
        };
      }
    }, [isCopied]);
    const copyToClipboard = useCallback2(
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard.writeText(text).then(
          () => {
            setIsCopied(true);
            onCopy?.(true, text);
          },
          () => {
            onCopy?.(false, text);
          }
        );
      },
      [text, onCopy]
    );
    const ClipboardIcon = /* @__PURE__ */ jsx2(
      "button",
      {
        onClick: copyToClipboard,
        type: "button",
        className: cn(
          "z-10",
          "flex items-center justify-center",
          "hover:text-dev-pink-400",
          "transition-colors duration-200 ease-in-out",
          "cursor-pointer",
          `size-[${iconSize}px]`,
          className
        ),
        children: /* @__PURE__ */ jsx2(
          Icon,
          {
            name: `icon-${isCopied ? "check" : "copy"}`,
            size: [iconSize],
            className: cn({
              "text-green-500": isCopied
            })
          }
        )
      }
    );
    if (!children) {
      return ClipboardIcon;
    }
    return children({
      ClipboardIcon,
      onClick: copyToClipboard
    });
  }
);

// src/web/hooks/use-merged-refs.ts
import { useCallback as useCallback3 } from "preact/compat";
var assignRef = (ref, value) => {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref !== null) {
    ref.current = value;
  }
};
var mergeRefs = (...refs) => {
  return (node) => {
    for (const ref of refs) {
      if (ref) {
        assignRef(ref, node);
      }
    }
  };
};
var useMergedRefs = (...refs) => {
  return useCallback3(mergeRefs(...refs), [...refs]);
};

// src/web/components/inspector/states.ts
import { signal as signal2 } from "@preact/signals";
var TIMELINE_MAX_UPDATES = 1e3;
var timelineStateDefault = {
  updates: [],
  currentFiber: null,
  totalUpdates: 0,
  windowOffset: 0,
  currentIndex: 0,
  isViewingHistory: false,
  latestFiber: null,
  isVisible: false,
  playbackSpeed: 1
};
var timelineState = signal2(timelineStateDefault);
var inspectorUpdateSignal = signal2(0);
var pendingUpdates = [];
var batchTimeout = null;
var batchUpdates = () => {
  if (pendingUpdates.length === 0) return;
  const batchedUpdates = [...pendingUpdates];
  const { updates, totalUpdates, currentIndex, isViewingHistory } = timelineState.value;
  const newUpdates = [...updates];
  let newTotalUpdates = totalUpdates;
  for (const { update } of batchedUpdates) {
    if (newUpdates.length >= TIMELINE_MAX_UPDATES) {
      newUpdates.shift();
    }
    newUpdates.push(update);
    newTotalUpdates++;
  }
  const newWindowOffset = Math.max(0, newTotalUpdates - TIMELINE_MAX_UPDATES);
  let newCurrentIndex;
  if (isViewingHistory) {
    if (currentIndex === totalUpdates - 1) {
      newCurrentIndex = newUpdates.length - 1;
    } else if (currentIndex === 0) {
      newCurrentIndex = 0;
    } else {
      if (newWindowOffset === 0) {
        newCurrentIndex = currentIndex;
      } else {
        newCurrentIndex = currentIndex - 1;
      }
    }
  } else {
    newCurrentIndex = newUpdates.length - 1;
  }
  const lastUpdate = batchedUpdates[batchedUpdates.length - 1];
  timelineState.value = {
    ...timelineState.value,
    latestFiber: lastUpdate.fiber,
    updates: newUpdates,
    totalUpdates: newTotalUpdates,
    windowOffset: newWindowOffset,
    currentIndex: newCurrentIndex,
    isViewingHistory
  };
  pendingUpdates = pendingUpdates.slice(batchedUpdates.length);
};
var timelineActions = {
  showTimeline: () => {
    timelineState.value = {
      ...timelineState.value,
      isVisible: true
    };
  },
  hideTimeline: () => {
    timelineState.value = {
      ...timelineState.value,
      isVisible: false,
      currentIndex: timelineState.value.updates.length - 1
    };
  },
  updateFrame: (index, isViewingHistory) => {
    timelineState.value = {
      ...timelineState.value,
      currentIndex: index,
      isViewingHistory
    };
  },
  updatePlaybackSpeed: (speed) => {
    timelineState.value = {
      ...timelineState.value,
      playbackSpeed: speed
    };
  },
  addUpdate: (update, latestFiber) => {
    pendingUpdates.push({ update, fiber: latestFiber });
    if (!batchTimeout) {
      const processBatch = () => {
        batchUpdates();
        batchTimeout = null;
        if (pendingUpdates.length > 0) {
          batchTimeout = setTimeout(processBatch, 96);
        }
      };
      batchTimeout = setTimeout(processBatch, 96);
    }
  },
  reset: () => {
    if (batchTimeout) {
      clearTimeout(batchTimeout);
      batchTimeout = null;
    }
    pendingUpdates = [];
    timelineState.value = timelineStateDefault;
  }
};

// src/web/components/inspector/properties.tsx
import { Fragment, jsx as jsx3, jsxs as jsxs2 } from "preact/jsx-runtime";
var EditableValue = ({
  value,
  onSave,
  onCancel
}) => {
  const refInput = useRef3(null);
  const [editValue, setEditValue] = useState3("");
  useEffect2(() => {
    let initialValue = "";
    try {
      if (value instanceof Date) {
        initialValue = value.toISOString().slice(0, 16);
      } else if (value instanceof Map || value instanceof Set || value instanceof RegExp || value instanceof Error || value instanceof ArrayBuffer || ArrayBuffer.isView(value) || typeof value === "object" && value !== null) {
        initialValue = formatValue(value);
      } else {
        initialValue = formatInitialValue(value);
      }
    } catch {
      initialValue = String(value);
    }
    const sanitizedValue = sanitizeString(initialValue);
    setEditValue(sanitizedValue);
    requestAnimationFrame(() => {
      if (!refInput.current) return;
      refInput.current.focus();
      if (typeof value === "string") {
        refInput.current.setSelectionRange(1, sanitizedValue.length - 1);
      } else {
        refInput.current.select();
      }
    });
  }, [value]);
  const handleChange = useCallback4((e) => {
    const target = e.target;
    if (target) {
      setEditValue(target.value);
    }
  }, []);
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      try {
        let newValue;
        if (value instanceof Date) {
          const date = new Date(editValue);
          if (Number.isNaN(date.getTime())) {
            throw new Error("Invalid date");
          }
          newValue = date;
        } else {
          const detected = detectValueType(editValue);
          newValue = detected.value;
        }
        onSave(newValue);
      } catch {
        onCancel();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      onCancel();
    }
  };
  return /* @__PURE__ */ jsx3(
    "input",
    {
      ref: refInput,
      type: value instanceof Date ? "datetime-local" : "text",
      className: "react-scan-input flex-1",
      value: editValue,
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      onBlur: onCancel,
      step: value instanceof Date ? 1 : void 0
    }
  );
};
var PropertyElement = ({
  name,
  value,
  section,
  level,
  parentPath,
  objectPathMap = /* @__PURE__ */ new WeakMap(),
  changedKeys = /* @__PURE__ */ new Set(),
  allowEditing = true
}) => {
  const { updates, currentIndex } = timelineState.value;
  const currentUpdate = updates[currentIndex];
  const fiberInfo = currentUpdate?.fiberInfo;
  const refElement = useRef3(null);
  const currentPath = getPath(
    fiberInfo.displayName,
    section,
    parentPath ?? "",
    name
  );
  const [isExpanded, setIsExpanded] = useState3(
    globalInspectorState.expandedPaths.has(currentPath)
  );
  const [isEditing, setIsEditing] = useState3(false);
  const prevValue = globalInspectorState.lastRendered.get(currentPath);
  const isChanged = !isEqual(prevValue, value);
  useEffect2(() => {
    if (name === "children") {
      return;
    }
    if (section === "context") {
      return;
    }
    const isFirstRender = !globalInspectorState.lastRendered.has(currentPath);
    const shouldFlash = isChanged && refElement.current && !isFirstRender;
    globalInspectorState.lastRendered.set(currentPath, value);
    if (shouldFlash && refElement.current && level === 0) {
      flashManager.create(refElement.current);
    }
  }, [value, isChanged, currentPath, level, name, section]);
  const handleToggleExpand = useCallback4(() => {
    setIsExpanded((prevState) => {
      const newIsExpanded = !prevState;
      if (newIsExpanded) {
        globalInspectorState.expandedPaths.add(currentPath);
      } else {
        globalInspectorState.expandedPaths.delete(currentPath);
      }
      return newIsExpanded;
    });
  }, [currentPath]);
  const valuePreview = useMemo(() => {
    if (typeof value === "object" && value !== null) {
      if ("displayValue" in value) {
        return String(value.displayValue);
      }
    }
    return formatValue(value);
  }, [value]);
  const clipboardText = useMemo(() => {
    if (typeof value === "object" && value !== null) {
      if ("value" in value) {
        return String(formatForClipboard(value.value));
      }
      if ("displayValue" in value) {
        return String(value.displayValue);
      }
    }
    return String(formatForClipboard(value));
  }, [value]);
  const isExpandableValue = useMemo(() => {
    if (!value || typeof value !== "object") return false;
    if ("type" in value) {
      const metadata2 = value;
      switch (metadata2.type) {
        case "array":
        case "Map":
        case "Set":
          return (metadata2.size ?? metadata2.length ?? 0) > 0;
        case "object":
          return (metadata2.size ?? 0) > 0;
        case "ArrayBuffer":
        case "DataView":
          return (metadata2.byteLength ?? 0) > 0;
        case "circular":
        case "promise":
        case "function":
        case "error":
          return false;
        default:
          if ("entries" in metadata2 || "items" in metadata2) {
            return true;
          }
          return false;
      }
    }
    return isExpandable(value);
  }, [value]);
  const { overrideProps, overrideHookState } = getOverrideMethods();
  const canEdit = useMemo(() => {
    if (!allowEditing) return false;
    if (section === "props") return !!overrideProps && name !== "children";
    if (section === "state") return !!overrideHookState;
    return false;
  }, [section, overrideProps, overrideHookState, allowEditing, name]);
  const handleEdit = useCallback4(() => {
    if (canEdit) {
      setIsEditing(true);
    }
  }, [canEdit]);
  const handleSave = (section2, name2, value2) => {
    const { updates: updates2, currentIndex: currentIndex2, latestFiber } = timelineState.value;
    const currentUpdate2 = updates2[currentIndex2];
    if (!latestFiber) return;
    const { overrideProps: overrideProps2, overrideHookState: overrideHookState2 } = getOverrideMethods();
    if (!overrideProps2 || !overrideHookState2) return;
    if (section2 === "props") {
      tryOrElse(() => {
        const currentProps = latestFiber.memoizedProps || {};
        let currentValue;
        let path;
        if (parentPath) {
          const parts = parentPath.split(".");
          path = parts.filter(
            (part) => part !== "props" && part !== getDisplayName2(latestFiber.type)
          );
          path.push(name2);
          currentValue = path.reduce(
            (obj, key) => obj && typeof obj === "object" ? obj[key] : {},
            currentProps
          );
        } else {
          path = [name2];
          currentValue = currentProps[name2];
        }
        if (!isEqual(currentValue, value2)) {
          overrideProps2(latestFiber, path, value2);
          if (latestFiber.alternate) {
            overrideProps2(latestFiber.alternate, path, value2);
          }
        }
      }, null);
    } else if (section2 === "state") {
      tryOrElse(() => {
        if (!parentPath) {
          const stateNames = currentUpdate2.stateNames;
          const namedStateIndex = stateNames.indexOf(name2);
          const hookId = namedStateIndex !== -1 ? namedStateIndex.toString() : name2;
          overrideHookState2(latestFiber, hookId, [], value2);
        } else {
          const fullPathParts = parentPath.split(".");
          const stateIndex = fullPathParts.indexOf("state");
          if (stateIndex === -1) return;
          const statePath = fullPathParts.slice(stateIndex + 1);
          const baseStateKey = statePath[0];
          const stateNames = currentUpdate2.stateNames;
          const namedStateIndex = stateNames.indexOf(baseStateKey);
          const hookId = namedStateIndex !== -1 ? namedStateIndex.toString() : "0";
          const currentState = currentUpdate2.state.current;
          if (!currentState || !currentState.find((item) => item.name === Number(baseStateKey))) {
            return;
          }
          const updatedState = updateNestedValue(
            currentState.find((item) => item.name === Number(baseStateKey))?.value,
            statePath.slice(1).concat(name2),
            value2
          );
          overrideHookState2(latestFiber, hookId, [], updatedState);
        }
      }, null);
    }
    setIsEditing(false);
  };
  const checkCircularInValue = useMemo(() => {
    if (!value || typeof value !== "object" || isPromise(value)) return false;
    return "type" in value && value.type === "circular";
  }, [value]);
  const renderNestedProperties = useCallback4(
    (obj) => {
      if (!obj || typeof obj !== "object") return null;
      if ("type" in obj) {
        const metadata2 = obj;
        if ("entries" in metadata2 && metadata2.entries) {
          const entries2 = Object.entries(metadata2.entries);
          if (entries2.length === 0) return null;
          return /* @__PURE__ */ jsx3("div", { className: "react-scan-nested", children: entries2.map(([key, val]) => /* @__PURE__ */ jsx3(
            PropertyElement,
            {
              name: key,
              value: val,
              section,
              level: level + 1,
              parentPath: currentPath,
              objectPathMap,
              changedKeys,
              allowEditing
            },
            `${currentPath}-entry-${key}`
          )) });
        }
        if ("items" in metadata2 && Array.isArray(metadata2.items)) {
          if (metadata2.items.length === 0) return null;
          return /* @__PURE__ */ jsx3("div", { className: "react-scan-nested", children: metadata2.items.map((item, i) => {
            const itemKey = `${currentPath}-item-${item.type}-${i}`;
            return /* @__PURE__ */ jsx3(
              PropertyElement,
              {
                name: `${i}`,
                value: item,
                section,
                level: level + 1,
                parentPath: currentPath,
                objectPathMap,
                changedKeys,
                allowEditing
              },
              itemKey
            );
          }) });
        }
        return null;
      }
      let entries;
      if (obj instanceof ArrayBuffer) {
        const view = new Uint8Array(obj);
        entries = Array.from(view).map((v, i) => [i, v]);
      } else if (obj instanceof DataView) {
        const view = new Uint8Array(obj.buffer, obj.byteOffset, obj.byteLength);
        entries = Array.from(view).map((v, i) => [i, v]);
      } else if (ArrayBuffer.isView(obj)) {
        if (obj instanceof BigInt64Array || obj instanceof BigUint64Array) {
          entries = Array.from({ length: obj.length }, (_, i) => [i, obj[i]]);
        } else {
          const typedArray = obj;
          entries = Array.from(typedArray).map((v, i) => [i, v]);
        }
      } else if (obj instanceof Map) {
        entries = Array.from(obj.entries()).map(([k, v]) => [String(k), v]);
      } else if (obj instanceof Set) {
        entries = Array.from(obj).map((v, i) => [i, v]);
      } else if (Array.isArray(obj)) {
        entries = obj.map((value2, index) => [`${index}`, value2]);
      } else {
        entries = Object.entries(obj);
      }
      if (entries.length === 0) return null;
      const canEditChildren = !(obj instanceof DataView || obj instanceof ArrayBuffer || ArrayBuffer.isView(obj));
      return /* @__PURE__ */ jsx3("div", { className: "react-scan-nested", children: entries.map(([key, val]) => {
        const itemKey = `${currentPath}-${typeof key === "number" ? `item-${key}` : key}`;
        return /* @__PURE__ */ jsx3(
          PropertyElement,
          {
            name: String(key),
            value: val,
            section,
            level: level + 1,
            parentPath: currentPath,
            objectPathMap,
            changedKeys,
            allowEditing: canEditChildren
          },
          itemKey
        );
      }) });
    },
    [section, level, currentPath, objectPathMap, changedKeys, allowEditing]
  );
  if (checkCircularInValue) {
    return /* @__PURE__ */ jsx3("div", { className: "react-scan-property", children: /* @__PURE__ */ jsx3("div", { className: "react-scan-property-content", children: /* @__PURE__ */ jsxs2("div", { className: "react-scan-preview-line", children: [
      /* @__PURE__ */ jsxs2("div", { className: "react-scan-key", children: [
        name,
        ":"
      ] }),
      /* @__PURE__ */ jsx3("span", { className: "text-yellow-500", children: "[Circular Reference]" })
    ] }) }) });
  }
  return /* @__PURE__ */ jsx3("div", { ref: refElement, className: "react-scan-property", children: /* @__PURE__ */ jsxs2("div", { className: "react-scan-property-content", children: [
    isExpandableValue && /* @__PURE__ */ jsx3(
      "button",
      {
        type: "button",
        onClick: handleToggleExpand,
        className: "react-scan-arrow",
        children: /* @__PURE__ */ jsx3(
          Icon,
          {
            name: "icon-chevron-right",
            size: 12,
            className: cn({
              "rotate-90": isExpanded
            })
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs2(
      "div",
      {
        className: cn("group", "react-scan-preview-line", {
          "react-scan-highlight": isChanged
        }),
        children: [
          /* @__PURE__ */ jsxs2("div", { className: "react-scan-key", children: [
            name,
            ":"
          ] }),
          isEditing && isEditableValue(value, parentPath) ? /* @__PURE__ */ jsx3(
            EditableValue,
            {
              value,
              onSave: (newValue) => handleSave(section, name, newValue),
              onCancel: () => setIsEditing(false)
            }
          ) : /* @__PURE__ */ jsx3("button", { type: "button", className: "truncate", onClick: handleEdit, children: valuePreview }),
          /* @__PURE__ */ jsx3(
            CopyToClipboard,
            {
              text: clipboardText,
              className: "opacity-0 transition-opacity group-hover:opacity-100",
              children: ({ ClipboardIcon }) => /* @__PURE__ */ jsx3(Fragment, { children: ClipboardIcon })
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx3(
      "div",
      {
        className: cn("react-scan-expandable", {
          "react-scan-expanded": isExpanded
        }),
        children: isExpandableValue && isExpanded && /* @__PURE__ */ jsx3("div", { className: "react-scan-nested", children: renderNestedProperties(value) })
      }
    )
  ] }) });
};
var PropertySection = ({
  refSticky,
  isSticky,
  section
}) => {
  const refStickyElement = useRef3(null);
  const refSection = useRef3(null);
  const { updates, currentIndex } = timelineState.value;
  const [isExpanded, setIsExpanded] = useState3(true);
  const refs = useMergedRefs(refStickyElement, refSticky);
  const pathMap = useMemo(() => /* @__PURE__ */ new WeakMap(), []);
  const { currentData, changedKeys } = useMemo(() => {
    const data = updates[currentIndex] ?? {
      props: { current: {}, changes: /* @__PURE__ */ new Set() },
      state: { current: {}, changes: /* @__PURE__ */ new Set() },
      context: { current: {}, changes: /* @__PURE__ */ new Set() }
    };
    switch (section) {
      case "props":
        return {
          currentData: data.props.current,
          changedKeys: data.props.changes
        };
      case "state":
        return {
          currentData: data.state.current,
          changedKeys: data.state.changes
        };
      case "context":
        return {
          currentData: data.context.current,
          changedKeys: data.context.changes
        };
      default:
        return {
          currentData: {},
          changedKeys: /* @__PURE__ */ new Set()
        };
    }
  }, [section, currentIndex, updates]);
  const toggleExpanded = useCallback4(() => {
    setIsExpanded((state) => {
      if (isSticky && isExpanded) {
        return state;
      }
      return !state;
    });
  }, [isExpanded, isSticky]);
  if (!currentData || (Array.isArray(currentData) ? currentData.length === 0 : Object.keys(currentData).length === 0)) {
    return null;
  }
  const propertyCount = Array.isArray(currentData) ? currentData.length : Object.keys(currentData).length;
  return /* @__PURE__ */ jsxs2(Fragment, { children: [
    /* @__PURE__ */ jsxs2(
      "button",
      {
        ref: refs,
        type: "button",
        onClick: toggleExpanded,
        "data-sticky": true,
        className: "react-section-header",
        children: [
          /* @__PURE__ */ jsx3("div", { className: "w-4 h-4 flex items-center justify-center", children: /* @__PURE__ */ jsx3(
            Icon,
            {
              name: "icon-chevron-right",
              size: 12,
              className: cn(
                {
                  "rotate-90": isExpanded,
                  "rotate-0": isSticky && isExpanded
                }
              )
            }
          ) }),
          /* @__PURE__ */ jsxs2("span", { className: "capitalize", children: [
            section,
            " ",
            !isExpanded && propertyCount > 0 && `(${propertyCount})`
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsx3("div", { ref: refSection, className: "react-scan-section", children: /* @__PURE__ */ jsx3(
      "div",
      {
        className: cn(
          "react-scan-expandable",
          {
            "react-scan-expanded py-0.5": isExpanded
          }
        ),
        children: /* @__PURE__ */ jsx3("div", { className: "overflow-hidden", children: Array.isArray(currentData) ? currentData.map(({ name, value }) => /* @__PURE__ */ jsx3(
          PropertyElement,
          {
            name,
            value,
            section,
            level: 0,
            objectPathMap: pathMap,
            changedKeys
          },
          name
        )) : Object.entries(currentData).map(([key, value]) => /* @__PURE__ */ jsx3(
          PropertyElement,
          {
            name: key,
            value,
            section,
            level: 0,
            objectPathMap: pathMap,
            changedKeys
          },
          key
        )) })
      }
    ) })
  ] });
};

// src/web/components/inspector/what-changed.tsx
import { memo as memo4 } from "preact/compat";
import {
  useCallback as useCallback7,
  useEffect as useEffect5,
  useRef as useRef6,
  useState as useState5
} from "preact/hooks";

// src/web/components/inspector/diff-value.tsx
import { useMemo as useMemo2, useState as useState4 } from "preact/hooks";
import { Fragment as Fragment2, jsx as jsx4, jsxs as jsxs3 } from "preact/jsx-runtime";
var DiffValueView = ({
  value,
  expanded,
  onToggle,
  isNegative
}) => {
  const { value: safeValue, error } = safeGetValue(value);
  const pathPrefix = useMemo2(() => Math.random().toString(36).slice(2), []);
  const [expandedPaths, setExpandedPaths] = useState4(/* @__PURE__ */ new Set());
  if (error) {
    return /* @__PURE__ */ jsx4("span", { className: "text-gray-500 font-italic", children: error });
  }
  const isExpandable2 = safeValue !== null && typeof safeValue === "object" && !(safeValue instanceof Promise);
  const renderExpandedValue = (obj, path = "") => {
    if (obj === null || typeof obj !== "object") {
      return /* @__PURE__ */ jsx4("span", { children: formatValuePreview(obj) });
    }
    const entries = Object.entries(obj);
    const seenObjects = /* @__PURE__ */ new WeakSet();
    return /* @__PURE__ */ jsx4("div", { children: entries.map(([key, val], i) => {
      const currentPath = path ? `${path}.${key}` : key;
      const fullPath = `${pathPrefix}.${currentPath}`;
      const isExpanded = expandedPaths.has(fullPath);
      const canExpand = val !== null && typeof val === "object";
      let isCircular = false;
      if (canExpand) {
        if (seenObjects.has(val)) {
          isCircular = true;
        } else {
          seenObjects.add(val);
        }
      }
      return /* @__PURE__ */ jsxs3(
        "div",
        {
          className: cn({ "mt-1": i > 0 }),
          children: [
            /* @__PURE__ */ jsxs3("div", { className: "flex items-center gap-1", children: [
              canExpand && !isCircular && /* @__PURE__ */ jsx4(
                "button",
                {
                  type: "button",
                  onClick: () => {
                    setExpandedPaths((prev) => {
                      const next = new Set(prev);
                      if (next.has(fullPath)) {
                        next.delete(fullPath);
                      } else {
                        next.add(fullPath);
                      }
                      return next;
                    });
                  },
                  className: cn(
                    "flex items-center",
                    "p-0 mr-1",
                    "opacity-50"
                  ),
                  children: /* @__PURE__ */ jsx4(
                    Icon,
                    {
                      name: "icon-chevron-right",
                      size: 12,
                      className: cn(
                        "transition-[transform,color]",
                        "text-[#4ade80]",
                        {
                          "transform rotate-90": isExpanded,
                          "text-[#f87171]": isNegative
                        }
                      )
                    }
                  )
                }
              ),
              /* @__PURE__ */ jsxs3("span", { className: "text-gray-500", children: [
                key,
                ":"
              ] }),
              isCircular ? /* @__PURE__ */ jsx4("span", { className: "text-gray-500 font-italic", children: "[Circular]" }) : !canExpand || !isExpanded ? /* @__PURE__ */ jsx4("span", { children: formatValuePreview(val) }) : null
            ] }),
            canExpand && isExpanded && !isCircular && renderExpandedValue(val, currentPath)
          ]
        },
        key
      );
    }) });
  };
  return /* @__PURE__ */ jsxs3("div", { className: "flex items-start gap-1", children: [
    isExpandable2 && /* @__PURE__ */ jsx4(
      "button",
      {
        type: "button",
        onClick: onToggle,
        className: cn(
          "flex items-center",
          "p-0 mt-0.5 mr-1",
          "opacity-50"
        ),
        children: /* @__PURE__ */ jsx4(
          Icon,
          {
            name: "icon-chevron-right",
            size: 12,
            className: cn(
              "transition-[transform,color]",
              "text-[#4ade80]",
              {
                "transform rotate-90": expanded,
                "text-[#f87171]": isNegative
              }
            )
          }
        )
      }
    ),
    /* @__PURE__ */ jsx4("div", { className: "flex-1", children: !expanded ? /* @__PURE__ */ jsx4("span", { children: formatValuePreview(safeValue) }) : renderExpandedValue(safeValue) }),
    /* @__PURE__ */ jsx4(
      CopyToClipboard,
      {
        text: formatForClipboard(safeValue),
        className: "opacity-0 transition-opacity group-hover:opacity-100",
        children: ({ ClipboardIcon }) => /* @__PURE__ */ jsx4(Fragment2, { children: ClipboardIcon })
      }
    )
  ] });
};

// src/web/components/inspector/timeline/index.tsx
import { isInstrumentationActive } from "bippy";
import { memo as memo3 } from "preact/compat";
import { useCallback as useCallback6, useEffect as useEffect4, useMemo as useMemo3, useRef as useRef5 } from "preact/hooks";

// src/web/components/slider/index.tsx
import { useCallback as useCallback5, useEffect as useEffect3, useRef as useRef4 } from "preact/hooks";
import { jsx as jsx5, jsxs as jsxs4 } from "preact/jsx-runtime";
var Slider = ({
  value,
  min,
  max,
  onChange,
  className,
  totalUpdates = max + 1
}) => {
  const refThumb = useRef4(null);
  const refLastValue = useRef4(value);
  const updateThumbPosition = useCallback5((value2) => {
    if (!refThumb.current) return;
    const range = Math.max(1, max - min);
    const valueOffset = value2 - min;
    const percentage = min === max ? 0 : Math.min(100, Math.round(valueOffset / range * 100));
    refThumb.current.style.setProperty("left", `${percentage}%`);
  }, [min, max]);
  useEffect3(() => {
    updateThumbPosition(value);
  }, [min, max, value]);
  const handleChange = useCallback5((e) => {
    const target = e.target;
    const newValue = Number.parseInt(target.value, 10);
    if (newValue >= totalUpdates) {
      return;
    }
    if (refLastValue.current !== newValue) {
      refLastValue.current = newValue;
      updateThumbPosition(newValue);
      onChange(e);
    }
  }, [onChange, updateThumbPosition, totalUpdates]);
  return /* @__PURE__ */ jsxs4(
    "div",
    {
      onMouseDown: (e) => {
        e.stopPropagation();
      },
      className: cn(
        "react-scan-slider relative",
        "flex-1",
        className
      ),
      children: [
        /* @__PURE__ */ jsx5(
          "input",
          {
            type: "range",
            value,
            min,
            max,
            onChange: handleChange,
            className: cn(
              "react-scan-slider",
              "flex-1",
              "h-1.5",
              "bg-gray-200",
              "rounded-lg",
              "appearance-none",
              "cursor-pointer",
              className
            )
          }
        ),
        /* @__PURE__ */ jsx5(
          "div",
          {
            className: cn(
              "absolute inset-0 right-2",
              "pointer-events-none"
            ),
            children: /* @__PURE__ */ jsx5("span", { ref: refThumb })
          }
        )
      ]
    }
  );
};

// src/web/components/inspector/timeline/index.tsx
import { Fragment as Fragment3, jsx as jsx6, jsxs as jsxs5 } from "preact/jsx-runtime";
var Timeline = memo3(({
  refSticky
}) => {
  const refPlayInterval = useRef5(null);
  const refChangeInterval = useRef5(null);
  const {
    currentIndex,
    isVisible,
    totalUpdates,
    updates
  } = timelineState.value;
  const sliderValues = useMemo3(() => {
    return calculateSliderValues(totalUpdates, currentIndex);
  }, [totalUpdates, currentIndex]);
  const handleSliderChange = async (e) => {
    const target = e.target;
    const value = Number.parseInt(target.value, 10);
    const newIndex = Math.min(updates.length - 1, Math.max(0, value));
    let isViewingHistory = false;
    if (newIndex > 0 && newIndex < updates.length - 1) {
      isViewingHistory = true;
    }
    timelineActions.updateFrame(newIndex, isViewingHistory);
  };
  useEffect4(() => {
    return () => {
      if (refPlayInterval.current) {
        clearInterval(refPlayInterval.current);
      }
      if (refChangeInterval.current) {
        cancelAnimationFrame(refChangeInterval.current);
      }
    };
  }, []);
  const handleShowTimeline = useCallback6(() => {
    if (!isVisible) {
      timelineActions.showTimeline();
    }
  }, [isVisible]);
  const handleHideTimeline = useCallback6((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (refPlayInterval.current) {
      clearInterval(refPlayInterval.current);
      refPlayInterval.current = null;
    }
    timelineActions.hideTimeline();
  }, []);
  if (!isInstrumentationActive()) {
    return null;
  }
  if (totalUpdates <= 1) {
    return null;
  }
  return /* @__PURE__ */ jsxs5(
    "button",
    {
      ref: refSticky,
      type: "button",
      onClick: handleShowTimeline,
      className: "react-section-header",
      "data-disable-scroll": "true",
      children: [
        /* @__PURE__ */ jsx6(
          "button",
          {
            type: "button",
            onClick: isVisible ? handleHideTimeline : void 0,
            title: isVisible ? "Hide Re-renders History" : "View Re-renders History",
            className: "w-4 h-4 flex items-center justify-center",
            children: /* @__PURE__ */ jsx6(Icon, { name: "icon-gallery-horizontal-end", size: 12 })
          }
        ),
        isVisible ? /* @__PURE__ */ jsxs5(Fragment3, { children: [
          /* @__PURE__ */ jsx6("div", { className: "text-xs text-gray-500", children: sliderValues.leftValue }),
          /* @__PURE__ */ jsx6(
            Slider,
            {
              min: sliderValues.min,
              max: sliderValues.max,
              value: sliderValues.value,
              onChange: handleSliderChange,
              className: "flex-1",
              totalUpdates: sliderValues.rightValue + 1
            }
          ),
          /* @__PURE__ */ jsx6("div", { className: "text-xs text-gray-500", children: sliderValues.rightValue })
        ] }) : "View Re-renders History"
      ]
    }
  );
});

// src/web/components/inspector/what-changed.tsx
import { Fragment as Fragment4, jsx as jsx7, jsxs as jsxs6 } from "preact/jsx-runtime";
var safeGetValue2 = (value) => {
  if (value === null || value === void 0) return { value };
  if (typeof value === "function") return { value };
  if (typeof value !== "object") return { value };
  if (isPromise(value)) {
    return { value: "Promise" };
  }
  try {
    const proto = Object.getPrototypeOf(value);
    if (proto === Promise.prototype || proto?.constructor?.name === "Promise") {
      return { value: "Promise" };
    }
    return { value };
  } catch {
    return { value: null, error: "Error accessing value" };
  }
};
var WhatChangedSection = memo4(() => {
  const refShowTimeline = useRef6(false);
  const [shouldShowChanges, setShouldShowChanges] = useState5(true);
  useEffect5(() => {
    const rafId = 0;
    const unsubscribe = timelineState.subscribe(async (state) => {
      cancelAnimationFrame(rafId);
      const { currentIndex, updates } = state;
      if (currentIndex === 0) {
        setShouldShowChanges(false);
        return;
      }
      if (updates.length > 0) {
        if (!refShowTimeline.current) {
          refShowTimeline.current = true;
        }
        setShouldShowChanges(true);
      }
    });
    return () => {
      unsubscribe();
      cancelAnimationFrame(rafId);
    };
  }, []);
  return /* @__PURE__ */ jsxs6(Fragment4, { children: [
    refShowTimeline.current && /* @__PURE__ */ jsx7(StickySection, { children: (props) => /* @__PURE__ */ jsx7(Timeline, { ...props }) }),
    /* @__PURE__ */ jsx7(StickySection, { children: (props) => /* @__PURE__ */ jsx7(
      WhatChanged,
      {
        ...props,
        shouldShowChanges
      }
    ) })
  ] });
});
var WhatChanged = memo4(({
  isSticky,
  refSticky,
  calculateStickyTop,
  shouldShowChanges
}) => {
  const [isExpanded, setIsExpanded] = useState5(true);
  return /* @__PURE__ */ jsxs6(Fragment4, { children: [
    /* @__PURE__ */ jsx7(
      WhatsChangedHeader,
      {
        refSticky,
        isSticky,
        calculateStickyTop,
        isExpanded,
        shouldShowChanges,
        setIsExpanded
      }
    ),
    /* @__PURE__ */ jsx7(
      "div",
      {
        className: cn("react-scan-expandable", {
          "react-scan-expanded": isExpanded
        }),
        children: /* @__PURE__ */ jsx7("div", { className: "overflow-hidden", children: shouldShowChanges && /* @__PURE__ */ jsxs6(
          "div",
          {
            className: cn(
              "relative",
              "flex flex-col gap-y-2",
              "pl-9 pr-2",
              'before:content-[""] before:absolute before:inset-x-0 before:bottom-0 before:h-[1px] before:bg-[#333]'
            ),
            children: [
              /* @__PURE__ */ jsx7(Section, { title: "Props", isExpanded }),
              /* @__PURE__ */ jsx7(Section, { title: "State", isExpanded }),
              /* @__PURE__ */ jsx7(Section, { title: "Context", isExpanded })
            ]
          }
        ) })
      }
    )
  ] });
});
var renderStateName = (key, componentName) => {
  if (Number.isNaN(Number(key))) {
    return key;
  }
  const n = Number.parseInt(key);
  const getOrdinalSuffix = (num) => {
    const lastDigit = num % 10;
    const lastTwoDigits = num % 100;
    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
      return "th";
    }
    switch (lastDigit) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };
  return /* @__PURE__ */ jsxs6("span", { children: [
    n,
    getOrdinalSuffix(n),
    " hook",
    " ",
    /* @__PURE__ */ jsxs6("span", { style: { color: "#666" }, children: [
      "called in",
      " ",
      /* @__PURE__ */ jsx7("i", { className: "text-[#A855F7] truncate", children: componentName })
    ] })
  ] });
};
var WhatsChangedHeader = memo4(
  ({
    refSticky,
    isSticky,
    calculateStickyTop,
    isExpanded,
    shouldShowChanges,
    setIsExpanded
  }) => {
    const refProps = useRef6(null);
    const refState = useRef6(null);
    const refContext = useRef6(null);
    const refStats = useRef6({
      isPropsChanged: false,
      isStateChanged: false,
      isContextChanged: false
    });
    useEffect5(() => {
      const flash = throttle(() => {
        const flashElements = [];
        if (refProps.current?.dataset.flash === "true") {
          flashElements.push(refProps.current);
        }
        if (refState.current?.dataset.flash === "true") {
          flashElements.push(refState.current);
        }
        if (refContext.current?.dataset.flash === "true") {
          flashElements.push(refContext.current);
        }
        for (const element of flashElements) {
          element.classList.remove("count-flash-white");
          void element.offsetWidth;
          element.classList.add("count-flash-white");
        }
      }, 400);
      const unsubscribe = timelineState.subscribe((state) => {
        if (!refProps.current || !refState.current || !refContext.current) {
          return;
        }
        const { currentIndex, updates } = state;
        const currentUpdate = updates[currentIndex];
        if (!currentUpdate || currentIndex === 0) {
          return;
        }
        ;
        flash();
        refStats.current = {
          isPropsChanged: (currentUpdate.props?.changes?.size ?? 0) > 0,
          isStateChanged: (currentUpdate.state?.changes?.size ?? 0) > 0,
          isContextChanged: (currentUpdate.context?.changes?.size ?? 0) > 0
        };
        if (refProps.current.dataset.flash !== "true") {
          refProps.current.dataset.flash = refStats.current.isPropsChanged.toString();
        }
        if (refState.current.dataset.flash !== "true") {
          refState.current.dataset.flash = refStats.current.isStateChanged.toString();
        }
        if (refContext.current.dataset.flash !== "true") {
          refContext.current.dataset.flash = refStats.current.isContextChanged.toString();
        }
      });
      return unsubscribe;
    }, []);
    const toggleExpanded = useCallback7(() => {
      setIsExpanded((state) => {
        if (isSticky && isExpanded) {
          return state;
        }
        return !state;
      });
    }, [setIsExpanded, isExpanded, isSticky]);
    const onTransitionStart = useCallback7((e) => {
      if (e.propertyName === "max-height") {
        calculateStickyTop(true);
      }
    }, [calculateStickyTop]);
    const onTransitionEnd = useCallback7((e) => {
      if (e.propertyName === "max-height") {
        calculateStickyTop(false);
      }
    }, [calculateStickyTop]);
    return /* @__PURE__ */ jsx7(
      "button",
      {
        ref: refSticky,
        type: "button",
        onClick: toggleExpanded,
        onTransitionStart,
        onTransitionEnd,
        className: cn(
          "react-section-header",
          "overflow-hidden",
          "max-h-0",
          "transition-[max-height]",
          {
            "max-h-8": shouldShowChanges
          }
        ),
        children: /* @__PURE__ */ jsx7(
          "div",
          {
            className: cn(
              "flex-1 react-scan-expandable",
              {
                "react-scan-expanded": shouldShowChanges
              }
            ),
            children: /* @__PURE__ */ jsx7("div", { className: "overflow-hidden", children: /* @__PURE__ */ jsxs6("div", { className: "flex items-center whitespace-nowrap", children: [
              /* @__PURE__ */ jsxs6("div", { className: "flex items-center gap-x-2", children: [
                /* @__PURE__ */ jsx7("div", { className: "w-4 h-4 flex items-center justify-center", children: /* @__PURE__ */ jsx7(
                  Icon,
                  {
                    name: "icon-chevron-right",
                    size: 12,
                    className: cn({
                      "rotate-90": isExpanded,
                      "rotate-0": isSticky && isExpanded
                    })
                  }
                ) }),
                "What changed?"
              ] }),
              /* @__PURE__ */ jsxs6(
                "div",
                {
                  className: cn(
                    "ml-auto",
                    "change-scope",
                    "opacity-0",
                    "transition-opacity duration-300 delay-150",
                    {
                      "opacity-100": !isExpanded
                    }
                  ),
                  children: [
                    /* @__PURE__ */ jsx7("div", { ref: refProps, children: "props" }),
                    /* @__PURE__ */ jsx7("div", { ref: refState, children: "state" }),
                    /* @__PURE__ */ jsx7("div", { ref: refContext, children: "context" })
                  ]
                }
              )
            ] }) })
          }
        )
      }
    );
  }
);
var Section = memo4(({ title, isExpanded }) => {
  const refFiberInfo = useRef6(null);
  const refLastUpdated = useRef6(/* @__PURE__ */ new Set());
  const refChangesValues = useRef6(/* @__PURE__ */ new Map());
  const refLatestChanges = useRef6([]);
  const [changes, setChanges] = useState5([]);
  const [expandedFns, setExpandedFns] = useState5(/* @__PURE__ */ new Set());
  const [expandedEntries, setExpandedEntries] = useState5(/* @__PURE__ */ new Set());
  useEffect5(() => {
    const unsubscribe = timelineState.subscribe((state) => {
      const { currentIndex, updates } = state;
      const currentUpdate = currentIndex >= 0 ? updates[currentIndex] : null;
      const prevUpdate = currentIndex > 0 ? updates[currentIndex - 1] : null;
      const currentData = currentUpdate?.[title.toLowerCase()];
      const prevData = prevUpdate?.[title.toLowerCase()];
      if (!currentData) {
        return;
      }
      refFiberInfo.current = currentUpdate?.fiberInfo;
      refLastUpdated.current.clear();
      const changesMap = new Map(
        refLatestChanges.current.map((c) => [c.name, c])
      );
      for (const { name, value } of currentData.current) {
        const currentCount = currentData.changesCounts?.get(name) ?? 0;
        const prevCount = prevData?.changesCounts?.get(name) ?? 0;
        const count = Math.max(currentCount, prevCount);
        const prevValue = prevData?.current.find(
          (p) => p.name === name
        )?.value;
        const hasValueChange = !isEqual(value, prevValue);
        if (count > 0 || hasValueChange) {
          const { value: safePrevValue, error: prevError } = safeGetValue2(prevValue);
          const { value: safeCurrValue, error: currError } = safeGetValue2(value);
          const diff = getObjectDiff(safePrevValue, safeCurrValue);
          refChangesValues.current.set(name, {
            name,
            prevValue,
            currValue: value,
            prevError,
            currError,
            diff,
            isFunction: typeof value === "function"
          });
          const change = { name, value, prevValue, count };
          const existingChange = changesMap.get(name);
          if (!existingChange || existingChange.count !== count || !isEqual(existingChange.value, value)) {
            refLastUpdated.current.add(name);
          }
          changesMap.set(name, change);
        }
      }
      refLatestChanges.current = Array.from(changesMap.values());
      setChanges(refLatestChanges.current);
    });
    return unsubscribe;
  }, [title]);
  const handleExpandEntry = useCallback7((entryKey) => {
    setExpandedEntries((prev) => {
      const next = new Set(prev);
      if (next.has(String(entryKey))) {
        next.delete(String(entryKey));
      } else {
        next.add(String(entryKey));
      }
      return next;
    });
  }, []);
  const memoizedRenderStateName = useCallback7((name) => {
    if (!refFiberInfo.current) return name;
    return renderStateName(name, refFiberInfo.current.displayName);
  }, []);
  if (changes.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxs6("div", { className: "pb-2", children: [
    /* @__PURE__ */ jsx7("div", { className: "text-xs text-[#888] mb-1.5", children: title }),
    /* @__PURE__ */ jsx7("div", { className: "flex flex-col gap-2", children: changes.map((change) => {
      const isEntryExpanded = expandedEntries.has(String(change.name));
      const values = refChangesValues.current.get(change.name);
      if (!values) return null;
      return /* @__PURE__ */ jsxs6("div", { children: [
        /* @__PURE__ */ jsx7(
          "button",
          {
            type: "button",
            onClick: () => handleExpandEntry(String(change.name)),
            className: cn(
              "relative",
              "flex items-center gap-2",
              "w-full p-0 cursor-pointer text-white text-xs"
            ),
            children: /* @__PURE__ */ jsxs6("div", { className: "flex items-center gap-1.5 flex-1", children: [
              /* @__PURE__ */ jsx7(
                Icon,
                {
                  name: "icon-chevron-right",
                  size: 12,
                  className: cn(
                    "text-[#666] transition-transform duration-200 ease-[cubic-bezier(0.25,0.1,0.25,1)]",
                    {
                      "rotate-90": isEntryExpanded
                    }
                  )
                }
              ),
              /* @__PURE__ */ jsxs6("div", { className: "whitespace-nowrap break-words text-left font-medium flex items-center gap-x-1.5", children: [
                memoizedRenderStateName(String(change.name)),
                /* @__PURE__ */ jsx7(
                  CountBadge,
                  {
                    forceFlash: isExpanded && refLastUpdated.current.has(change.name),
                    count: change.count,
                    isFunction: values.isFunction,
                    showWarning: values.diff.changes.length === 0
                  }
                )
              ] })
            ] })
          }
        ),
        /* @__PURE__ */ jsx7(
          "div",
          {
            className: cn(
              "react-scan-expandable",
              "overflow-hidden",
              {
                "react-scan-expanded": isEntryExpanded
              }
            ),
            children: /* @__PURE__ */ jsx7("div", { className: "pl-3 text-xs font-mono border-l-1 border-[#333] overflow-hidden", children: /* @__PURE__ */ jsx7("div", { className: "flex flex-col gap-0.5", children: values.prevError || values.currError ? /* @__PURE__ */ jsx7(
              AccessError,
              {
                currError: values.currError,
                prevError: values.prevError
              }
            ) : values.diff.changes.length > 0 ? /* @__PURE__ */ jsx7(
              DiffChange,
              {
                title,
                change,
                diff: values.diff,
                expandedFns,
                renderName: memoizedRenderStateName,
                setExpandedFns
              }
            ) : /* @__PURE__ */ jsx7(
              ReferenceOnlyChange,
              {
                currValue: values.currValue,
                entryKey: change.name,
                expandedFns,
                prevValue: values.prevValue,
                setExpandedFns
              }
            ) }) })
          }
        )
      ] }, change.name);
    }) })
  ] });
});
var AccessError = ({
  prevError,
  currError
}) => {
  return /* @__PURE__ */ jsxs6(Fragment4, { children: [
    prevError && /* @__PURE__ */ jsx7("div", { className: "text-[#f87171] bg-[#2a1515] px-1.5 py-[3px] rounded-[2px] italic", children: prevError }),
    currError && /* @__PURE__ */ jsx7("div", { className: "text-[#4ade80] bg-[#1a2a1a] px-1.5 py-[3px] rounded-[2px] italic mt-0.5", children: currError })
  ] });
};
var DiffChange = ({
  diff,
  title,
  renderName,
  change,
  expandedFns,
  setExpandedFns
}) => {
  return diff.changes.map((diffChange, i) => {
    const { value: prevDiffValue, error: prevDiffError } = safeGetValue2(
      diffChange.prevValue
    );
    const { value: currDiffValue, error: currDiffError } = safeGetValue2(
      diffChange.currentValue
    );
    const isFunction = typeof prevDiffValue === "function" || typeof currDiffValue === "function";
    let path;
    if (title === "Props") {
      path = diffChange.path.length > 0 ? `${renderName(String(change.name))}.${formatPath(diffChange.path)}` : void 0;
    }
    if (title === "State" && diffChange.path.length > 0) {
      path = `state.${formatPath(diffChange.path)}`;
    }
    if (!path) {
      path = formatPath(diffChange.path);
    }
    return /* @__PURE__ */ jsxs6(
      "div",
      {
        className: cn("flex flex-col gap-y-1", {
          "mb-4": i < diff.changes.length - 1
        }),
        children: [
          path && /* @__PURE__ */ jsx7("div", { className: "text-[#666] text-[10px]", children: path }),
          /* @__PURE__ */ jsxs6(
            "button",
            {
              type: "button",
              className: cn(
                "group",
                "flex items-start",
                "py-[3px] px-1.5",
                "text-left text-[#f87171] bg-[#2a1515]",
                "rounded-[2px]",
                "overflow-hidden break-all",
                isFunction && "cursor-pointer"
              ),
              onClick: isFunction ? () => {
                const fnKey = `${formatPath(diffChange.path)}-prev`;
                setExpandedFns((prev) => {
                  const next = new Set(prev);
                  if (next.has(fnKey)) {
                    next.delete(fnKey);
                  } else {
                    next.add(fnKey);
                  }
                  return next;
                });
              } : void 0,
              children: [
                /* @__PURE__ */ jsx7("span", { className: "w-3 opacity-50", children: "-" }),
                /* @__PURE__ */ jsx7("span", { className: "flex-1 whitespace-nowrap font-mono", children: prevDiffError ? /* @__PURE__ */ jsx7("span", { className: "italic text-[#f87171]", children: prevDiffError }) : isFunction ? /* @__PURE__ */ jsxs6("div", { className: "flex gap-1 items-start flex-col", children: [
                  /* @__PURE__ */ jsxs6("div", { className: "flex gap-1 items-start w-full", children: [
                    /* @__PURE__ */ jsx7("span", { className: "flex-1 max-h-40", children: formatFunctionPreview(
                      prevDiffValue,
                      expandedFns.has(`${formatPath(diffChange.path)}-prev`)
                    ) }),
                    typeof prevDiffValue === "function" && /* @__PURE__ */ jsx7(
                      CopyToClipboard,
                      {
                        text: prevDiffValue.toString(),
                        className: "opacity-0 transition-opacity group-hover:opacity-100",
                        children: ({ ClipboardIcon }) => /* @__PURE__ */ jsx7(Fragment4, { children: ClipboardIcon })
                      }
                    )
                  ] }),
                  prevDiffValue?.toString() === currDiffValue?.toString() && /* @__PURE__ */ jsx7("div", { className: "text-[10px] text-[#666] italic", children: "Function reference changed" })
                ] }) : /* @__PURE__ */ jsx7(
                  DiffValueView,
                  {
                    value: prevDiffValue,
                    expanded: expandedFns.has(
                      `${formatPath(diffChange.path)}-prev`
                    ),
                    onToggle: () => {
                      const key = `${formatPath(diffChange.path)}-prev`;
                      setExpandedFns((prev) => {
                        const next = new Set(prev);
                        if (next.has(key)) {
                          next.delete(key);
                        } else {
                          next.add(key);
                        }
                        return next;
                      });
                    },
                    isNegative: true
                  }
                ) })
              ]
            }
          ),
          /* @__PURE__ */ jsxs6(
            "button",
            {
              type: "button",
              className: cn(
                "group",
                "flex items-start",
                "py-[3px] px-1.5",
                "text-left text-[#4ade80] bg-[#1a2a1a]",
                "rounded-[2px]",
                "overflow-hidden break-all",
                isFunction && "cursor-pointer"
              ),
              onClick: isFunction ? () => {
                const fnKey = `${formatPath(diffChange.path)}-current`;
                setExpandedFns((prev) => {
                  const next = new Set(prev);
                  if (next.has(fnKey)) {
                    next.delete(fnKey);
                  } else {
                    next.add(fnKey);
                  }
                  return next;
                });
              } : void 0,
              children: [
                /* @__PURE__ */ jsx7("span", { className: "w-3 opacity-50", children: "+" }),
                /* @__PURE__ */ jsx7("span", { className: "flex-1 whitespace-pre-wrap font-mono", children: currDiffError ? /* @__PURE__ */ jsx7("span", { className: "italic text-[#4ade80]", children: currDiffError }) : isFunction ? /* @__PURE__ */ jsxs6("div", { className: "flex gap-1 items-start flex-col", children: [
                  /* @__PURE__ */ jsxs6("div", { className: "flex gap-1 items-start w-full", children: [
                    /* @__PURE__ */ jsx7("span", { className: "flex-1", children: formatFunctionPreview(
                      currDiffValue,
                      expandedFns.has(`${formatPath(diffChange.path)}-current`)
                    ) }),
                    typeof currDiffValue === "function" && /* @__PURE__ */ jsx7(
                      CopyToClipboard,
                      {
                        text: currDiffValue.toString(),
                        className: "opacity-0 transition-opacity group-hover:opacity-100",
                        children: ({ ClipboardIcon }) => /* @__PURE__ */ jsx7(Fragment4, { children: ClipboardIcon })
                      }
                    )
                  ] }),
                  prevDiffValue?.toString() === currDiffValue?.toString() && /* @__PURE__ */ jsx7("div", { className: "text-[10px] text-[#666] italic", children: "Function reference changed" })
                ] }) : /* @__PURE__ */ jsx7(
                  DiffValueView,
                  {
                    value: currDiffValue,
                    expanded: expandedFns.has(
                      `${formatPath(diffChange.path)}-current`
                    ),
                    onToggle: () => {
                      const key = `${formatPath(diffChange.path)}-current`;
                      setExpandedFns((prev) => {
                        const next = new Set(prev);
                        if (next.has(key)) {
                          next.delete(key);
                        } else {
                          next.add(key);
                        }
                        return next;
                      });
                    },
                    isNegative: false
                  }
                ) })
              ]
            }
          )
        ]
      },
      `${path}-${change.name}-${i}`
    );
  });
};
var ReferenceOnlyChange = ({
  prevValue,
  currValue,
  entryKey,
  expandedFns,
  setExpandedFns
}) => {
  return /* @__PURE__ */ jsxs6(Fragment4, { children: [
    /* @__PURE__ */ jsxs6("div", { className: "group flex items-start text-[#f87171] bg-[#2a1515] py-[3px] px-1.5 rounded-[2px]", children: [
      /* @__PURE__ */ jsx7("span", { className: "w-3 opacity-50", children: "-" }),
      /* @__PURE__ */ jsx7("span", { className: "flex-1 whitespace-pre-wrap font-mono", children: /* @__PURE__ */ jsx7(
        DiffValueView,
        {
          value: prevValue,
          expanded: expandedFns.has(`${String(entryKey)}-prev`),
          onToggle: () => {
            const key = `${String(entryKey)}-prev`;
            setExpandedFns((prev) => {
              const next = new Set(prev);
              if (next.has(key)) {
                next.delete(key);
              } else {
                next.add(key);
              }
              return next;
            });
          },
          isNegative: true
        }
      ) })
    ] }),
    /* @__PURE__ */ jsxs6("div", { className: "group flex items-start text-[#4ade80] bg-[#1a2a1a] py-[3px] px-1.5 rounded-[2px] mt-0.5", children: [
      /* @__PURE__ */ jsx7("span", { className: "w-3 opacity-50", children: "+" }),
      /* @__PURE__ */ jsx7("span", { className: "flex-1 whitespace-pre-wrap font-mono", children: /* @__PURE__ */ jsx7(
        DiffValueView,
        {
          value: currValue,
          expanded: expandedFns.has(`${String(entryKey)}-current`),
          onToggle: () => {
            const key = `${String(entryKey)}-current`;
            setExpandedFns((prev) => {
              const next = new Set(prev);
              if (next.has(key)) {
                next.delete(key);
              } else {
                next.add(key);
              }
              return next;
            });
          },
          isNegative: false
        }
      ) })
    ] }),
    typeof currValue === "object" && currValue !== null && /* @__PURE__ */ jsx7("div", { className: "text-[#666] text-[10px] italic mt-1", children: "Reference changed but objects are the same" })
  ] });
};
var CountBadge = ({
  count,
  forceFlash,
  isFunction,
  showWarning
}) => {
  const refTimer = useRef6();
  const refIsFirstRender = useRef6(true);
  const refBadge = useRef6(null);
  const refPrevCount = useRef6(count);
  useEffect5(() => {
    const element = refBadge.current;
    if (!element || refPrevCount.current === count) {
      return;
    }
    element.classList.remove("count-flash");
    void element.offsetWidth;
    element.classList.add("count-flash");
    refPrevCount.current = count;
  }, [count]);
  useEffect5(() => {
    if (refIsFirstRender.current) {
      refIsFirstRender.current = false;
      return;
    }
    if (forceFlash) {
      refTimer.current = setTimeout(() => {
        refBadge.current?.classList.add("count-flash-white");
        refTimer.current = setTimeout(() => {
          refBadge.current?.classList.remove("count-flash-white");
        }, 300);
      }, 500);
    }
    return () => {
      clearTimeout(refTimer.current);
    };
  }, [forceFlash]);
  return /* @__PURE__ */ jsxs6("div", { ref: refBadge, className: "count-badge", children: [
    showWarning && /* @__PURE__ */ jsx7(
      Icon,
      {
        name: "icon-triangle-alert",
        className: "text-yellow-500 mb-px",
        size: 14
      }
    ),
    isFunction && /* @__PURE__ */ jsx7(Icon, { name: "icon-function", className: "text-[#A855F7] mb-px", size: 14 }),
    "x",
    count
  ] });
};

// src/web/components/inspector/index.tsx
import { jsx as jsx8, jsxs as jsxs7 } from "preact/jsx-runtime";
var globalInspectorState = {
  lastRendered: /* @__PURE__ */ new Map(),
  expandedPaths: /* @__PURE__ */ new Set(),
  cleanup: () => {
    globalInspectorState.lastRendered.clear();
    globalInspectorState.expandedPaths.clear();
    flashManager.cleanupAll();
    resetTracking();
    timelineActions.reset();
  }
};
var InspectorErrorBoundary = class extends Component {
  constructor() {
    super(...arguments);
    this.state = {
      hasError: false,
      error: null
    };
    this.handleReset = () => {
      this.setState({ hasError: false, error: null });
      globalInspectorState.cleanup();
    };
  }
  static getDerivedStateFromError(e) {
    return { hasError: true, error: e };
  }
  render() {
    if (this.state.hasError) {
      return /* @__PURE__ */ jsxs7("div", { className: "p-4 bg-red-950/50 h-screen backdrop-blur-sm", children: [
        /* @__PURE__ */ jsxs7("div", { className: "flex items-center gap-2 mb-3 text-red-400 font-medium", children: [
          /* @__PURE__ */ jsx8(Icon, { name: "icon-flame", className: "text-red-500", size: 16 }),
          "Something went wrong in the inspector"
        ] }),
        /* @__PURE__ */ jsx8("div", { className: "p-3 bg-black/40 rounded font-mono text-xs text-red-300 mb-4 break-words", children: this.state.error?.message || JSON.stringify(this.state.error) }),
        /* @__PURE__ */ jsx8(
          "button",
          {
            type: "button",
            onClick: this.handleReset,
            className: "px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2",
            children: "Reset Inspector"
          }
        )
      ] });
    }
    return this.props.children;
  }
};
var Inspector = constant(() => {
  const refInspector = useRef7(null);
  const refLastInspectedFiber = useRef7(null);
  const isSettingsOpen = signalIsSettingsOpen.value;
  useEffect6(() => {
    const processUpdate = (fiber) => {
      if (!fiber) return;
      refLastInspectedFiber.current = fiber;
      const { data: inspectorData, shouldUpdate } = collectInspectorData(fiber);
      if (shouldUpdate) {
        const update = {
          timestamp: Date.now(),
          fiberInfo: extractMinimalFiberInfo(fiber),
          props: inspectorData.fiberProps,
          state: inspectorData.fiberState,
          context: inspectorData.fiberContext,
          stateNames: getStateNames(fiber)
        };
        timelineActions.addUpdate(update, fiber);
      }
    };
    const unSubState = Store.inspectState.subscribe((state) => {
      if (state.kind !== "focused" || !state.focusedDomElement) {
        refLastInspectedFiber.current = null;
        globalInspectorState.cleanup();
        return;
      }
      if (state.kind === "focused") {
        signalIsSettingsOpen.value = false;
      }
      const { parentCompositeFiber } = getCompositeFiberFromElement(
        state.focusedDomElement,
        state.fiber
      );
      if (!parentCompositeFiber) return;
      const isNewComponent = refLastInspectedFiber.current?.type !== parentCompositeFiber.type;
      if (isNewComponent) {
        refLastInspectedFiber.current = parentCompositeFiber;
        globalInspectorState.cleanup();
        processUpdate(parentCompositeFiber);
      }
    });
    const unSubInspectorUpdate = inspectorUpdateSignal.subscribe(() => {
      const inspectState = Store.inspectState.value;
      if (inspectState.kind !== "focused" || !inspectState.focusedDomElement) {
        refLastInspectedFiber.current = null;
        globalInspectorState.cleanup();
        return;
      }
      const { parentCompositeFiber } = getCompositeFiberFromElement(
        inspectState.focusedDomElement,
        inspectState.fiber
      );
      if (!parentCompositeFiber) {
        Store.inspectState.value = {
          kind: "inspect-off"
        };
        return;
      }
      processUpdate(parentCompositeFiber);
      if (!inspectState.focusedDomElement.isConnected) {
        refLastInspectedFiber.current = null;
        globalInspectorState.cleanup();
        Store.inspectState.value = {
          kind: "inspecting",
          hoveredDomElement: null
        };
      }
    });
    return () => {
      unSubState();
      unSubInspectorUpdate();
      globalInspectorState.cleanup();
    };
  }, []);
  return /* @__PURE__ */ jsx8(InspectorErrorBoundary, { children: /* @__PURE__ */ jsxs7(
    "div",
    {
      ref: refInspector,
      className: cn(
        "react-scan-inspector",
        "flex-1",
        "opacity-0",
        "overflow-y-auto overflow-x-hidden",
        "transition-opacity delay-0",
        "pointer-events-none",
        {
          "opacity-100 delay-300 pointer-events-auto": !isSettingsOpen
        }
      ),
      children: [
        /* @__PURE__ */ jsx8(WhatChangedSection, {}),
        /* @__PURE__ */ jsx8(StickySection, { children: (props) => /* @__PURE__ */ jsx8(PropertySection, { section: "props", ...props }) }),
        /* @__PURE__ */ jsx8(StickySection, { children: (props) => /* @__PURE__ */ jsx8(PropertySection, { section: "state", ...props }) }),
        /* @__PURE__ */ jsx8(StickySection, { children: (props) => /* @__PURE__ */ jsx8(PropertySection, { section: "context", ...props }) })
      ]
    }
  ) });
});

// src/web/components/inspector/utils.ts
var getFiberFromElement = (element) => {
  if ("__REACT_DEVTOOLS_GLOBAL_HOOK__" in window) {
    const hook = window.__REACT_DEVTOOLS_GLOBAL_HOOK__;
    if (!hook?.renderers) return null;
    for (const [, renderer] of Array.from(hook.renderers)) {
      try {
        const fiber = renderer.findFiberByHostInstance?.(element);
        if (fiber) return fiber;
      } catch {
      }
    }
  }
  if ("_reactRootContainer" in element) {
    const elementWithRoot = element;
    const rootContainer = elementWithRoot._reactRootContainer;
    return rootContainer?._internalRoot?.current?.child ?? null;
  }
  for (const key in element) {
    if (key.startsWith("__reactInternalInstance$") || key.startsWith("__reactFiber")) {
      const elementWithFiber = element;
      return elementWithFiber[key];
    }
  }
  return null;
};
var getFirstStateNode = (fiber) => {
  let current = fiber;
  while (current) {
    if (current.stateNode instanceof Element) {
      return current.stateNode;
    }
    if (!current.child) {
      break;
    }
    current = current.child;
  }
  while (current) {
    if (current.stateNode instanceof Element) {
      return current.stateNode;
    }
    if (!current.return) {
      break;
    }
    current = current.return;
  }
  return null;
};
var getNearestFiberFromElement = (element) => {
  if (!element) return null;
  try {
    const fiber = getFiberFromElement(element);
    if (!fiber) return null;
    const res = getParentCompositeFiber(fiber);
    return res ? res[0] : null;
  } catch {
    return null;
  }
};
var getParentCompositeFiber = (fiber) => {
  let current = fiber;
  let prevHost = null;
  while (current) {
    if (isCompositeFiber(current)) return [current, prevHost];
    if (isHostFiber(current) && !prevHost) prevHost = current;
    current = current.return;
  }
  return null;
};
var isFiberInTree = (fiber, root) => {
  {
    const res = !!traverseFiber(root, (searchFiber) => searchFiber === fiber);
    return res;
  }
};
var getCompositeComponentFromElement = (element) => {
  const associatedFiber = getNearestFiberFromElement(element);
  if (!associatedFiber) return {};
  const stateNode = getFirstStateNode(associatedFiber);
  if (!stateNode) return {};
  const parentCompositeFiberInfo = getParentCompositeFiber(associatedFiber);
  if (!parentCompositeFiberInfo) {
    return {};
  }
  const [parentCompositeFiber] = parentCompositeFiberInfo;
  return {
    parentCompositeFiber
  };
};
var getCompositeFiberFromElement = (element, knownFiber) => {
  if (!element.isConnected) return {};
  let fiber = knownFiber ?? getNearestFiberFromElement(element);
  if (!fiber) return {};
  let curr = fiber;
  let rootFiber = null;
  let currentRootFiber = null;
  while (curr) {
    if (!curr.stateNode) {
      curr = curr.return;
      continue;
    }
    if (ReactScanInternals.instrumentation?.fiberRoots.has(curr.stateNode)) {
      rootFiber = curr;
      currentRootFiber = curr.stateNode.current;
      break;
    }
    curr = curr.return;
  }
  if (!rootFiber || !currentRootFiber) return {};
  fiber = isFiberInTree(fiber, currentRootFiber) ? fiber : fiber.alternate ?? fiber;
  if (!fiber) return {};
  if (!getFirstStateNode(fiber)) return {};
  const parentCompositeFiber = getParentCompositeFiber(fiber)?.[0];
  if (!parentCompositeFiber) return {};
  return {
    parentCompositeFiber: isFiberInTree(parentCompositeFiber, currentRootFiber) ? parentCompositeFiber : parentCompositeFiber.alternate ?? parentCompositeFiber
  };
};
var getChangedPropsDetailed = (fiber) => {
  const currentProps = fiber.memoizedProps ?? {};
  const previousProps = fiber.alternate?.memoizedProps ?? {};
  const changes = [];
  for (const key in currentProps) {
    if (key === "children") continue;
    const currentValue = currentProps[key];
    const prevValue = previousProps[key];
    if (!isEqual(currentValue, prevValue)) {
      changes.push({
        name: key,
        value: currentValue,
        prevValue,
        type: 1 /* Props */
      });
    }
  }
  return changes;
};
var isRecord = (value) => {
  return value !== null && typeof value === "object";
};
var getOverrideMethods = () => {
  let overrideProps = null;
  let overrideHookState = null;
  let overrideContext = null;
  if ("__REACT_DEVTOOLS_GLOBAL_HOOK__" in window) {
    const hook = window.__REACT_DEVTOOLS_GLOBAL_HOOK__;
    if (!hook?.renderers) {
      return {
        overrideProps: null,
        overrideHookState: null,
        overrideContext: null
      };
    }
    for (const [, renderer] of Array.from(hook.renderers)) {
      try {
        const devToolsRenderer = renderer;
        if (overrideHookState) {
          const prevOverrideHookState = overrideHookState;
          overrideHookState = (fiber, id, path, value) => {
            let current = fiber.memoizedState;
            for (let i = 0; i < Number(id); i++) {
              if (!current?.next) break;
              current = current.next;
            }
            if (current?.queue) {
              const queue = current.queue;
              if (isRecord(queue) && "dispatch" in queue) {
                const dispatch = queue.dispatch;
                dispatch(value);
                return;
              }
            }
            prevOverrideHookState(fiber, id, path, value);
            devToolsRenderer.overrideHookState?.(fiber, id, path, value);
          };
        } else if (devToolsRenderer.overrideHookState) {
          overrideHookState = devToolsRenderer.overrideHookState;
        }
        if (overrideProps) {
          const prevOverrideProps = overrideProps;
          overrideProps = (fiber, path, value) => {
            prevOverrideProps(fiber, path, value);
            devToolsRenderer.overrideProps?.(fiber, path, value);
          };
        } else if (devToolsRenderer.overrideProps) {
          overrideProps = devToolsRenderer.overrideProps;
        }
        overrideContext = (fiber, contextType, value) => {
          let current = fiber;
          while (current) {
            const type = current.type;
            if (type === contextType || type?.Provider === contextType) {
              if (overrideProps) {
                overrideProps(current, ["value"], value);
                if (current.alternate) {
                  overrideProps(current.alternate, ["value"], value);
                }
              }
              break;
            }
            current = current.return;
          }
        };
      } catch {
      }
    }
  }
  return { overrideProps, overrideHookState, overrideContext };
};
var isExpandable = (value) => {
  if (value === null || typeof value !== "object" || isPromise(value)) {
    return false;
  }
  if (value instanceof ArrayBuffer) {
    return true;
  }
  if (value instanceof DataView) {
    return true;
  }
  if (ArrayBuffer.isView(value)) {
    return true;
  }
  if (value instanceof Map || value instanceof Set) {
    return value.size > 0;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return Object.keys(value).length > 0;
};
var isEditableValue = (value, parentPath) => {
  if (value == null) return true;
  if (isPromise(value)) return false;
  if (typeof value === "function") {
    return false;
  }
  if (parentPath) {
    const parts = parentPath.split(".");
    let currentPath = "";
    for (const part of parts) {
      currentPath = currentPath ? `${currentPath}.${part}` : part;
      const obj = globalInspectorState.lastRendered.get(currentPath);
      if (obj instanceof DataView || obj instanceof ArrayBuffer || ArrayBuffer.isView(obj)) {
        return false;
      }
    }
  }
  switch (value.constructor) {
    case Date:
    case RegExp:
    case Error:
      return true;
    default:
      switch (typeof value) {
        case "string":
        case "number":
        case "boolean":
        case "bigint":
          return true;
        default:
          return false;
      }
  }
};
var getPath = (componentName, section, parentPath, key) => {
  if (parentPath) {
    return `${componentName}.${parentPath}.${key}`;
  }
  if (section === "context" && !key.startsWith("context.")) {
    return `${componentName}.${section}.context.${key}`;
  }
  return `${componentName}.${section}.${key}`;
};
var sanitizeString = (value) => {
  return value.replace(/[<>]/g, "").replace(/javascript:/gi, "").replace(/data:/gi, "").replace(/on\w+=/gi, "").slice(0, 5e4);
};
var formatValue = (value) => {
  const metadata2 = ensureRecord(value);
  return metadata2.displayValue;
};
var formatForClipboard = (value) => {
  try {
    if (value === null) return "null";
    if (value === void 0) return "undefined";
    if (isPromise(value)) return "Promise";
    if (typeof value === "function") {
      const fnStr = value.toString();
      try {
        const formatted = fnStr.replace(/\s+/g, " ").replace(/{\s+/g, "{\n  ").replace(/;\s+/g, ";\n  ").replace(/}\s*$/g, "\n}").replace(/\(\s+/g, "(").replace(/\s+\)/g, ")").replace(/,\s+/g, ", ");
        return formatted;
      } catch {
        return fnStr;
      }
    }
    switch (true) {
      case value instanceof Date:
        return value.toISOString();
      case value instanceof RegExp:
        return value.toString();
      case value instanceof Error:
        return `${value.name}: ${value.message}`;
      case value instanceof Map:
        return JSON.stringify(Array.from(value.entries()), null, 2);
      case value instanceof Set:
        return JSON.stringify(Array.from(value), null, 2);
      case value instanceof DataView:
        return JSON.stringify(
          Array.from(new Uint8Array(value.buffer)),
          null,
          2
        );
      case value instanceof ArrayBuffer:
        return JSON.stringify(Array.from(new Uint8Array(value)), null, 2);
      case (ArrayBuffer.isView(value) && "length" in value):
        return JSON.stringify(
          Array.from(value),
          null,
          2
        );
      case Array.isArray(value):
        return JSON.stringify(value, null, 2);
      case typeof value === "object":
        return JSON.stringify(value, null, 2);
      default:
        return String(value);
    }
  } catch {
    return String(value);
  }
};
var detectValueType = (value) => {
  const trimmed = value.trim();
  switch (trimmed) {
    case "undefined":
      return { type: "undefined", value: void 0 };
    case "null":
      return { type: "null", value: null };
    case "true":
      return { type: "boolean", value: true };
    case "false":
      return { type: "boolean", value: false };
  }
  if (/^".*"$/.test(trimmed)) {
    return { type: "string", value: trimmed.slice(1, -1) };
  }
  if (/^-?\d+(?:\.\d+)?$/.test(trimmed)) {
    return { type: "number", value: Number(trimmed) };
  }
  return { type: "string", value: `"${trimmed}"` };
};
var formatInitialValue = (value) => {
  if (value === void 0) return "undefined";
  if (value === null) return "null";
  if (typeof value === "string") return `"${value}"`;
  return String(value);
};
var updateNestedValue = (obj, path, value) => {
  try {
    if (path.length === 0) return value;
    const [key, ...rest] = path;
    if (Array.isArray(obj) && obj.every((item) => "name" in item && "value" in item)) {
      const index = obj.findIndex((item) => item.name === key);
      if (index === -1) return obj;
      const newArray = [...obj];
      if (rest.length === 0) {
        newArray[index] = { ...newArray[index], value };
      } else {
        newArray[index] = {
          ...newArray[index],
          value: updateNestedValue(newArray[index].value, rest, value)
        };
      }
      return newArray;
    }
    if (obj instanceof Map) {
      const newMap = new Map(obj);
      if (rest.length === 0) {
        newMap.set(key, value);
      } else {
        const currentValue = newMap.get(key);
        newMap.set(key, updateNestedValue(currentValue, rest, value));
      }
      return newMap;
    }
    if (Array.isArray(obj)) {
      const index = Number.parseInt(key, 10);
      const newArray = [...obj];
      if (rest.length === 0) {
        newArray[index] = value;
      } else {
        newArray[index] = updateNestedValue(obj[index], rest, value);
      }
      return newArray;
    }
    if (obj && typeof obj === "object") {
      if (rest.length === 0) {
        return { ...obj, [key]: value };
      }
      return {
        ...obj,
        [key]: updateNestedValue(
          obj[key],
          rest,
          value
        )
      };
    }
    return value;
  } catch {
    return obj;
  }
};
var areFunctionsEqual = (prev, current) => {
  try {
    if (typeof prev !== "function" || typeof current !== "function") {
      return false;
    }
    return prev.toString() === current.toString();
  } catch {
    return false;
  }
};
var getObjectDiff = (prev, current, path = [], seen = /* @__PURE__ */ new WeakSet()) => {
  if (prev === current) {
    return { type: "primitive", changes: [], hasDeepChanges: false };
  }
  if (typeof prev === "function" && typeof current === "function") {
    const isSameFunction = areFunctionsEqual(prev, current);
    return {
      type: "primitive",
      changes: [
        {
          path,
          prevValue: prev,
          currentValue: current,
          sameFunction: isSameFunction
        }
      ],
      hasDeepChanges: !isSameFunction
    };
  }
  if (prev === null || current === null || prev === void 0 || current === void 0 || typeof prev !== "object" || typeof current !== "object") {
    return {
      type: "primitive",
      changes: [{ path, prevValue: prev, currentValue: current }],
      hasDeepChanges: true
    };
  }
  if (seen.has(prev) || seen.has(current)) {
    return {
      type: "object",
      changes: [{ path, prevValue: "[Circular]", currentValue: "[Circular]" }],
      hasDeepChanges: false
    };
  }
  seen.add(prev);
  seen.add(current);
  const prevObj = prev;
  const currentObj = current;
  const allKeys = /* @__PURE__ */ new Set([
    ...Object.keys(prevObj),
    ...Object.keys(currentObj)
  ]);
  const changes = [];
  let hasDeepChanges = false;
  for (const key of allKeys) {
    const prevValue = prevObj[key];
    const currentValue = currentObj[key];
    if (prevValue !== currentValue) {
      if (typeof prevValue === "object" && typeof currentValue === "object" && prevValue !== null && currentValue !== null) {
        const nestedDiff = getObjectDiff(
          prevValue,
          currentValue,
          [...path, key],
          seen
        );
        changes.push(...nestedDiff.changes);
        if (nestedDiff.hasDeepChanges) {
          hasDeepChanges = true;
        }
      } else {
        changes.push({
          path: [...path, key],
          prevValue,
          currentValue
        });
        hasDeepChanges = true;
      }
    }
  }
  return {
    type: "object",
    changes,
    hasDeepChanges
  };
};
var formatPath = (path) => {
  if (path.length === 0) return "";
  return path.reduce((acc, segment, i) => {
    if (/^\d+$/.test(segment)) {
      return `${acc}[${segment}]`;
    }
    return i === 0 ? segment : `${acc}.${segment}`;
  }, "");
};
function hackyJsFormatter(code) {
  const normalizedCode = code.replace(/\s+/g, " ").trim();
  const rawTokens = [];
  let current = "";
  for (let i = 0; i < normalizedCode.length; i++) {
    const c = normalizedCode[i];
    if (c === "=" && normalizedCode[i + 1] === ">") {
      if (current.trim()) rawTokens.push(current.trim());
      rawTokens.push("=>");
      current = "";
      i++;
      continue;
    }
    if (/[(){}[\];,<>:\?!]/.test(c)) {
      if (current.trim()) {
        rawTokens.push(current.trim());
      }
      rawTokens.push(c);
      current = "";
    } else if (/\s/.test(c)) {
      if (current.trim()) {
        rawTokens.push(current.trim());
      }
      current = "";
    } else {
      current += c;
    }
  }
  if (current.trim()) {
    rawTokens.push(current.trim());
  }
  const merged = [];
  for (let i = 0; i < rawTokens.length; i++) {
    const t = rawTokens[i];
    const n = rawTokens[i + 1];
    if (t === "(" && n === ")" || t === "[" && n === "]" || t === "{" && n === "}" || t === "<" && n === ">") {
      merged.push(t + n);
      i++;
    } else {
      merged.push(t);
    }
  }
  const arrowParamSet = /* @__PURE__ */ new Set();
  const genericSet = /* @__PURE__ */ new Set();
  function findMatchingPair(openTok, closeTok, startIndex) {
    let depth = 0;
    for (let j = startIndex; j < merged.length; j++) {
      const token = merged[j];
      if (token === openTok) depth++;
      else if (token === closeTok) {
        depth--;
        if (depth === 0) return j;
      }
    }
    return -1;
  }
  for (let i = 0; i < merged.length; i++) {
    const t = merged[i];
    if (t === "(") {
      const closeIndex = findMatchingPair("(", ")", i);
      if (closeIndex !== -1 && merged[closeIndex + 1] === "=>") {
        for (let k = i; k <= closeIndex; k++) {
          arrowParamSet.add(k);
        }
      }
    }
  }
  for (let i = 1; i < merged.length; i++) {
    const prev = merged[i - 1];
    const t = merged[i];
    if (/^[a-zA-Z0-9_$]+$/.test(prev) && t === "<") {
      const closeIndex = findMatchingPair("<", ">", i);
      if (closeIndex !== -1) {
        for (let k = i; k <= closeIndex; k++) {
          genericSet.add(k);
        }
      }
    }
  }
  let indentLevel = 0;
  const indentStr = "  ";
  const lines = [];
  let line = "";
  function pushLine() {
    if (line.trim()) {
      lines.push(line.replace(/\s+$/, ""));
    }
    line = "";
  }
  function newLine() {
    pushLine();
    line = indentStr.repeat(indentLevel);
  }
  const stack = [];
  function stackTop() {
    return stack.length ? stack[stack.length - 1] : null;
  }
  function placeToken(tok, noSpaceBefore = false) {
    if (!line.trim()) {
      line += tok;
    } else {
      if (noSpaceBefore || /^[),;:\].}>]$/.test(tok)) {
        line += tok;
      } else {
        line += ` ${tok}`;
      }
    }
  }
  for (let i = 0; i < merged.length; i++) {
    const tok = merged[i];
    const next = merged[i + 1] || "";
    if (["(", "{", "[", "<"].includes(tok)) {
      placeToken(tok);
      stack.push(tok);
      if (tok === "{") {
        indentLevel++;
        newLine();
      } else if (tok === "(" || tok === "[" || tok === "<") {
        if (arrowParamSet.has(i) && tok === "(" || genericSet.has(i) && tok === "<") {
        } else {
          const directClose = {
            "(": ")",
            "[": "]",
            "<": ">"
          }[tok];
          if (next !== directClose && next !== "()" && next !== "[]" && next !== "<>") {
            indentLevel++;
            newLine();
          }
        }
      }
    } else if ([")", "}", "]", ">"].includes(tok)) {
      const opening = stackTop();
      if (tok === ")" && opening === "(" || tok === "]" && opening === "[" || tok === ">" && opening === "<") {
        if (!(arrowParamSet.has(i) && tok === ")") && !(genericSet.has(i) && tok === ">")) {
          indentLevel = Math.max(indentLevel - 1, 0);
          newLine();
        }
      } else if (tok === "}" && opening === "{") {
        indentLevel = Math.max(indentLevel - 1, 0);
        newLine();
      }
      stack.pop();
      placeToken(tok);
      if (tok === "}") {
        newLine();
      }
    } else if (/^\(\)|\[\]|\{\}|\<\>$/.test(tok)) {
      placeToken(tok);
    } else if (tok === "=>") {
      placeToken(tok);
    } else if (tok === ";") {
      placeToken(tok, true);
      newLine();
    } else if (tok === ",") {
      placeToken(tok, true);
      const top = stackTop();
      if (!(arrowParamSet.has(i) && top === "(") && !(genericSet.has(i) && top === "<")) {
        if (top && ["{", "[", "(", "<"].includes(top)) {
          newLine();
        }
      }
    } else {
      placeToken(tok);
    }
  }
  pushLine();
  return lines.join("\n").replace(/\n\s*\n+/g, "\n").trim();
}
var formatFunctionPreview = (fn, expanded = false) => {
  try {
    const fnStr = fn.toString();
    const match = fnStr.match(
      /(?:function\s*)?(?:\(([^)]*)\)|([^=>\s]+))\s*=>?/
    );
    if (!match) return "\u0192";
    const params = match[1] || match[2] || "";
    const cleanParams = params.replace(/\s+/g, "");
    if (!expanded) {
      return `\u0192 (${cleanParams}) => ...`;
    }
    return hackyJsFormatter(fnStr);
  } catch {
    return "\u0192";
  }
};
var formatValuePreview = (value) => {
  if (value === null) return "null";
  if (value === void 0) return "undefined";
  if (typeof value === "string")
    return `"${value.length > 150 ? `${value.slice(0, 20)}...` : value}"`;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (typeof value === "function") return formatFunctionPreview(value);
  if (Array.isArray(value)) return `Array(${value.length})`;
  if (value instanceof Map) return `Map(${value.size})`;
  if (value instanceof Set) return `Set(${value.size})`;
  if (value instanceof Date) return value.toISOString();
  if (value instanceof RegExp) return value.toString();
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (typeof value === "object") {
    const keys = Object.keys(value);
    return `{${keys.length > 2 ? `${keys.slice(0, 2).join(", ")}, ...` : keys.join(", ")}}`;
  }
  return String(value);
};
var safeGetValue = (value) => {
  if (value === null || value === void 0) return { value };
  if (typeof value === "function") return { value };
  if (typeof value !== "object") return { value };
  if (value instanceof Promise) {
    return { value: "Promise" };
  }
  try {
    const proto = Object.getPrototypeOf(value);
    if (proto === Promise.prototype || proto?.constructor?.name === "Promise") {
      return { value: "Promise" };
    }
    return { value };
  } catch {
    return { value: null, error: "Error accessing value" };
  }
};
var calculateSliderValues = (totalUpdates, currentIndex) => {
  if (totalUpdates <= TIMELINE_MAX_UPDATES) {
    return {
      leftValue: 0,
      min: 0,
      max: totalUpdates - 1,
      value: currentIndex,
      rightValue: totalUpdates - 1
    };
  }
  return {
    leftValue: totalUpdates - TIMELINE_MAX_UPDATES,
    min: 0,
    max: TIMELINE_MAX_UPDATES - 1,
    value: currentIndex,
    rightValue: totalUpdates - 1
  };
};
var isPromise = (value) => {
  return !!value && (value instanceof Promise || typeof value === "object" && "then" in value);
};
var ensureRecord = (value, maxDepth = 2, seen = /* @__PURE__ */ new WeakSet()) => {
  if (isPromise(value)) {
    return { type: "promise", displayValue: "Promise" };
  }
  if (value === null) {
    return { type: "null", displayValue: "null" };
  }
  if (value === void 0) {
    return { type: "undefined", displayValue: "undefined" };
  }
  switch (typeof value) {
    case "object": {
      if (seen.has(value)) {
        return { type: "circular", displayValue: "[Circular Reference]" };
      }
      if (!value) return { type: "null", displayValue: "null" };
      seen.add(value);
      try {
        const result = {};
        if (value instanceof Element) {
          result.type = "Element";
          result.tagName = value.tagName.toLowerCase();
          result.displayValue = value.tagName.toLowerCase();
          return result;
        }
        if (value instanceof Map) {
          result.type = "Map";
          result.size = value.size;
          result.displayValue = `Map(${value.size})`;
          if (maxDepth > 0) {
            const entries = {};
            let index = 0;
            for (const [key, val] of value.entries()) {
              if (index >= 50) break;
              try {
                entries[String(key)] = ensureRecord(val, maxDepth - 1, seen);
              } catch {
                entries[String(index)] = {
                  type: "error",
                  displayValue: "Error accessing Map entry"
                };
              }
              index++;
            }
            result.entries = entries;
          }
          return result;
        }
        if (value instanceof Set) {
          result.type = "Set";
          result.size = value.size;
          result.displayValue = `Set(${value.size})`;
          if (maxDepth > 0) {
            const items = [];
            let count = 0;
            for (const item of value) {
              if (count >= 50) break;
              items.push(ensureRecord(item, maxDepth - 1, seen));
              count++;
            }
            result.items = items;
          }
          return result;
        }
        if (value instanceof Date) {
          result.type = "Date";
          result.value = value.toISOString();
          result.displayValue = value.toLocaleString();
          return result;
        }
        if (value instanceof RegExp) {
          result.type = "RegExp";
          result.value = value.toString();
          result.displayValue = value.toString();
          return result;
        }
        if (value instanceof Error) {
          result.type = "Error";
          result.name = value.name;
          result.message = value.message;
          result.displayValue = `${value.name}: ${value.message}`;
          return result;
        }
        if (value instanceof ArrayBuffer) {
          result.type = "ArrayBuffer";
          result.byteLength = value.byteLength;
          result.displayValue = `ArrayBuffer(${value.byteLength})`;
          return result;
        }
        if (value instanceof DataView) {
          result.type = "DataView";
          result.byteLength = value.byteLength;
          result.displayValue = `DataView(${value.byteLength})`;
          return result;
        }
        if (ArrayBuffer.isView(value)) {
          const typedArray = value;
          result.type = typedArray.constructor.name;
          result.length = typedArray.length;
          result.byteLength = typedArray.buffer.byteLength;
          result.displayValue = `${typedArray.constructor.name}(${typedArray.length})`;
          return result;
        }
        if (Array.isArray(value)) {
          result.type = "array";
          result.length = value.length;
          result.displayValue = `Array(${value.length})`;
          if (maxDepth > 0) {
            result.items = value.slice(0, 50).map((item) => ensureRecord(item, maxDepth - 1, seen));
          }
          return result;
        }
        const keys = Object.keys(value);
        result.type = "object";
        result.size = keys.length;
        result.displayValue = keys.length <= 5 ? `{${keys.join(", ")}}` : `{${keys.slice(0, 5).join(", ")}, ...${keys.length - 5}}`;
        if (maxDepth > 0) {
          const entries = {};
          for (const key of keys.slice(0, 50)) {
            try {
              entries[key] = ensureRecord(
                value[key],
                maxDepth - 1,
                seen
              );
            } catch {
              entries[key] = {
                type: "error",
                displayValue: "Error accessing property"
              };
            }
          }
          result.entries = entries;
        }
        return result;
      } finally {
        seen.delete(value);
      }
    }
    case "string":
      return {
        type: "string",
        value,
        displayValue: `"${value}"`
      };
    case "function":
      return {
        type: "function",
        displayValue: "\u0192()",
        name: value.name || "anonymous"
      };
    default:
      return {
        type: typeof value,
        value,
        displayValue: String(value)
      };
  }
};
var extractMinimalFiberInfo = (fiber) => {
  const timings = getTimings(fiber);
  return {
    displayName: getDisplayName3(fiber) || "Unknown",
    type: fiber.type,
    key: fiber.key,
    id: fiber.index,
    selfTime: timings?.selfTime ?? null,
    totalTime: timings?.totalTime ?? null
  };
};

// src/web/components/inspector/timeline/utils.ts
var propsTracker = /* @__PURE__ */ new Map();
var stateTracker = /* @__PURE__ */ new Map();
var contextTracker = /* @__PURE__ */ new Map();
var lastComponentType = null;
var STATE_NAME_REGEX = /\[(?<name>\w+),\s*set\w+\]/g;
var PROPS_ORDER_REGEX = /\(\s*{\s*(?<props>[^}]+)\s*}\s*\)/;
var getStateNames = (fiber) => {
  const componentSource = fiber.type?.toString?.() || "";
  return componentSource ? Array.from(
    componentSource.matchAll(STATE_NAME_REGEX),
    (m) => m.groups?.name ?? ""
  ) : [];
};
var resetTracking = () => {
  propsTracker.clear();
  stateTracker.clear();
  contextTracker.clear();
  lastComponentType = null;
};
var isInitialComponentUpdate = (fiber) => {
  const isNewComponent = fiber.type !== lastComponentType;
  lastComponentType = fiber.type;
  return isNewComponent;
};
var trackChange = (tracker, key, currentValue, previousValue) => {
  const existing = tracker.get(key);
  const isInitialValue = tracker === propsTracker || tracker === contextTracker;
  const hasChanged = !isEqual(currentValue, previousValue);
  if (!existing) {
    tracker.set(key, {
      count: hasChanged && isInitialValue ? 1 : 0,
      currentValue,
      previousValue,
      lastUpdated: Date.now()
    });
    return {
      hasChanged,
      count: hasChanged && isInitialValue ? 1 : isInitialValue ? 0 : 1
    };
  }
  if (!isEqual(existing.currentValue, currentValue)) {
    const newCount = existing.count + 1;
    tracker.set(key, {
      count: newCount,
      currentValue,
      previousValue: existing.currentValue,
      lastUpdated: Date.now()
    });
    return { hasChanged: true, count: newCount };
  }
  return { hasChanged: false, count: existing.count };
};
var getStateFromFiber = (fiber) => {
  if (!fiber) return {};
  if (fiber.tag === FunctionComponentTag2 || fiber.tag === ForwardRefTag2 || fiber.tag === SimpleMemoComponentTag2 || fiber.tag === MemoComponentTag2) {
    let memoizedState = fiber.memoizedState;
    const state = {};
    let index = 0;
    while (memoizedState) {
      if (memoizedState.queue && memoizedState.memoizedState !== void 0) {
        state[index] = memoizedState.memoizedState;
      }
      memoizedState = memoizedState.next;
      index++;
    }
    return state;
  }
  if (fiber.tag === ClassComponentTag) {
    return fiber.memoizedState || {};
  }
  return {};
};
var getPropsOrder = (fiber) => {
  const componentSource = fiber.type?.toString?.() || "";
  const match = componentSource.match(PROPS_ORDER_REGEX);
  if (!match?.groups?.props) return [];
  return match.groups.props.split(",").map((prop) => prop.trim().split(":")[0].split("=")[0].trim()).filter(Boolean);
};
var collectPropsChanges = (fiber) => {
  const currentProps = fiber.memoizedProps || {};
  const prevProps = fiber.alternate?.memoizedProps || {};
  const orderedProps = getPropsOrder(fiber);
  const current = {};
  const prev = {};
  const allProps = [
    .../* @__PURE__ */ new Set([...orderedProps, ...Object.keys(currentProps)])
  ];
  for (const key of allProps) {
    if (key in currentProps) {
      current[key] = currentProps[key];
      prev[key] = prevProps[key];
    }
  }
  const changes = getChangedPropsDetailed(fiber).map((change) => ({
    name: change.name,
    value: change.value,
    prevValue: change.prevValue
  }));
  return { current, prev, changes };
};
var collectStateChanges = (fiber) => {
  const current = getStateFromFiber(fiber);
  const prev = fiber.alternate ? getStateFromFiber(fiber.alternate) : {};
  const changes = [];
  for (const [index, value] of Object.entries(current)) {
    const stateKey = fiber.tag === ClassComponentTag ? index : Number(index);
    if (fiber.alternate && !isEqual(prev[index], value)) {
      changes.push({
        name: stateKey,
        value,
        prevValue: prev[index]
      });
    }
  }
  return { current, prev, changes };
};
var collectContextChanges = (fiber) => {
  const currentContexts = getAllFiberContexts(fiber);
  const prevContexts = fiber.alternate ? getAllFiberContexts(fiber.alternate) : /* @__PURE__ */ new Map();
  const current = {};
  const prev = {};
  const changes = [];
  const seenContexts = /* @__PURE__ */ new Set();
  for (const [contextType, ctx] of currentContexts) {
    const name = ctx.displayName;
    const contextKey = `${name}-${contextType?.toString()}`;
    if (seenContexts.has(contextKey)) continue;
    seenContexts.add(contextKey);
    current[name] = ctx.value;
    const prevCtx = prevContexts.get(contextType);
    if (prevCtx) {
      prev[name] = prevCtx.value;
      if (!isEqual(prevCtx.value, ctx.value)) {
        changes.push({
          name,
          value: ctx.value,
          prevValue: prevCtx.value,
          contextType
        });
      }
    }
  }
  return { current, prev, changes };
};
var collectInspectorData = (fiber) => {
  const emptySection = () => ({
    current: [],
    changes: /* @__PURE__ */ new Set(),
    changesCounts: /* @__PURE__ */ new Map()
  });
  if (!fiber) {
    return {
      data: {
        fiberProps: emptySection(),
        fiberState: emptySection(),
        fiberContext: emptySection()
      },
      shouldUpdate: false
    };
  }
  let hasNewChanges = false;
  const isInitialUpdate = isInitialComponentUpdate(fiber);
  const propsData = emptySection();
  if (fiber.memoizedProps) {
    const { current, changes } = collectPropsChanges(fiber);
    for (const [key, value] of Object.entries(current)) {
      propsData.current.push({
        name: key,
        value: isPromise(value) ? { type: "promise", displayValue: "Promise" } : value
      });
    }
    for (const change of changes) {
      const { hasChanged, count } = trackChange(
        propsTracker,
        change.name,
        change.value,
        change.prevValue
      );
      if (hasChanged) {
        hasNewChanges = true;
        propsData.changes.add(change.name);
        propsData.changesCounts.set(change.name, count);
      }
    }
  }
  const stateData = emptySection();
  const { current: stateCurrent, changes: stateChanges } = collectStateChanges(fiber);
  for (const [index, value] of Object.entries(stateCurrent)) {
    const stateKey = fiber.tag === ClassComponentTag ? index : Number(index);
    stateData.current.push({ name: stateKey, value });
  }
  for (const change of stateChanges) {
    const { hasChanged, count } = trackChange(
      stateTracker,
      change.name,
      change.value,
      change.prevValue
    );
    if (hasChanged) {
      hasNewChanges = true;
      stateData.changes.add(change.name);
      stateData.changesCounts.set(change.name, count);
    }
  }
  const contextData = emptySection();
  const { current: contextCurrent, changes: contextChanges } = collectContextChanges(fiber);
  for (const [name, value] of Object.entries(contextCurrent)) {
    contextData.current.push({ name, value });
  }
  if (!isInitialUpdate) {
    for (const change of contextChanges) {
      const { hasChanged, count } = trackChange(
        contextTracker,
        change.name,
        change.value,
        change.prevValue
      );
      if (hasChanged) {
        hasNewChanges = true;
        contextData.changes.add(change.name);
        contextData.changesCounts.set(change.name, count);
      }
    }
  }
  if (!hasNewChanges && !isInitialUpdate) {
    propsData.changes.clear();
    stateData.changes.clear();
    contextData.changes.clear();
  }
  return {
    data: {
      fiberProps: propsData,
      fiberState: stateData,
      fiberContext: contextData
    },
    shouldUpdate: hasNewChanges || isInitialUpdate
  };
};
var getAllFiberContexts = (fiber) => {
  const contexts = /* @__PURE__ */ new Map();
  if (!fiber) {
    return contexts;
  }
  let currentFiber = fiber;
  while (currentFiber) {
    const dependencies = currentFiber.dependencies;
    if (dependencies?.firstContext) {
      let contextItem = dependencies.firstContext;
      while (contextItem) {
        const memoizedValue = contextItem.memoizedValue;
        const displayName = contextItem.context?.displayName;
        if (!contexts.has(memoizedValue)) {
          contexts.set(contextItem.context, {
            value: memoizedValue,
            displayName: displayName ?? "UnnamedContext",
            contextType: null
          });
        }
        if (contextItem === contextItem.next) {
          break;
        }
        contextItem = contextItem.next;
      }
    }
    currentFiber = currentFiber.return;
  }
  return contexts;
};

// src/core/instrumentation.ts
var fps = 0;
var lastTime = performance.now();
var frameCount = 0;
var initedFps = false;
var updateFPS = () => {
  frameCount++;
  const now = performance.now();
  if (now - lastTime >= 1e3) {
    fps = frameCount;
    frameCount = 0;
    lastTime = now;
  }
  requestAnimationFrame(updateFPS);
};
var getFPS = () => {
  if (!initedFps) {
    initedFps = true;
    updateFPS();
    fps = 60;
  }
  return fps;
};
var isValueUnstable = (prevValue, nextValue) => {
  const prevValueString = fastSerialize(prevValue);
  const nextValueString = fastSerialize(nextValue);
  return prevValueString === nextValueString && unstableTypes.includes(typeof prevValue) && unstableTypes.includes(typeof nextValue);
};
var unstableTypes = ["function", "object"];
var cache = /* @__PURE__ */ new WeakMap();
function fastSerialize(value, depth = 0) {
  if (depth < 0) return "\u2026";
  switch (typeof value) {
    case "function":
      return value.toString();
    case "string":
      return value;
    case "number":
    case "boolean":
    case "undefined":
      return String(value);
    case "object":
      break;
    default:
      return String(value);
  }
  if (value === null) return "null";
  if (cache.has(value)) {
    const cached = cache.get(value);
    if (cached !== void 0) {
      return cached;
    }
  }
  if (Array.isArray(value)) {
    const str2 = value.length ? `[${value.length}]` : "[]";
    cache.set(value, str2);
    return str2;
  }
  if (isValidElement(value)) {
    const type = getDisplayName4(value.type) ?? "";
    const propCount = value.props ? Object.keys(value.props).length : 0;
    const str2 = `<${type} ${propCount}>`;
    cache.set(value, str2);
    return str2;
  }
  if (Object.getPrototypeOf(value) === Object.prototype) {
    const keys = Object.keys(value);
    const str2 = keys.length ? `{${keys.length}}` : "{}";
    cache.set(value, str2);
    return str2;
  }
  const ctor = value && typeof value === "object" ? value.constructor : void 0;
  if (ctor && typeof ctor === "function" && ctor.name) {
    const str2 = `${ctor.name}{\u2026}`;
    cache.set(value, str2);
    return str2;
  }
  const tagString = Object.prototype.toString.call(value).slice(8, -1);
  const str = `${tagString}{\u2026}`;
  cache.set(value, str);
  return str;
}
var instrumentationInstances = /* @__PURE__ */ new Map();
var inited = false;
var getAllInstances = () => Array.from(instrumentationInstances.values());
function isRenderUnnecessaryTraversal(_propsName, prevValue, nextValue) {
  if (!isEqual(prevValue, nextValue) && !isValueUnstable(prevValue, nextValue)) {
    this.isRequiredChange = true;
  }
}
var isRenderUnnecessary = (fiber) => {
  if (!didFiberCommit(fiber)) return true;
  const mutatedHostFibers = getMutatedHostFibers(fiber);
  for (const mutatedHostFiber of mutatedHostFibers) {
    const state = {
      isRequiredChange: false
    };
    traverseProps(mutatedHostFiber, isRenderUnnecessaryTraversal.bind(state));
    if (state.isRequiredChange) return false;
  }
  return true;
};
var TRACK_UNNECESSARY_RENDERS = false;
var createInstrumentation = (instanceKey, config) => {
  const instrumentation = {
    // this will typically be false, but in cases where a user provides showToolbar: true, this will be true
    isPaused: signal3(!ReactScanInternals.options.value.enabled),
    fiberRoots: /* @__PURE__ */ new WeakSet()
  };
  instrumentationInstances.set(instanceKey, {
    key: instanceKey,
    config,
    instrumentation
  });
  if (!inited) {
    inited = true;
    instrument({
      name: "react-scan",
      onActive: config.onActive,
      onCommitFiberRoot(_rendererID, root) {
        instrumentation.fiberRoots.add(root);
        if (ReactScanInternals.instrumentation?.isPaused.value && (Store.inspectState.value.kind === "inspect-off" || Store.inspectState.value.kind === "uninitialized") && !config.forceAlwaysTrackRenders) {
          return;
        }
        const allInstances = getAllInstances();
        for (const instance of allInstances) {
          instance.config.onCommitStart();
        }
        traverseRenderedFibers(
          root.current,
          (fiber, phase) => {
            const type = getType2(fiber.type);
            if (!type) return null;
            const allInstances2 = getAllInstances();
            const validInstancesIndicies = [];
            for (let i = 0, len = allInstances2.length; i < len; i++) {
              const instance = allInstances2[i];
              if (!instance.config.isValidFiber(fiber)) continue;
              validInstancesIndicies.push(i);
            }
            if (!validInstancesIndicies.length) return null;
            const changes = [];
            if (allInstances2.some((instance) => instance.config.trackChanges)) {
              const changesProps = collectPropsChanges(fiber).changes;
              const changesState = collectStateChanges(fiber).changes;
              const changesContext = collectContextChanges(fiber).changes;
              changes.push.apply(
                null,
                changesProps.map(
                  (change) => ({
                    type: 1 /* Props */,
                    name: change.name,
                    value: change.value
                  })
                )
              );
              for (const change of changesState) {
                if (fiber.tag === ClassComponentTag2) {
                  changes.push({
                    type: 3 /* ClassState */,
                    name: change.name.toString(),
                    value: change.value
                  });
                } else {
                  changes.push({
                    type: 2 /* FunctionalState */,
                    name: change.name.toString(),
                    value: change.value
                  });
                }
              }
              changes.push.apply(
                null,
                changesContext.map(
                  (change) => ({
                    type: 4 /* Context */,
                    name: change.name,
                    value: change.value,
                    contextType: Number(change.contextType)
                  })
                )
              );
            }
            const { selfTime } = getTimings2(fiber);
            const fps2 = getFPS();
            const render2 = {
              phase: RENDER_PHASE_STRING_TO_ENUM[phase],
              componentName: getDisplayName4(type),
              count: 1,
              changes,
              time: selfTime,
              forget: hasMemoCache2(fiber),
              // todo: allow this to be toggle-able through toolbar
              // todo: performance optimization: if the last fiber measure was very off screen, do not run isRenderUnnecessary
              unnecessary: TRACK_UNNECESSARY_RENDERS ? isRenderUnnecessary(fiber) : null,
              didCommit: didFiberCommit(fiber),
              fps: fps2
            };
            for (let i = 0, len = validInstancesIndicies.length; i < len; i++) {
              const index = validInstancesIndicies[i];
              const instance = allInstances2[index];
              instance.config.onRender(fiber, [render2]);
            }
          }
        );
        for (const instance of allInstances) {
          instance.config.onCommitFinish();
        }
      }
    });
  }
  return instrumentation;
};

// src/web/toolbar.tsx
import { Component as Component2, render } from "preact";

// src/web/components/widget/index.tsx
import { useCallback as useCallback12, useEffect as useEffect15, useRef as useRef16 } from "preact/hooks";

// src/web/components/inspector/overlay/index.tsx
import { getDisplayName as getDisplayName6 } from "bippy";
import { useEffect as useEffect7, useRef as useRef8 } from "preact/hooks";
import { Fragment as Fragment5, jsx as jsx9, jsxs as jsxs8 } from "preact/jsx-runtime";
var ANIMATION_CONFIG = {
  frameInterval: 1e3 / 60,
  speeds: {
    fast: 0.51,
    slow: 0.1,
    off: 0
  }
};
var OVERLAY_DPR = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;

// src/web/components/widget/components-tree/index.tsx
import {
  useCallback as useCallback9,
  useEffect as useEffect10,
  useMemo as useMemo5,
  useRef as useRef11,
  useState as useState8
} from "preact/hooks";

// src/web/hooks/use-virtual-list.ts
import {
  useCallback as useCallback8,
  useEffect as useEffect8,
  useMemo as useMemo4,
  useRef as useRef9,
  useState as useState6
} from "preact/hooks";

// src/web/utils/pin.ts
var metadata = readLocalStorage("react-scann-pinned");

// src/web/components/widget/components-tree/breadcrumb.tsx
import { useEffect as useEffect9, useRef as useRef10, useState as useState7 } from "preact/hooks";

// src/web/components/widget/components-tree/state.ts
import { signal as signal4 } from "@preact/signals";
var searchState = signal4({
  query: "",
  matches: [],
  currentMatchIndex: -1
});
var signalSkipTreeUpdate = signal4(false);

// src/web/components/widget/components-tree/breadcrumb.tsx
import { jsx as jsx10, jsxs as jsxs9 } from "preact/jsx-runtime";

// src/web/components/widget/components-tree/index.tsx
import { Fragment as Fragment6, jsx as jsx11, jsxs as jsxs10 } from "preact/jsx-runtime";

// src/web/components/widget/header.tsx
import { useEffect as useEffect11, useMemo as useMemo6, useRef as useRef12, useState as useState9 } from "preact/hooks";
import { Fragment as Fragment7, jsx as jsx12, jsxs as jsxs11 } from "preact/jsx-runtime";

// src/web/components/widget/resize-handle.tsx
import { useCallback as useCallback10, useEffect as useEffect12, useRef as useRef13 } from "preact/hooks";
import { jsx as jsx13 } from "preact/jsx-runtime";

// src/web/components/widget/toolbar/index.tsx
import { useCallback as useCallback11, useEffect as useEffect14, useRef as useRef15 } from "preact/hooks";

// src/web/components/toggle/index.tsx
import { jsx as jsx14, jsxs as jsxs12 } from "preact/jsx-runtime";
var Toggle = ({
  checked,
  onChange,
  class: className,
  ...props
}) => {
  return /* @__PURE__ */ jsxs12("div", { className: cn("react-scan-toggle", className), children: [
    /* @__PURE__ */ jsx14(
      "input",
      {
        type: "checkbox",
        checked,
        onChange,
        ...props
      }
    ),
    /* @__PURE__ */ jsx14("div", {})
  ] });
};

// src/web/components/widget/fps-meter.tsx
import { useEffect as useEffect13, useRef as useRef14 } from "preact/hooks";
import { jsx as jsx15, jsxs as jsxs13 } from "preact/jsx-runtime";
var FpsMeter = () => {
  const refFps = useRef14(null);
  useEffect13(() => {
    const intervalId = setInterval(() => {
      const fps2 = getFPS();
      let color = "#fff";
      if (fps2) {
        if (fps2 < 30) color = "#f87171";
        if (fps2 < 50) color = "#fbbf24";
      }
      if (refFps.current) {
        refFps.current.setAttribute("data-text", fps2.toString());
        refFps.current.style.color = color;
      }
    }, 100);
    return () => clearInterval(intervalId);
  }, []);
  return /* @__PURE__ */ jsxs13(
    "span",
    {
      className: cn(
        "flex items-center gap-x-1 px-1.5",
        "h-full",
        "rounded-xl",
        "font-mono text-xs font-medium",
        "bg-neutral-600"
      ),
      children: [
        /* @__PURE__ */ jsx15(
          "span",
          {
            ref: refFps,
            "data-text": "120",
            className: "transition-color ease-in-out with-data-text"
          }
        ),
        /* @__PURE__ */ jsx15("span", { className: "tracking-wide font-mono text-xxs mt-[1px]", children: "FPS" })
      ]
    }
  );
};
var fps_meter_default = FpsMeter;

// src/web/components/widget/toolbar/index.tsx
import { jsx as jsx16, jsxs as jsxs14 } from "preact/jsx-runtime";
var Toolbar = constant(() => {
  const refSettingsButton = useRef15(null);
  const inspectState = Store.inspectState;
  const isInspectActive = inspectState.value.kind === "inspecting";
  const isInspectFocused = inspectState.value.kind === "focused";
  const onToggleInspect = useCallback11(() => {
    const currentState = Store.inspectState.value;
    switch (currentState.kind) {
      case "inspecting":
        Store.inspectState.value = {
          kind: "inspect-off"
        };
        break;
      case "focused":
        Store.inspectState.value = {
          kind: "inspect-off"
        };
        break;
      case "inspect-off":
        Store.inspectState.value = {
          kind: "inspecting",
          hoveredDomElement: null
        };
        break;
      case "uninitialized":
        break;
    }
  }, []);
  const onToggleActive = useCallback11((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!ReactScanInternals.instrumentation) {
      return;
    }
    const isPaused = !ReactScanInternals.instrumentation.isPaused.value;
    ReactScanInternals.instrumentation.isPaused.value = isPaused;
    const existingLocalStorageOptions = readLocalStorage("react-scan-options");
    saveLocalStorage("react-scan-options", {
      ...existingLocalStorageOptions,
      enabled: !isPaused
    });
  }, []);
  useEffect14(() => {
    const unSubState = Store.inspectState.subscribe((state) => {
      if (state.kind === "uninitialized") {
        Store.inspectState.value = {
          kind: "inspect-off"
        };
      }
    });
    const unSubSettings = signalIsSettingsOpen.subscribe((state) => {
      refSettingsButton.current?.classList.toggle("text-inspect", state);
    });
    return () => {
      unSubState();
      unSubSettings();
    };
  }, []);
  let inspectIcon = null;
  let inspectColor = "#999";
  if (isInspectActive) {
    inspectIcon = /* @__PURE__ */ jsx16(Icon, { name: "icon-inspect" });
    inspectColor = "#8e61e3";
  } else if (isInspectFocused) {
    inspectIcon = /* @__PURE__ */ jsx16(Icon, { name: "icon-focus" });
    inspectColor = "#8e61e3";
  } else {
    inspectIcon = /* @__PURE__ */ jsx16(Icon, { name: "icon-inspect" });
    inspectColor = "#999";
  }
  return /* @__PURE__ */ jsxs14(
    "div",
    {
      className: "flex max-h-9 min-h-9 flex-1 items-stretch overflow-hidden gap-x-[6px]",
      children: [
        /* @__PURE__ */ jsxs14("div", { className: "h-full flex items-center min-w-fit gap-x-[6px]", children: [
          /* @__PURE__ */ jsx16(
            "button",
            {
              type: "button",
              id: "react-scan-inspect-element",
              title: "Inspect element",
              onClick: onToggleInspect,
              className: "button flex items-center justify-center px-3 h-full",
              style: { color: inspectColor },
              children: inspectIcon
            }
          ),
          /* @__PURE__ */ jsx16(
            Toggle,
            {
              checked: !ReactScanInternals.instrumentation?.isPaused.value,
              onChange: onToggleActive,
              title: ReactScanInternals.instrumentation?.isPaused.value ? "Start" : "Stop"
            }
          )
        ] }),
        /* @__PURE__ */ jsxs14(
          "div",
          {
            className: cn(
              "flex items-center justify-end w-full gap-x-2",
              "pl-2 p-1.5",
              "whitespace-nowrap text-sm text-white"
            ),
            children: [
              "react-scan",
              /* @__PURE__ */ jsx16(fps_meter_default, {})
            ]
          }
        )
      ]
    }
  );
});

// src/web/components/widget/index.tsx
import { Fragment as Fragment8, jsx as jsx17, jsxs as jsxs15 } from "preact/jsx-runtime";

// src/web/toolbar.tsx
import { jsx as jsx18, jsxs as jsxs16 } from "preact/jsx-runtime";

// src/core/index.ts
var Store = {
  wasDetailsOpen: signal5(true),
  isInIframe: signal5(
    typeof window !== "undefined" && window.self !== window.top
  ),
  inspectState: signal5({
    kind: "uninitialized"
  }),
  monitor: signal5(null),
  fiberRoots: /* @__PURE__ */ new Set(),
  reportData: /* @__PURE__ */ new Map(),
  legacyReportData: /* @__PURE__ */ new Map(),
  lastReportTime: signal5(0)
};
var ReactScanInternals = {
  instrumentation: null,
  componentAllowList: null,
  options: signal5({
    enabled: true,
    includeChildren: true,
    playSound: false,
    log: false,
    showToolbar: true,
    renderCountThreshold: 0,
    report: void 0,
    alwaysShowLabels: false,
    animationSpeed: "fast",
    dangerouslyForceRunInProduction: false,
    smoothlyAnimateOutlines: true,
    trackUnnecessaryRenders: false
  }),
  onRender: null,
  scheduledOutlines: /* @__PURE__ */ new Map(),
  activeOutlines: /* @__PURE__ */ new Map(),
  Store
};
function isOptionKey(key) {
  return key in ReactScanInternals.options.value;
}
var validateOptions = (options) => {
  const errors = [];
  const validOptions = {};
  for (const key in options) {
    if (!isOptionKey(key)) continue;
    const value = options[key];
    switch (key) {
      case "enabled":
      // case 'includeChildren':
      case "log":
      case "showToolbar":
      // case 'report':
      // case 'alwaysShowLabels':
      case "dangerouslyForceRunInProduction":
        if (typeof value !== "boolean") {
          errors.push(`- ${key} must be a boolean. Got "${value}"`);
        } else {
          validOptions[key] = value;
        }
        break;
      // case 'renderCountThreshold':
      // case 'resetCountTimeout':
      //   if (typeof value !== 'number' || value < 0) {
      //     errors.push(`- ${key} must be a non-negative number. Got "${value}"`);
      //   } else {
      //     validOptions[key] = value as number;
      //   }
      //   break;
      case "animationSpeed":
        if (!["slow", "fast", "off"].includes(value)) {
          errors.push(
            `- Invalid animation speed "${value}". Using default "fast"`
          );
        } else {
          validOptions[key] = value;
        }
        break;
      case "onCommitStart":
        if (typeof value !== "function") {
          errors.push(`- ${key} must be a function. Got "${value}"`);
        } else {
          validOptions.onCommitStart = value;
        }
        break;
      case "onCommitFinish":
        if (typeof value !== "function") {
          errors.push(`- ${key} must be a function. Got "${value}"`);
        } else {
          validOptions.onCommitFinish = value;
        }
        break;
      case "onRender":
        if (typeof value !== "function") {
          errors.push(`- ${key} must be a function. Got "${value}"`);
        } else {
          validOptions.onRender = value;
        }
        break;
      case "onPaintStart":
      case "onPaintFinish":
        if (typeof value !== "function") {
          errors.push(`- ${key} must be a function. Got "${value}"`);
        } else {
          validOptions[key] = value;
        }
        break;
      // case 'trackUnnecessaryRenders': {
      //   validOptions.trackUnnecessaryRenders =
      //     typeof value === 'boolean' ? value : false;
      //   break;
      // }
      // case 'smoothlyAnimateOutlines': {
      //   validOptions.smoothlyAnimateOutlines =
      //     typeof value === 'boolean' ? value : false;
      //   break;
      // }
      default:
        errors.push(`- Unknown option "${key}"`);
    }
  }
  if (errors.length > 0) {
    console.warn(`[React Scan] Invalid options:
${errors.join("\n")}`);
  }
  return validOptions;
};
var setOptions = (userOptions) => {
  const validOptions = validateOptions(userOptions);
  if (Object.keys(validOptions).length === 0) {
    return;
  }
  const newOptions = {
    ...ReactScanInternals.options.value,
    ...validOptions
  };
  const { instrumentation } = ReactScanInternals;
  if (instrumentation && "enabled" in validOptions) {
    instrumentation.isPaused.value = validOptions.enabled === false;
  }
  ReactScanInternals.options.value = newOptions;
  const existingLocalStorageOptions = readLocalStorage("react-scan-options");
  saveLocalStorage("react-scan-options", {
    ...newOptions,
    enabled: newOptions.showToolbar ? existingLocalStorageOptions?.enabled ?? newOptions.enabled ?? true : newOptions.enabled
  });
  return newOptions;
};

// src/core/monitor/constants.ts
import * as React from "react";
var isRSC = () => !React.useRef;
var isSSR = () => typeof window === "undefined" || isRSC();
var isTest = typeof window !== "undefined" && /**
 * @see https://docs.cypress.io/faq/questions/using-cypress-faq#Is-there-any-way-to-detect-if-my-app-is-running-under-Cypress
 */
(window.Cypress || /**
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Navigator/webdriver
 */
navigator.webdriver) || /**
 * @see https://stackoverflow.com/a/60491322
 */
// @ts-expect-error jest is a global in test
typeof jest !== "undefined";
var GZIP_MIN_LEN = 1e3;
var GZIP_MAX_LEN = 6e4;
var MAX_PENDING_REQUESTS = 15;

// src/core/monitor/utils.ts
var getDeviceType = () => {
  const userAgent = navigator.userAgent;
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    userAgent
  )) {
    return 2 /* MOBILE */;
  }
  if (/iPad|Tablet/i.test(userAgent)) {
    return 1 /* TABLET */;
  }
  return 0 /* DESKTOP */;
};
var generateId = () => {
  const alphabet = "useandom-26T198340PX75pxJACKVERYMINDBUSHWOLF_GQZbfghjklqvwyzrict";
  let id = "";
  const randomValues = crypto.getRandomValues(new Uint8Array(21));
  for (let i = 0; i < 21; i++) {
    id += alphabet[63 & randomValues[i]];
  }
  return id;
};
var getGpuRenderer = () => {
  if (!("chrome" in window)) return "";
  const gl = document.createElement("canvas").getContext("webgl", { powerPreference: "high-performance" });
  if (!gl) return "";
  const ext = gl.getExtension("WEBGL_debug_renderer_info");
  return ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : "";
};
var cachedSession;
var getSession = async ({
  commit = null,
  branch = null
}) => {
  if (isSSR()) return null;
  if (cachedSession) {
    return cachedSession;
  }
  const id = generateId();
  const url = window.location.toString();
  const connection = navigator.connection;
  const wifi = connection?.effectiveType ?? null;
  const cpu = navigator.hardwareConcurrency;
  const mem = navigator.deviceMemory ?? 0;
  const gpuRendererPromise = new Promise((resolve) => {
    onIdle(() => {
      resolve(getGpuRenderer());
    });
  });
  const session = {
    id,
    url,
    route: null,
    device: getDeviceType(),
    wifi: wifi ?? "",
    cpu,
    mem,
    gpu: await gpuRendererPromise,
    agent: navigator.userAgent,
    commit,
    branch,
    version: "0.1.2"
  };
  cachedSession = session;
  return session;
};

// src/core/monitor/network.ts
var INTERACTION_TIME_TILL_COMPLETED = 4e3;
var truncate = (value, decimalPlaces = 4) => Number(value.toFixed(decimalPlaces));
var flush = async () => {
  const monitor = Store.monitor.value;
  if (!monitor || !navigator.onLine || !monitor.url || !monitor.interactions.length) {
    return;
  }
  const now = performance.now();
  const pendingInteractions = new Array();
  const completedInteractions = new Array();
  const interactions = monitor.interactions;
  for (let i = 0; i < interactions.length; i++) {
    const interaction = interactions[i];
    const timeSinceStart = now - interaction.performanceEntry.startTime;
    if (timeSinceStart <= 3e4) {
      if (timeSinceStart <= INTERACTION_TIME_TILL_COMPLETED) {
        pendingInteractions.push(interaction);
      } else {
        completedInteractions.push(interaction);
      }
    }
  }
  if (!completedInteractions.length)
    return;
  const session = await getSession({
    commit: monitor.commit,
    branch: monitor.branch
  }).catch(() => null);
  if (!session) return;
  const aggregatedComponents = new Array();
  const aggregatedInteractions = new Array();
  for (let i = 0; i < completedInteractions.length; i++) {
    const interaction = completedInteractions[i];
    const {
      duration,
      entries,
      id,
      inputDelay,
      latency,
      presentationDelay,
      processingDuration,
      processingEnd,
      processingStart,
      referrer,
      startTime,
      timeOrigin,
      timeSinceTabInactive,
      timestamp,
      type,
      visibilityState
    } = interaction.performanceEntry;
    aggregatedInteractions.push({
      id: i,
      path: interaction.componentPath,
      name: interaction.componentName,
      time: truncate(duration),
      timestamp,
      type,
      // fixme: we can aggregate around url|route|commit|branch better to compress payload
      url: interaction.url,
      route: interaction.route,
      commit: interaction.commit,
      branch: interaction.branch,
      uniqueInteractionId: interaction.uniqueInteractionId,
      meta: {
        performanceEntry: {
          id,
          inputDelay: truncate(inputDelay),
          latency: truncate(latency),
          presentationDelay: truncate(presentationDelay),
          processingDuration: truncate(processingDuration),
          processingEnd,
          processingStart,
          referrer,
          startTime,
          timeOrigin,
          timeSinceTabInactive,
          visibilityState,
          duration: truncate(duration),
          entries: entries.map((entry) => {
            const {
              duration: duration2,
              entryType,
              interactionId,
              name,
              processingEnd: processingEnd2,
              processingStart: processingStart2,
              startTime: startTime2
            } = entry;
            return {
              duration: truncate(duration2),
              entryType,
              interactionId,
              name,
              processingEnd: processingEnd2,
              processingStart: processingStart2,
              startTime: startTime2
            };
          })
        }
      }
    });
    const components = Array.from(interaction.components.entries());
    for (let j = 0; j < components.length; j++) {
      const [name, component] = components[j];
      aggregatedComponents.push({
        name,
        instances: component.fibers.size,
        interactionId: i,
        renders: component.renders,
        selfTime: typeof component.selfTime === "number" ? truncate(component.selfTime) : component.selfTime,
        totalTime: typeof component.totalTime === "number" ? truncate(component.totalTime) : component.totalTime
      });
    }
  }
  const payload = {
    interactions: aggregatedInteractions,
    components: aggregatedComponents,
    session: {
      ...session,
      url: window.location.toString(),
      route: monitor.route,
      // this might be inaccurate but used to caculate which paths all the unique sessions are coming from without having to join on the interactions table (expensive)
      wifi: session.wifi ?? ""
    }
  };
  monitor.pendingRequests++;
  monitor.interactions = pendingInteractions;
  try {
    transport(monitor.url, payload).then(() => {
      monitor.pendingRequests--;
    }).catch(async () => {
      monitor.interactions = monitor.interactions.concat(
        completedInteractions
      );
    });
  } catch {
  }
  monitor.interactions = pendingInteractions;
};
var CONTENT_TYPE = "application/json";
var supportsCompression = typeof CompressionStream === "function";
var compress = async (payload) => {
  const stream = new Blob([payload], { type: CONTENT_TYPE }).stream().pipeThrough(new CompressionStream("gzip"));
  return new Response(stream).arrayBuffer();
};
var transport = async (initialUrl, payload) => {
  const fail = { ok: false };
  const json = JSON.stringify(payload);
  const shouldCompress = json.length > GZIP_MIN_LEN;
  const body = shouldCompress && supportsCompression ? await compress(json) : json;
  if (!navigator.onLine) return fail;
  const headerValues = {
    "Content-Type": CONTENT_TYPE,
    "Content-Encoding": shouldCompress ? "gzip" : void 0,
    "x-api-key": Store.monitor.value?.apiKey ?? void 0
  };
  let url = initialUrl;
  if (shouldCompress) url += "?z=1";
  const size = typeof body === "string" ? body.length : body.byteLength;
  return fetch(url, {
    body,
    method: "POST",
    referrerPolicy: "origin",
    /**
     * Outgoing requests are usually cancelled when navigating to a different page, causing a "TypeError: Failed to
     * fetch" error and sending a "network_error" client-outcome - in Chrome, the request status shows "(cancelled)".
     * The `keepalive` flag keeps outgoing requests alive, even when switching pages. We want this since we're
     * frequently sending events right before the user is switching pages (e.g., when finishing navigation transactions).
     *
     * This is the modern alternative to the navigator.sendBeacon API.
     * @see https://javascript.info/fetch-api#keepalive
     *
     * Gotchas:
     * - `keepalive` isn't supported by Firefox
     * - As per spec (https://fetch.spec.whatwg.org/#http-network-or-cache-fetch):
     *   If the sum of contentLength and inflightKeepaliveBytes is greater than 64 kibibytes, then return a network error.
     *   We will therefore only activate the flag when we're below that limit.
     * - There is also a limit of requests that can be open at the same time, so we also limit this to 15.
     *
     * @see https://github.com/getsentry/sentry-javascript/pull/7553
     */
    keepalive: GZIP_MAX_LEN > size && MAX_PENDING_REQUESTS > (Store.monitor.value?.pendingRequests ?? 0),
    priority: "low",
    // mode: 'no-cors',
    headers: headerValues
  });
};

// src/core/monitor/params/utils.ts
function computeRouteWithFormatter(pathname, pathParams, formatter) {
  if (!pathname || !pathParams) {
    return pathname;
  }
  let result = pathname;
  try {
    const entries = Object.entries(pathParams);
    for (const [key, value] of entries) {
      if (!Array.isArray(value)) {
        const matcher = turnValueToRegExp(value);
        if (matcher.test(result)) {
          result = result.replace(matcher, formatter.param(key));
        }
      }
    }
    for (const [key, value] of entries) {
      if (Array.isArray(value)) {
        const matcher = turnValueToRegExp(value.join("/"));
        if (matcher.test(result)) {
          result = result.replace(matcher, formatter.catchAll(key));
        }
      }
    }
    return result;
  } catch {
    return pathname;
  }
}
function computeRoute(pathname, pathParams) {
  return computeRouteWithFormatter(pathname, pathParams, {
    param: (key) => `/[${key}]`,
    catchAll: (key) => `/[...${key}]`
  });
}
function turnValueToRegExp(value) {
  return new RegExp(`/${escapeRegExp(value)}(?=[/?#]|$)`);
}
function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// src/core/monitor/performance.ts
import { getDisplayName as getDisplayName7 } from "bippy";
var DEFAULT_FILTERS = {
  skipProviders: true,
  skipHocs: true,
  skipContainers: true,
  skipMinified: true,
  skipUtilities: true,
  skipBoundaries: true
};
var FILTER_PATTERNS = {
  providers: [/Provider$/, /^Provider$/, /^Context$/],
  hocs: [/^with[A-Z]/, /^forward(?:Ref)?$/i, /^Forward(?:Ref)?\(/],
  containers: [/^(?:App)?Container$/, /^Root$/, /^ReactDev/],
  utilities: [
    /^Fragment$/,
    /^Suspense$/,
    /^ErrorBoundary$/,
    /^Portal$/,
    /^Consumer$/,
    /^Layout$/,
    /^Router/,
    /^Hydration/
  ],
  boundaries: [/^Boundary$/, /Boundary$/, /^Provider$/, /Provider$/]
};
var shouldIncludeInPath = (name, filters = DEFAULT_FILTERS) => {
  const patternsToCheck = [];
  if (filters.skipProviders) patternsToCheck.push(...FILTER_PATTERNS.providers);
  if (filters.skipHocs) patternsToCheck.push(...FILTER_PATTERNS.hocs);
  if (filters.skipContainers)
    patternsToCheck.push(...FILTER_PATTERNS.containers);
  if (filters.skipUtilities) patternsToCheck.push(...FILTER_PATTERNS.utilities);
  if (filters.skipBoundaries)
    patternsToCheck.push(...FILTER_PATTERNS.boundaries);
  return !patternsToCheck.some((pattern) => pattern.test(name));
};
var minifiedPatterns = [
  /^[a-z]$/,
  // Single lowercase letter
  /^[a-z][0-9]$/,
  // Lowercase letter followed by number
  /^_+$/,
  // Just underscores
  /^[A-Za-z][_$]$/,
  // Letter followed by underscore or dollar
  /^[a-z]{1,2}$/
  // 1-2 lowercase letters
];
var isMinified = (name) => {
  if (!name || typeof name !== "string") {
    return true;
  }
  for (let i = 0; i < minifiedPatterns.length; i++) {
    if (minifiedPatterns[i].test(name)) return true;
  }
  const hasNoVowels = !/[aeiou]/i.test(name);
  const hasMostlyNumbers = (name.match(/\d/g)?.length ?? 0) > name.length / 2;
  const isSingleWordLowerCase = /^[a-z]+$/.test(name);
  const hasRandomLookingChars = /[$_]{2,}/.test(name);
  return Number(hasNoVowels) + Number(hasMostlyNumbers) + Number(isSingleWordLowerCase) + Number(hasRandomLookingChars) >= 2;
};
var getInteractionPath = (initialFiber, filters = DEFAULT_FILTERS) => {
  if (!initialFiber) return [];
  const currentName = getDisplayName7(initialFiber.type);
  if (!currentName) return [];
  const stack = new Array();
  let fiber = initialFiber;
  while (fiber.return) {
    const name = getCleanComponentName(fiber.type);
    if (name && !isMinified(name) && shouldIncludeInPath(name, filters)) {
      stack.push(name);
    }
    fiber = fiber.return;
  }
  const fullPath = new Array(stack.length);
  for (let i = 0; i < stack.length; i++) {
    fullPath[i] = stack[stack.length - i - 1];
  }
  return fullPath;
};
var currentMouseOver;
var getCleanComponentName = (component) => {
  const name = getDisplayName7(component);
  if (!name) return "";
  return name.replace(
    /^(?:Memo|Forward(?:Ref)?|With.*?)\((?<inner>.*?)\)$/,
    "$<inner>"
  );
};
var handleMouseover = (event) => {
  if (!(event.target instanceof Element)) return;
  currentMouseOver = event.target;
};
var getFirstNamedAncestorCompositeFiber = (element) => {
  let curr = element;
  let parentCompositeFiber = null;
  while (!parentCompositeFiber && curr.parentElement) {
    curr = curr.parentElement;
    const { parentCompositeFiber: fiber } = getCompositeComponentFromElement(curr);
    if (!fiber) {
      continue;
    }
    if (getDisplayName7(fiber.type)) {
      parentCompositeFiber = fiber;
    }
  }
  return parentCompositeFiber;
};
var unsubscribeTrackVisibilityChange;
var lastVisibilityHiddenAt = "never-hidden";
var trackVisibilityChange = () => {
  unsubscribeTrackVisibilityChange?.();
  const onVisibilityChange = () => {
    if (document.hidden) {
      lastVisibilityHiddenAt = Date.now();
    }
  };
  document.addEventListener("visibilitychange", onVisibilityChange);
  unsubscribeTrackVisibilityChange = () => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
};
function initPerformanceMonitoring(options) {
  const filters = { ...DEFAULT_FILTERS, ...options };
  const monitor = Store.monitor.value;
  if (!monitor) return;
  document.addEventListener("mouseover", handleMouseover);
  const disconnectPerformanceListener = setupPerformanceListener((entry) => {
    const target = entry.target ?? (entry.type === "pointer" ? currentMouseOver : null);
    if (!target) {
      return;
    }
    const parentCompositeFiber = getFirstNamedAncestorCompositeFiber(target);
    if (!parentCompositeFiber) {
      return;
    }
    const displayName = getDisplayName7(parentCompositeFiber.type);
    if (!displayName || isMinified(displayName)) {
      return;
    }
    const path = getInteractionPath(parentCompositeFiber, filters);
    monitor.interactions.push({
      componentName: displayName,
      componentPath: path,
      performanceEntry: entry,
      components: /* @__PURE__ */ new Map(),
      url: window.location.toString(),
      route: Store.monitor.value?.route ?? new URL(window.location.href).pathname,
      commit: Store.monitor.value?.commit ?? null,
      branch: Store.monitor.value?.branch ?? null,
      uniqueInteractionId: entry.id
    });
  });
  return () => {
    disconnectPerformanceListener();
    document.removeEventListener("mouseover", handleMouseover);
  };
}
var getInteractionType = (eventName) => {
  if (["pointerdown", "pointerup", "click"].includes(eventName)) {
    return "pointer";
  }
  if (["keydown", "keyup"].includes(eventName)) {
    return "keyboard";
  }
  return null;
};
var setupPerformanceListener = (onEntry) => {
  trackVisibilityChange();
  const longestInteractionMap = /* @__PURE__ */ new Map();
  const interactionTargetMap = /* @__PURE__ */ new Map();
  const processInteractionEntry = (entry) => {
    if (!(entry.interactionId || entry.entryType === "first-input")) return;
    if (entry.interactionId && entry.target && !interactionTargetMap.has(entry.interactionId)) {
      interactionTargetMap.set(entry.interactionId, entry.target);
    }
    const existingInteraction = longestInteractionMap.get(entry.interactionId);
    if (existingInteraction) {
      if (entry.duration > existingInteraction.latency) {
        existingInteraction.entries = [entry];
        existingInteraction.latency = entry.duration;
      } else if (entry.duration === existingInteraction.latency && entry.startTime === existingInteraction.entries[0].startTime) {
        existingInteraction.entries.push(entry);
      }
    } else {
      const interactionType = getInteractionType(entry.name);
      if (!interactionType) return;
      const interaction = {
        id: entry.interactionId,
        latency: entry.duration,
        entries: [entry],
        target: entry.target,
        type: interactionType,
        startTime: entry.startTime,
        processingStart: entry.processingStart,
        processingEnd: entry.processingEnd,
        duration: entry.duration,
        inputDelay: entry.processingStart - entry.startTime,
        processingDuration: entry.processingEnd - entry.processingStart,
        presentationDelay: entry.duration - (entry.processingEnd - entry.startTime),
        timestamp: Date.now(),
        timeSinceTabInactive: lastVisibilityHiddenAt === "never-hidden" ? "never-hidden" : Date.now() - lastVisibilityHiddenAt,
        visibilityState: document.visibilityState,
        timeOrigin: performance.timeOrigin,
        referrer: document.referrer
      };
      longestInteractionMap.set(interaction.id, interaction);
      onEntry(interaction);
    }
  };
  const po = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    for (let i = 0, len = entries.length; i < len; i++) {
      const entry = entries[i];
      processInteractionEntry(entry);
    }
  });
  try {
    po.observe({
      type: "event",
      buffered: true,
      durationThreshold: 16
    });
    po.observe({
      type: "first-input",
      buffered: true
    });
  } catch {
  }
  return () => po.disconnect();
};

// src/core/monitor/index.ts
var MAX_RETRIES_BEFORE_COMPONENT_GC = 7;
var Monitoring = ({
  url,
  apiKey,
  params,
  path = null,
  // path passed down would be reactive
  route = null,
  commit = null,
  branch = null
}) => {
  if (!apiKey)
    throw new Error("Please provide a valid API key for React Scan monitoring");
  url ??= "https://monitoring.react-scan.com/api/v1/ingest";
  Store.monitor.value ??= {
    pendingRequests: 0,
    interactions: [],
    session: getSession({ commit, branch }).catch(() => null),
    url,
    apiKey,
    route,
    commit,
    branch
  };
  if (!route && path && params) {
    Store.monitor.value.route = computeRoute(path, params);
  } else if (typeof window !== "undefined") {
    Store.monitor.value.route = route ?? path ?? new URL(window.location.toString()).pathname;
  }
  useEffect16(() => {
    scanMonitoring({ enabled: true });
    return initPerformanceMonitoring();
  }, []);
  return null;
};
var scanMonitoring = (options) => {
  setOptions(options);
  startMonitoring();
};
var flushInterval;
var startMonitoring = () => {
  if (!Store.monitor.value) {
    if (true) {
      throw new Error(
        "Invariant: startMonitoring can never be called when monitoring is not initialized"
      );
    }
  }
  if (flushInterval) {
    clearInterval(flushInterval);
  }
  flushInterval = setInterval(() => {
    try {
      void flush();
    } catch {
    }
  }, 2e3);
  globalThis.__REACT_SCAN__ = {
    ReactScanInternals
  };
  const instrumentation = createInstrumentation("monitoring", {
    onCommitStart() {
    },
    onError() {
    },
    isValidFiber() {
      return true;
    },
    onRender(fiber, renders) {
      updateFiberRenderData(fiber, renders);
      if (isCompositeFiber3(fiber)) {
        aggregateComponentRenderToInteraction(fiber, renders);
      }
    },
    onCommitFinish() {
    },
    trackChanges: false,
    forceAlwaysTrackRenders: true
  });
  ReactScanInternals.instrumentation = instrumentation;
};
var aggregateComponentRenderToInteraction = (fiber, renders) => {
  const monitor = Store.monitor.value;
  if (!monitor || !monitor.interactions || monitor.interactions.length === 0)
    return;
  const lastInteraction = monitor.interactions.at(-1);
  if (!lastInteraction) return;
  const displayName = getDisplayName8(fiber.type);
  if (!displayName) return;
  let component = lastInteraction.components.get(displayName);
  if (!component) {
    component = {
      fibers: /* @__PURE__ */ new Set(),
      name: displayName,
      renders: 0,
      retiresAllowed: MAX_RETRIES_BEFORE_COMPONENT_GC,
      uniqueInteractionId: lastInteraction.uniqueInteractionId
    };
    lastInteraction.components.set(displayName, component);
  }
  if (fiber.alternate && !component.fibers.has(fiber.alternate)) {
    component.fibers.add(fiber.alternate);
  }
  const rendersCount = renders.length;
  component.renders += rendersCount;
  if (fiber.actualDuration) {
    const { selfTime, totalTime } = getTimings3(fiber);
    if (!component.totalTime) component.totalTime = 0;
    if (!component.selfTime) component.selfTime = 0;
    component.totalTime += totalTime;
    component.selfTime += selfTime;
  }
};

// src/core/monitor/params/next.ts
var useRoute = () => {
  const params = useParams();
  const searchParams = useSearchParams();
  const path = usePathname();
  if (!params) {
    return { route: null, path };
  }
  const finalParams = Object.keys(params).length ? params : Object.fromEntries(searchParams.entries());
  return { route: computeRoute(path, finalParams), path };
};
function MonitoringInner(props) {
  const { route, path } = useRoute();
  return createElement2(Monitoring, {
    ...props,
    route,
    path
  });
}
var Monitoring2 = /* @__PURE__ */ (() => {
  "use client";
  return function Monitoring3(props) {
    return createElement2(
      Suspense,
      { fallback: null },
      createElement2(MonitoringInner, props)
    );
  };
})();
export {
  Monitoring2 as Monitoring,
  MonitoringInner
};
